<template>
  <div class="generic-layout pricing-page">
    <Header />

    <!-- PROMO BANNER -->
    <div class="ph-promo-banner">
      <div class="container ph-promo-banner-inner">
        <span class="ph-promo-icon">🎖️</span>
        <span>Active or veteran military — 30% off every course with code <strong>Thankyouforyourservice</strong></span>
      </div>
    </div>

    <!-- Cart/API error banner — surfaces silent cart-init or network failures -->
    <div v-if="cartError" class="ph-cart-error" role="alert" aria-live="polite">
      <div class="container ph-cart-error-inner">
        <span class="ph-cart-error-icon">⚠️</span>
        <span>{{ cartError }}</span>
        <button type="button" class="ph-cart-error-dismiss" @click="cartError = ''" aria-label="Dismiss">×</button>
      </div>
    </div>

    <!-- Prefill error banner — surfaces unresolved ?course=… slugs from
         landing-page deep links so the buyer doesn't see a silent drop. -->
    <div v-if="prefillError" class="ph-cart-error ph-cart-error--soft" role="status" aria-live="polite">
      <div class="container ph-cart-error-inner">
        <span class="ph-cart-error-icon">ℹ️</span>
        <span>{{ prefillError }}</span>
        <button type="button" class="ph-cart-error-dismiss" @click="prefillError = ''" aria-label="Dismiss">×</button>
      </div>
    </div>

    <!-- Prefill loading overlay — shown while landing-page query params resolve
         to cart adds. Buyer sees a friendly "Loading your cart…" instead of a
         flash of empty state. -->
    <div v-if="prefillRunning" class="ph-prefill-overlay" role="status" aria-live="polite">
      <div class="ph-prefill-overlay-inner">
        <div class="ph-prefill-spinner" aria-hidden="true"></div>
        <span>Loading your cart…</span>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════════════
         HERO — pricing + cart live right here, above the fold
         ═══════════════════════════════════════════════════════════════ -->
    <section class="ph-hero" data-reveal>
      <div class="container ph-hero-grid">

        <div class="ph-hero-main">
          <span class="ph-eyebrow">Pricing</span>
          <h1 class="ph-h1">
            Training that
            <span class="ph-h1-accent">just works.</span>
          </h1>
          <p class="ph-sub">
            Pick one course or outfit your whole team. Honest prices, real certificates.
          </p>

          <div class="ph-tabs" role="tablist" aria-label="Pricing options">
            <button
              v-for="t in tabs"
              :key="t.key"
              :id="`ph-tab-${t.key}`"
              class="ph-tab"
              :class="{ 'ph-tab--active': tab === t.key }"
              type="button"
              role="tab"
              :aria-selected="tab === t.key ? 'true' : 'false'"
              :aria-controls="`ph-tabpanel-${t.key}`"
              :tabindex="tab === t.key ? 0 : -1"
              @mousedown.prevent
              @click="switchTab(t.key, $event)"
              @keydown.right.prevent="moveTabFocus(1)"
              @keydown.left.prevent="moveTabFocus(-1)"
              @keydown.home.prevent="focusTab(tabs[0].key)"
              @keydown.end.prevent="focusTab(tabs[tabs.length - 1].key)"
            >
              <span class="ph-tab-icon" aria-hidden="true">{{ t.icon }}</span>
              <span class="ph-tab-label">{{ t.label }}</span>
              <span class="ph-tab-sub">{{ t.sub }}</span>
            </button>
          </div>

          <div class="ph-panes" ref="panes" :style="panesStyle">
          <!-- ═══ INDIVIDUAL TAB ═══ -->
          <div
            id="ph-tabpanel-individual"
            class="ph-tab-pane"
            :class="{ 'ph-tab-pane--active': tab === 'individual' }"
            role="tabpanel"
            aria-labelledby="ph-tab-individual"
            :aria-hidden="tab !== 'individual' ? 'true' : 'false'"
          >
            <div class="ph-catalog-head">
              <h2>Pick the course you need.</h2>
              <p>Pay once, take it on any device, get your certificate the moment you pass.</p>
            </div>

            <div class="ph-catalog-controls">
              <div class="ph-search-wrap">
                <svg class="ph-search-icon" viewBox="0 0 20 20" fill="none">
                  <circle cx="9" cy="9" r="6.5" stroke="currentColor" stroke-width="2"/>
                  <path d="M14 14L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                <input
                  v-model="catalogQuery"
                  type="text"
                  placeholder="Search courses…"
                  aria-label="Search courses"
                  class="ph-search-input"
                  @input="catalogPage = 1"
                />
              </div>
              <div class="ph-categories">
                <button
                  v-for="cat in categories"
                  :key="cat"
                  type="button"
                  class="ph-cat-chip"
                  :class="{ 'ph-cat-chip--active': catalogCategory === cat }"
                  @click="catalogCategory = cat; catalogPage = 1"
                >{{ cat }}</button>
              </div>
            </div>

            <div v-if="filteredCatalog.length === 0" class="ph-empty-catalog">
              <p>No matches for "{{ catalogQuery }}". Try another word.</p>
            </div>

            <div v-else class="ph-courses-grid">
              <article
                v-for="entry in pagedCatalog"
                :key="entry.key"
                class="ph-course-card"
                :class="{
                  'ph-course-card--in-cart': !entry.isGroup && isInCart(entry.id),
                  'ph-course-card--group': entry.isGroup,
                  'ph-course-card--group-active': entry.isGroup && variantsInCart(entry) > 0,
                }"
              >
                <div class="ph-course-head">
                  <span class="ph-course-badge">{{ entry.category }}</span>
                  <div class="ph-course-price">
                    <span v-if="entry.isGroup && !entry.costSame" class="ph-currency">From $</span>
                    <span v-else class="ph-currency">$</span>{{ formatPrice(entry.cost) }}
                  </div>
                </div>
                <h3 class="ph-course-name">
                  {{ entry.name }}
                </h3>
                <p class="ph-course-desc">{{ entry.description || 'Self-paced online certification.' }}</p>
                <div class="ph-course-meta">
                  <span v-if="entry.length">⏱ {{ entry.length }}</span>
                  <span v-if="entry.certificate_available">🎓 Certificate</span>
                  <span v-if="entry.isGroup && variantsInCart(entry) > 0">
                    ✓ {{ variantsInCart(entry) }} of {{ entry.variantCount }} added
                  </span>
                </div>
                <button
                  type="button"
                  class="ph-btn ph-course-btn"
                  :class="(!entry.isGroup && isInCart(entry.id)) ? 'ph-btn--in-cart' : 'ph-btn--add'"
                  :disabled="!entry.isGroup && busyCourseId === entry.id"
                  @click="onCourseCtaClick(entry)"
                >
                  <span v-if="!entry.isGroup && busyCourseId === entry.id">…</span>
                  <span v-else-if="entry.isGroup">Choose version →</span>
                  <span v-else-if="isInCart(entry.id)">✓ In Cart</span>
                  <span v-else>Add to Cart</span>
                </button>
              </article>
            </div>

            <!-- Pagination -->
            <nav v-if="totalPages > 1" class="ph-pagination" aria-label="Course catalog pagination">
              <button
                type="button"
                class="ph-page-btn"
                :disabled="catalogPage === 1"
                @click="catalogPage = Math.max(1, catalogPage - 1)"
              >← Prev</button>

              <button
                v-for="p in pageNumbers"
                :key="p"
                type="button"
                class="ph-page-btn ph-page-btn--num"
                :class="{ 'ph-page-btn--active': p === catalogPage, 'ph-page-btn--dots': p === '...' }"
                :disabled="p === '...'"
                @click="p !== '...' && (catalogPage = p)"
              >{{ p }}</button>

              <button
                type="button"
                class="ph-page-btn"
                :disabled="catalogPage === totalPages"
                @click="catalogPage = Math.min(totalPages, catalogPage + 1)"
              >Next →</button>

              <span class="ph-page-info">
                {{ catalogStartIdx + 1 }}–{{ catalogEndIdx }} of {{ filteredCatalog.length }}
              </span>
            </nav>
          </div>

          <!-- ═══ COMPANY TAB ═══ -->
          <div
            id="ph-tabpanel-company"
            class="ph-tab-pane"
            :class="{ 'ph-tab-pane--active': tab === 'company' }"
            role="tabpanel"
            aria-labelledby="ph-tab-company"
            :aria-hidden="tab !== 'company' ? 'true' : 'false'"
          >
            <div class="ph-catalog-head">
              <h2 v-if="licenseMode === 'chooseLMS'">Plans for every team.</h2>
              <h2 v-else>License our content for your LMS.</h2>
              <p v-if="licenseMode === 'chooseLMS'">Volume discounts kick in automatically — no formulas, no surprises.</p>
              <p v-else>Pick the courses, tell us how many learners. We package SCORM and license per-user.</p>
            </div>

            <!-- Sub-toggle inside the My Team tab — picks which company
                 surface renders below. Lifted from legacy ChoosePlatform.vue
                 with a new layout that fits the v3 page rhythm. -->
            <LicenseModeChooser v-model="licenseMode" />

            <!-- ─── LMS mode (default) ─── -->
            <template v-if="licenseMode === 'chooseLMS'">
            <div class="ph-plans-grid">
              <article
                v-for="plan in visibleCompanyPlans"
                :key="plan.key"
                class="ph-plan-card"
                :class="{
                  'ph-plan-card--popular': plan.popular,
                  'ph-plan-card--selected': companyConfig.plan === plan.key,
                }"
                role="button"
                tabindex="0"
                :aria-pressed="companyConfig.plan === plan.key ? 'true' : 'false'"
                :aria-label="`Select ${plan.name} plan`"
                @click="selectPlan(plan.key)"
                @keydown.enter.prevent="selectPlan(plan.key)"
                @keydown.space.prevent="selectPlan(plan.key)"
              >
                <div v-if="plan.popular" class="ph-plan-pop">★ Most Popular</div>
                <h3 class="ph-plan-name">{{ plan.name }}</h3>
                <p class="ph-plan-tag">{{ plan.tagline }}</p>
                <div class="ph-plan-price">
                  <span class="ph-plan-from">From</span>
                  <span class="ph-plan-currency">$</span>
                  <span class="ph-plan-amount">{{ plan.from_price }}</span>
                  <span class="ph-plan-per">/user/mo</span>
                </div>
                <div class="ph-plan-range">+ ${{ LOCATION_FEE }}/location/month</div>
                <div class="ph-plan-includes">
                  <div class="ph-plan-includes-head">
                    <span>Includes</span>
                    <strong>{{ includedCountLabel(plan.key) }}</strong>
                  </div>
                  <ul class="ph-plan-includes-list">
                    <li v-for="c in previewIncluded(plan.key)" :key="c.id">
                      <span class="ph-check">✓</span>{{ c.name }}
                    </li>
                  </ul>
                  <button
                    type="button"
                    class="ph-plan-seeall"
                    @click.stop="openIncludesModal(plan.key)"
                  >
                    <span v-if="remainingIncluded(plan.key) > 0">+ {{ remainingIncluded(plan.key) }} more — see all</span>
                    <span v-else>See all included courses</span>
                  </button>
                </div>
                <button
                  type="button"
                  class="ph-btn ph-plan-cta"
                  :class="companyConfig.plan === plan.key ? 'ph-btn--in-cart' : 'ph-btn--add'"
                >
                  {{ companyConfig.plan === plan.key ? '✓ Selected' : plan.cta }}
                </button>
                <div v-if="plan.key === 'hot'" class="ph-plan-hint">
                  Don't serve food?
                  <button type="button" class="ph-plan-hint-link" @click.stop="selectAltOption('build')">See build your own →</button>
                </div>
              </article>
            </div>

            <!-- Compact config strip — replaces the heavier "Configure your team"
                 panel. Number inputs only (sliders dropped); billing toggle inline. -->
            <div class="ph-config-strip">
              <div class="ph-config-item">
                <span class="ph-config-label">Employees</span>
                <input
                  type="number"
                  min="1"
                  max="5000"
                  class="ph-config-input"
                  :value="companyConfig.total_users"
                  @input="onUsersInput($event)"
                />
              </div>
              <div class="ph-config-item">
                <span class="ph-config-label">Locations</span>
                <input
                  type="number"
                  min="1"
                  max="500"
                  class="ph-config-input"
                  :value="companyConfig.locations"
                  @input="onLocationsInput($event)"
                />
              </div>
              <div class="ph-config-item ph-config-item--billing">
                <span class="ph-config-label">Billing</span>
                <div class="ph-cycle-toggle">
                  <button
                    type="button"
                    class="ph-cycle-opt"
                    :class="{ 'ph-cycle-opt--active': companyConfig.billing_cycle === 'monthly' }"
                    @click="setBillingCycle('monthly')"
                  >Monthly</button>
                  <button
                    type="button"
                    class="ph-cycle-opt"
                    :class="{ 'ph-cycle-opt--active': companyConfig.billing_cycle === 'annual' }"
                    @click="setBillingCycle('annual')"
                  >
                    Annual
                    <span class="ph-save-pill">Save 10%</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Alternative (non-bundle) options — collapsed disclosure below
                 the team config so the plans grid stays the focal point. -->
            <details class="ph-alt-options ph-alt-options--collapsed">
              <summary class="ph-alt-options-summary">More ways to start</summary>
              <div class="ph-alt-options-grid">
                <button
                  v-for="alt in altOptions"
                  :key="alt.key"
                  type="button"
                  class="ph-alt-tile"
                  :class="{ 'ph-alt-tile--on': altActiveKey === alt.key }"
                  @click="selectAltOption(alt.key)"
                >
                  <span class="ph-alt-icon">{{ alt.icon }}</span>
                  <div class="ph-alt-body">
                    <div class="ph-alt-name">{{ alt.name }}</div>
                    <div class="ph-alt-tag">{{ alt.tagline }}</div>
                  </div>
                  <span class="ph-alt-check" v-if="altActiveKey === alt.key">✓</span>
                </button>
              </div>
            </details>

            <!-- Build-your-own OR plan-extras. Shows the full catalog when
                 no base plan is picked (BYO), or the catalog minus already-
                 included courses when a plan is selected. -->
            <div v-if="showExtrasPanel" class="ph-extras-panel">
              <div class="ph-extras-head">
                <div class="ph-extras-title">
                  <h3>{{ isBuildYourOwn ? 'Pick your courses' : 'Need something extra?' }}</h3>
                  <p>
                    <template v-if="isBuildYourOwn">
                      Hand-pick the courses your team needs. Each one is a
                      one-time add — yours to keep across seats and locations.
                    </template>
                    <template v-else>
                      Add individual courses on top of your {{ selectedPlan ? selectedPlan.name : 'plan' }}.
                      One-time purchase — keeps forever.
                    </template>
                  </p>
                </div>
                <div class="ph-categories ph-categories--extras">
                  <button
                    v-for="cat in categories"
                    :key="'extras-' + cat"
                    type="button"
                    class="ph-cat-chip"
                    :class="{ 'ph-cat-chip--active': catalogCategory === cat }"
                    @click="catalogCategory = cat; extrasPage = 1"
                  >{{ cat }}</button>
                </div>
                <div class="ph-extras-search">
                  <span aria-hidden="true">🔎</span>
                  <input
                    type="search"
                    class="ph-extras-search-input"
                    :placeholder="isBuildYourOwn ? 'Search 200+ courses by name or category…' : 'Search extras…'"
                    :value="catalogQuery"
                    @input="catalogQuery = $event.target.value; extrasPage = 1"
                  />
                </div>
              </div>
              <div v-if="!extraCoursesPaged.length" class="ph-extras-empty">
                No courses match “{{ catalogQuery }}”.
                <button v-if="catalogQuery" type="button" class="ph-extras-clear" @click="catalogQuery = ''">Clear search</button>
              </div>
              <div v-else class="ph-extras-grid">
                <article
                  v-for="entry in extraCoursesPaged"
                  :key="'extra-' + entry.key"
                  class="ph-extra-card"
                  :class="{ 'ph-extra-card--group': entry.isGroup }"
                >
                  <div class="ph-extra-name">
                    {{ entry.name }}
                    </div>
                  <div class="ph-extra-meta">
                    <span class="ph-extra-cat">{{ entry.category }}</span>
                    <span class="ph-extra-price">
                      <template v-if="entry.isGroup && !entry.costSame">From </template>${{ formatPrice(entry.cost) }}
                    </span>
                  </div>
                  <button
                    type="button"
                    class="ph-btn ph-extra-btn"
                    :class="(!entry.isGroup && isInCart(entry.id)) ? 'ph-btn--in-cart' : 'ph-btn--add'"
                    :disabled="!entry.isGroup && busyCourseId === entry.id"
                    @click="onCourseCtaClick(entry)"
                  >
                    <template v-if="entry.isGroup">Choose version →</template>
                    <template v-else>{{ isInCart(entry.id) ? '✓ Added' : '+ Add to cart' }}</template>
                  </button>
                </article>
              </div>
              <div v-if="extraCourses.length > extrasPageSize" class="ph-extras-pagination">
                <button type="button" class="ph-extras-more" @click="extrasPage = extrasPage + 1" v-if="(extrasPage * extrasPageSize) < extraCourses.length">
                  Show more courses ({{ extraCourses.length - (extrasPage * extrasPageSize) }} left)
                </button>
                <button type="button" class="ph-extras-more" @click="extrasPage = 1" v-else-if="extrasPage > 1">
                  Collapse
                </button>
              </div>
            </div>

            <!-- Add-ons picker — only renders in LMS mode + once a plan or
                 BYO is active (no plan = nothing to add to). License mode
                 doesn't surface add-ons; that path is its own thing. -->
            <AddOnsPicker
              v-if="(selectedPlan || isBuildYourOwn) && addons && addons.length"
              :addons="addons"
              v-model="selectedAddons"
              :users="companyConfig.total_users"
              :locations="companyConfig.locations"
            />
            </template>

            <!-- ─── License-content mode ─── -->
            <template v-else>
              <LicensePane :catalog="catalog" v-model="licenseConfig" />
            </template>
          </div>

          </div>
        </div>

        <!-- CART SIDEBAR — lives inside the hero -->
        <aside class="ph-cart" aria-label="Your cart" aria-live="polite" aria-atomic="false">
          <div class="ph-cart-inner">
            <header class="ph-cart-header">
              <h3>{{ cartTitle }}</h3>
              <span v-if="tab === 'individual' && cart.item_count" class="ph-cart-count">
                {{ cart.item_count }} item{{ cart.item_count === 1 ? '' : 's' }}
              </span>
              <span v-else-if="tab === 'company' && selectedPlan" class="ph-cart-count ph-cart-count--plan">
                {{ selectedPlan.name }}
              </span>
            </header>

            <!-- Cart revival pill — shown when an old cart was restored on mount.
                 30-day server-side TTL means buyers can return weeks later and
                 see ghost items they forgot about; the pill makes it explicit. -->
            <div v-if="showRevivalPill" class="ph-cart-revival" role="status">
              <span class="ph-cart-revival-text">
                Resumed cart from {{ revivalAgeDays }}
                {{ revivalAgeDays === 1 ? 'day' : 'days' }} ago
              </span>
              <button type="button" class="ph-cart-revival-clear" @click="clearRevivedCart">
                Clear cart
              </button>
              <button type="button" class="ph-cart-revival-dismiss" @click="dismissRevivalPill" aria-label="Dismiss revival notice">×</button>
            </div>

            <!-- Individual items -->
            <div v-if="tab === 'individual'" class="ph-cart-body">
              <div v-if="!cart.items || cart.items.length === 0" class="ph-cart-empty">
                <div class="ph-cart-empty-icon">🛒</div>
                <p>Click any course on the left to add it.</p>
              </div>
              <ul v-else class="ph-cart-list">
                <li v-for="item in cart.items" :key="item.id" class="ph-cart-item">
                  <div class="ph-cart-item-name">{{ item.course_name }}</div>
                  <div class="ph-cart-item-row">
                    <span class="ph-cart-item-price">${{ formatPrice(item.line_total) }}</span>
                    <button
                      class="ph-cart-remove"
                      :class="{ 'ph-cart-remove--locked': isPlanLocked(item.course_id) }"
                      type="button"
                      :disabled="isPlanLocked(item.course_id)"
                      :title="isPlanLocked(item.course_id) ? 'Included with your plan — pick a different plan or Build Your Own to drop it.' : 'Remove'"
                      @click="removeCourse(item.course_id)"
                      :aria-label="isPlanLocked(item.course_id) ? 'Included with plan — locked' : 'Remove'"
                    >{{ isPlanLocked(item.course_id) ? '🔒' : '×' }}</button>
                  </div>
                </li>
              </ul>
            </div>

            <!-- Company summary -->
            <div v-if="tab === 'company'" class="ph-cart-body">
              <div class="ph-summary-row">
                <span>Plan</span>
                <strong class="ph-summary-plan">{{ planLabel }}</strong>
              </div>
              <div class="ph-summary-row ph-summary-row--cycle">
                <span>Billing</span>
                <div class="ph-cart-cycle" role="radiogroup" aria-label="Billing cycle">
                  <button
                    type="button"
                    class="ph-cart-cycle-opt"
                    role="radio"
                    :aria-checked="companyConfig.billing_cycle === 'monthly' ? 'true' : 'false'"
                    :class="{ 'ph-cart-cycle-opt--active': companyConfig.billing_cycle === 'monthly' }"
                    @click="setBillingCycle('monthly')"
                  >Monthly</button>
                  <button
                    type="button"
                    class="ph-cart-cycle-opt"
                    role="radio"
                    :aria-checked="companyConfig.billing_cycle === 'annual' ? 'true' : 'false'"
                    :class="{ 'ph-cart-cycle-opt--active': companyConfig.billing_cycle === 'annual' }"
                    @click="setBillingCycle('annual')"
                  >Annual <span class="ph-cart-cycle-save">-10%</span></button>
                </div>
              </div>
              <div class="ph-summary-row ph-summary-row--calc">
                <span>{{ companyConfig.total_users }} employees × ${{ selectedPlan ? formatPrice(selectedPlan.per_user_rate) : '0' }}</span>
                <strong>${{ formatPrice((companyConfig.total_users || 0) * (selectedPlan ? selectedPlan.per_user_rate : 0)) }}</strong>
              </div>
              <div class="ph-summary-row ph-summary-row--calc">
                <span>{{ companyConfig.locations }} location{{ companyConfig.locations === 1 ? '' : 's' }} × $19</span>
                <strong>${{ formatPrice((companyConfig.locations || 0) * LOCATION_FEE) }}</strong>
              </div>
              <div class="ph-summary-row ph-summary-row--rate">
                <span>Plan monthly</span>
                <strong>${{ formatPrice(companySimpleMonthly) }}</strong>
              </div>

              <!-- Ad-hoc extras on top of the plan -->
              <div v-if="cart.items && cart.items.length" class="ph-summary-extras">
                <div class="ph-summary-extras-head">
                  <span>Extra courses</span>
                  <span>{{ cart.items.length }} item{{ cart.items.length === 1 ? '' : 's' }}</span>
                </div>
                <ul class="ph-cart-list ph-cart-list--compact">
                  <li v-for="item in cart.items" :key="'cex-' + item.id" class="ph-cart-item">
                    <div class="ph-cart-item-name">{{ item.course_name }}</div>
                    <div class="ph-cart-item-row">
                      <span class="ph-cart-item-price">${{ formatPrice(item.line_total) }}</span>
                      <button
                      class="ph-cart-remove"
                      :class="{ 'ph-cart-remove--locked': isPlanLocked(item.course_id) }"
                      type="button"
                      :disabled="isPlanLocked(item.course_id)"
                      :title="isPlanLocked(item.course_id) ? 'Included with your plan — pick a different plan or Build Your Own to drop it.' : 'Remove'"
                      @click="removeCourse(item.course_id)"
                      :aria-label="isPlanLocked(item.course_id) ? 'Included with plan — locked' : 'Remove'"
                    >{{ isPlanLocked(item.course_id) ? '🔒' : '×' }}</button>
                    </div>
                  </li>
                </ul>
              </div>

              <div class="ph-summary-volume">
                Volume discounts applied automatically at checkout.
              </div>
            </div>

            <!-- Promo code row -->
            <div class="ph-promo">
              <div v-if="cart.promo_code" class="ph-promo-applied">
                <span><strong>{{ cart.promo_code }}</strong> applied</span>
                <button type="button" @click="clearPromo">Remove</button>
              </div>
              <div v-else class="ph-promo-input-row">
                <input
                  type="text"
                  v-model="promoCodeInput"
                  placeholder="Promo code"
                  class="ph-promo-input"
                  @keyup.enter="applyPromo"
                />
                <button type="button" class="ph-promo-apply" @click="applyPromo">Apply</button>
              </div>
              <div v-if="promoMessage" class="ph-promo-message" :class="{ 'ph-promo-message--err': promoError }">{{ promoMessage }}</div>
            </div>

            <!-- Add-on summary — sandwiched between the plan summary and
                 the discount/total stack so the buyer sees what's
                 contributing to the bottom line before the total reveal. -->
            <div v-if="addonLineItems.length" class="ph-cart-addons">
              <div class="ph-cart-addons-head">
                <span>Add-ons</span>
                <span>{{ addonLineItems.length }}</span>
              </div>
              <ul class="ph-cart-list ph-cart-list--compact">
                <li
                  v-for="addon in addonLineItems"
                  :key="'cart-addon-' + addon.key"
                  class="ph-cart-item"
                >
                  <div class="ph-cart-item-name">{{ addon.label }}</div>
                  <div class="ph-cart-item-row">
                    <span class="ph-cart-item-price">${{ formatPrice(addon.monthly) }}<small>/mo</small></span>
                  </div>
                </li>
              </ul>
            </div>

            <!-- Totals (only when non-zero) -->
            <div v-if="cartHasContents" class="ph-cart-totals">
              <div class="ph-totals-row">
                <span>Subtotal</span>
                <span>${{ formatPrice(displayedSubtotal) }}</span>
              </div>
              <div
                v-for="d in discountBreakdown"
                :key="'cart-' + d.key"
                class="ph-totals-row ph-totals-row--discount"
              >
                <span>{{ d.label }}</span>
                <span>−${{ formatPrice(d.amount) }}</span>
              </div>
              <!-- Invoice surcharge intentionally NOT broken out here.
                   Owner directive 2026-05-03: just show the increased
                   total — the small ⓘ on the Invoice pill in the modal
                   explains why. surchargeBreakdown computed stays alive
                   for backend payload + lead email. -->

              <div class="ph-totals-row ph-totals-row--total">
                <span>Total</span>
                <span>${{ formatPrice(displayedTotal) }}<small v-if="tab === 'company'">/{{ companyConfig.billing_cycle === 'annual' ? 'yr' : 'mo' }}</small></span>
              </div>

              <button
                type="button"
                class="ph-btn ph-btn--checkout"
                @click="openCheckoutModal"
              >
                <span v-if="tab === 'company' && licenseMode === 'chooseOtherPlatform'">
                  Continue — request license
                </span>
                <span v-else-if="tab === 'company' && paymentType === 'invoice'">
                  Continue — pay by invoice
                </span>
                <span v-else>
                  Pay Now — ${{ formatPrice(displayedTotal) }} →
                </span>
              </button>

              <div class="ph-checkout-trust">
                <span>🔒 Secure · Stripe</span>
              </div>
            </div>

            <!-- Tiny inline off-ramps — sits under the cart but doesn't
                 visually compete with it. Schedule-demo opens a modal;
                 support is a plain mailto. Lifted from legacy /sign-up's
                 PaymentsDetails.vue side rail but stripped to two text
                 links so the cart stays the dominant surface. -->
            <ScheduleDemoCard :prefill="sharedContactPrefill" />
            <a
              class="ph-cart-support-link"
              href="mailto:support@homeoftraining.com?subject=Question%20about%20HOT%20pricing"
              rel="noopener"
            >Or email <strong>support@homeoftraining.com</strong></a>
          </div>
        </aside>
      </div>
    </section>

    <!-- ═════════ TRUST BAND (stats on the left, compliance on the right) ═════════ -->
    <section class="ph-trust-band" data-reveal>
      <div class="container ph-trust-container">
        <div class="ph-trust-header">
          <span class="ph-trust-label">Trusted & compliant</span>
          <h2 class="ph-trust-title">Built on the same standards you trust your bank with.</h2>
        </div>
        <div class="ph-trust-grid">
          <div class="ph-trust-stat">
            <strong>40,000+</strong>
            <span>Locations trained</span>
          </div>
          <div class="ph-trust-stat">
            <strong>250+</strong>
            <span>Courses in library</span>
          </div>
          <div class="ph-trust-stat">
            <strong>7-day</strong>
            <span>Free trial</span>
          </div>
          <div class="ph-trust-badge-wrap">
            <img
              src="/compliance/21972-312_SOC_NonCPA.png"
              alt="AICPA SOC 2 Type II Compliant"
              class="ph-trust-badge"
              loading="lazy"
            />
            <span class="ph-trust-badge-label">SOC 2 Type II</span>
          </div>
          <div class="ph-trust-badge-wrap">
            <img
              src="/compliance/HIPAA_Badge.png"
              alt="HIPAA Compliant"
              class="ph-trust-badge"
              loading="lazy"
            />
            <span class="ph-trust-badge-label">HIPAA Compliant</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ═════════ FAQ ═════════ -->
    <section class="ph-faq" data-reveal>
      <div class="container">
        <h2 class="ph-faq-title">Common questions</h2>
        <div class="ph-faq-grid">
          <details class="ph-faq-item">
            <summary>Is there a free trial?</summary>
            <p>Yep. Every Company plan includes a 7-day free trial, no card needed.</p>
          </details>
          <details class="ph-faq-item">
            <summary>What payment methods do you take?</summary>
            <p>Cards via Stripe, ACH for company plans, and invoices on request. Receipts go out the moment you pay.</p>
          </details>
          <details class="ph-faq-item">
            <summary>Can I add courses or seats later?</summary>
            <p>Yes, any time, right from the dashboard. Volume discounts kick in automatically as you grow.</p>
          </details>
          <details class="ph-faq-item">
            <summary>How do certifications work?</summary>
            <p>Most courses hand you a state-approved certificate the second you pass. PDFs are stored in your account forever.</p>
          </details>
          <details class="ph-faq-item">
            <summary>What if I go over my plan?</summary>
            <p>You get a heads-up and a one-click upgrade. We won't lock anyone out mid-training.</p>
          </details>
          <details class="ph-faq-item">
            <summary>Any setup fees?</summary>
            <p>No setup fees on any plan. White-glove onboarding is a cheap add-on for company plans.</p>
          </details>
        </div>
      </div>
    </section>

    <!-- ═════════ CHECKOUT MODAL ═════════ -->
    <transition name="ph-modal-fade">
      <div v-if="checkoutModalOpen" class="ph-modal-backdrop" @click.self="closeCheckoutModal" @keydown.esc.stop="closeCheckoutModal">
        <div
          class="ph-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ph-modal-title"
          tabindex="-1"
          ref="checkoutModal"
          @keydown.tab="trapFocus($event, 'checkoutModal')"
        >
          <button type="button" class="ph-modal-close" @click="closeCheckoutModal" aria-label="Close">×</button>
          <header class="ph-modal-header">
            <span class="ph-modal-eyebrow">Checkout</span>
            <h2 id="ph-modal-title">Almost there.</h2>
            <p>Drop your email and we'll send the receipt and login in a second.</p>
          </header>
          <div class="ph-modal-summary">
            <div v-if="tab === 'individual'">
              <div v-for="item in cart.items" :key="item.id" class="ph-modal-line">
                <span>{{ item.course_name }}</span>
                <strong>${{ formatPrice(item.line_total) }}</strong>
              </div>
            </div>
            <template v-else>
              <div v-if="licenseMode === 'chooseOtherPlatform'" class="ph-modal-line">
                <span>License — {{ (licenseConfig.selectedCourseIds || []).length }} courses · {{ licenseConfig.users }} learners · {{ licenseConfig.locations }} locations</span>
                <strong>${{ formatPrice(licenseMonthlyEstimate) }}<small>/mo est.</small></strong>
              </div>
              <div v-else class="ph-modal-line">
                <span>{{ planLabel }} — {{ companyConfig.total_users }} users · {{ companyConfig.locations }} locations</span>
                <strong>${{ formatPrice(companyDisplayedTotal) }}<small>/{{ companyConfig.billing_cycle === 'annual' ? 'yr' : 'mo' }}</small></strong>
              </div>
              <div
                v-for="addon in addonLineItems"
                :key="'modal-addon-' + addon.key"
                class="ph-modal-line ph-modal-line--addon"
              >
                <span>+ {{ addon.label }}</span>
                <strong>${{ formatPrice(addon.monthly) }}<small>/mo</small></strong>
              </div>
            </template>
            <div
              v-for="d in discountBreakdown"
              :key="'modal-' + d.key"
              class="ph-modal-line ph-modal-line--discount"
            >
              <span>{{ d.label }}</span>
              <strong>−${{ formatPrice(d.amount) }}</strong>
            </div>
            <!-- Invoice surcharge not broken out — see ph-totals-row
                 comment above. ⓘ on the Invoice pill explains it. -->

            <div class="ph-modal-line ph-modal-line--total">
              <span>Total today</span>
              <strong>${{ formatPrice(displayedTotal) }}</strong>
            </div>
          </div>
          <div class="ph-modal-form">
            <label class="ph-field ph-field--full">
              <span class="ph-field-label">Email address *</span>
              <input
                type="email"
                v-model="checkoutEmail"
                placeholder="you@example.com"
                class="ph-field-input"
                :class="{ 'ph-field-input--invalid': !!emailError }"
                @blur="checkEmail"
                aria-describedby="ph-email-help"
                required
              />
              <small
                v-if="emailError"
                id="ph-email-help"
                class="ph-promo-message ph-promo-message--err"
                role="alert"
              >
                {{ emailError }}
                <a
                  v-if="emailTaken"
                  href="/login"
                  class="ph-email-login-link"
                >Sign in instead →</a>
              </small>
              <small
                v-else-if="emailCheckLoading"
                class="ph-promo-message"
                aria-live="polite"
              >Checking…</small>
            </label>

            <!-- Individual: name + phone fields. Phone is required so receipts /
                 SMS workflows have a contact channel — matches legacy /sign-up
                 individual flow which always captured phone. -->
            <template v-if="tab === 'individual'">
              <div class="ph-field-row">
                <label class="ph-field">
                  <span class="ph-field-label">First name *</span>
                  <input
                    type="text"
                    v-model="buyer.first_name"
                    class="ph-field-input"
                    autocomplete="given-name"
                    required
                  />
                </label>
                <label class="ph-field">
                  <span class="ph-field-label">Last name *</span>
                  <input
                    type="text"
                    v-model="buyer.last_name"
                    class="ph-field-input"
                    autocomplete="family-name"
                    required
                  />
                </label>
              </div>
              <label class="ph-field ph-field--full">
                <span class="ph-field-label">Phone *</span>
                <input
                  type="tel"
                  :value="buyer.phone"
                  @input="onPhoneInput($event)"
                  @blur="phoneTouched = true"
                  class="ph-field-input"
                  :class="{ 'ph-field-input--invalid': phoneTouched && !!phoneError }"
                  autocomplete="tel"
                  inputmode="tel"
                  placeholder="(555) 555-5555"
                  maxlength="20"
                  aria-describedby="ph-phone-help-individual"
                  required
                />
                <small
                  v-if="phoneTouched && phoneError"
                  id="ph-phone-help-individual"
                  class="ph-promo-message ph-promo-message--err"
                  role="alert"
                >{{ phoneError }}</small>
              </label>
            </template>

            <!-- Company: business + admin fields -->
            <template v-if="tab === 'company'">
              <label class="ph-field ph-field--full">
                <span class="ph-field-label">Business name *</span>
                <input
                  type="text"
                  v-model="buyer.company_name"
                  class="ph-field-input"
                  autocomplete="organization"
                  required
                />
              </label>
              <div class="ph-field-row">
                <label class="ph-field">
                  <span class="ph-field-label">First name *</span>
                  <input
                    type="text"
                    v-model="buyer.first_name"
                    class="ph-field-input"
                    autocomplete="given-name"
                    required
                  />
                </label>
                <label class="ph-field">
                  <span class="ph-field-label">Last name *</span>
                  <input
                    type="text"
                    v-model="buyer.last_name"
                    class="ph-field-input"
                    autocomplete="family-name"
                    required
                  />
                </label>
              </div>
              <label class="ph-field ph-field--full">
                <span class="ph-field-label">Phone *</span>
                <input
                  type="tel"
                  :value="buyer.phone"
                  @input="onPhoneInput($event)"
                  @blur="phoneTouched = true"
                  class="ph-field-input"
                  :class="{ 'ph-field-input--invalid': phoneTouched && !!phoneError }"
                  autocomplete="tel"
                  inputmode="tel"
                  placeholder="(555) 555-5555"
                  maxlength="20"
                  aria-describedby="ph-phone-help"
                  required
                />
                <small
                  v-if="phoneTouched && phoneError"
                  id="ph-phone-help"
                  class="ph-promo-message ph-promo-message--err"
                  role="alert"
                >{{ phoneError }}</small>
              </label>
            </template>

            <!-- Password (both tabs) — captured now so the buyer can log in
                 right after fulfillment without a reset-password round-trip. -->
            <div class="ph-field-row">
              <label class="ph-field">
                <span class="ph-field-label">Password *</span>
                <input
                  type="password"
                  v-model="buyer.password"
                  class="ph-field-input"
                  autocomplete="new-password"
                  minlength="8"
                  maxlength="72"
                  required
                />
              </label>
              <label class="ph-field">
                <span class="ph-field-label">Confirm password *</span>
                <input
                  type="password"
                  v-model="buyer.password_confirm"
                  class="ph-field-input"
                  autocomplete="new-password"
                  minlength="8"
                  maxlength="72"
                  required
                />
              </label>
            </div>
            <div
              v-if="buyer.password && buyer.password_confirm && buyer.password !== buyer.password_confirm"
              class="ph-promo-message ph-promo-message--err"
              role="alert"
            >Passwords don't match.</div>
            <div
              v-else-if="buyer.password && buyer.password.length < 8"
              class="ph-promo-message ph-promo-message--err"
              role="alert"
            >Use at least 8 characters.</div>

            <!-- Payment-method picker — visible on both tabs (Josh
                 directive 2026-05-01). Compact pill row, same rhythm as
                 the billing-cycle toggle so the modal stays visually
                 quiet. Individual buyers picking invoice/ACH route through
                 the same legacy /company/register endpoint with their
                 first+last as the company_name fallback (see
                 submitAltCheckout). -->
            <fieldset class="ph-paytype">
              <legend class="ph-field-label">Payment</legend>
              <div class="ph-paytype-row" role="radiogroup">
                <button
                  type="button"
                  role="radio"
                  :aria-checked="paymentType === 'card' ? 'true' : 'false'"
                  class="ph-paytype-pill"
                  :class="{ 'ph-paytype-pill--on': paymentType === 'card' }"
                  @click="paymentType = 'card'"
                >Card</button>
                <button
                  type="button"
                  role="radio"
                  :aria-checked="paymentType === 'invoice' ? 'true' : 'false'"
                  class="ph-paytype-pill"
                  :class="{ 'ph-paytype-pill--on': paymentType === 'invoice' }"
                  @click="paymentType = 'invoice'"
                >Invoice<span
                  class="ph-paytype-info"
                  role="tooltip"
                  tabindex="0"
                  data-tip="Net-30 invoice. Small handling fee included."
                  aria-label="Net-30 invoice. Small handling fee included in the total."
                  @click.stop
                >ⓘ</span></button>
                <button
                  type="button"
                  role="radio"
                  :aria-checked="paymentType === 'ach' ? 'true' : 'false'"
                  class="ph-paytype-pill"
                  :class="{ 'ph-paytype-pill--on': paymentType === 'ach' }"
                  @click="paymentType = 'ach'"
                >ACH</button>
              </div>
            </fieldset>

            <!-- Stripe card. Elements-mode: buyer stays on /pricing through
                 payment, no bounce to checkout.stripe.com. Hidden when the
                 buyer picked invoice or ACH so the alt-payment forms own
                 the panel. v-show (not v-if) keeps the StripeElementCard
                 mounted so its iframe survives a back-and-forth between
                 payment methods without a re-tokenize.
                 Note (code review 2026-05-01): the prior gate read
                 `tab === 'individual' || paymentType === 'card'` which
                 left the iframe visible on individual tab even when the
                 buyer switched to invoice/ACH. Fixed to a single check. -->
            <div
              v-show="paymentType === 'card'"
              class="ph-stripe-field"
            >
              <span class="ph-field-label">Card details *</span>
              <stripe-element-card
                ref="stripeCard"
                :pk="stripePublicKey"
                :hidePostalCode="stripeHidePostalCode"
                @token="onStripeToken"
                @error="onStripeError"
              />
            </div>

            <!-- Invoice form — visible on both tabs when invoice is
                 selected. -->
            <InvoiceFieldsForm
              v-if="paymentType === 'invoice'"
              v-model="invoiceFields"
              :error="altPaymentError"
            />

            <!-- ACH form — visible on both tabs when ACH is selected. -->
            <AchFieldsForm
              v-if="paymentType === 'ach'"
              v-model="achFields"
              :error="altPaymentError"
            />

            <div v-if="!cart.promo_code" class="ph-modal-promo">
              <input
                type="text"
                v-model="promoCodeInput"
                placeholder="Promo code (optional)"
                class="ph-field-input"
                @keyup.enter="applyPromo"
              />
              <button type="button" class="ph-promo-apply" @click="applyPromo">Apply</button>
            </div>
            <div v-else class="ph-promo-applied" style="margin-bottom:14px">
              <!-- Just say the code is applied; the savings figure lives in
                   the itemized summary above. Pre-2026-04-30 this row also
                   showed a dollar amount that lumped Annual + Promo into
                   one number, which read as conflicting with the cart-side
                   panel that now itemizes the same two effects. -->
              <span><strong>{{ cart.promo_code }}</strong> applied</span>
              <button type="button" @click="clearPromo">Remove</button>
            </div>
            <div v-if="promoMessage" class="ph-promo-message" :class="{ 'ph-promo-message--err': promoError }">{{ promoMessage }}</div>
          </div>
          <!-- Pay + trial CTAs hide once a trial succeeds — token branch is
               redirecting; no-token branch shows a retry button on the
               success panel itself. Reduces "did anything happen?" noise. -->
          <template v-if="!trialSignupResult">
            <button
              type="button"
              class="ph-btn ph-btn--checkout ph-modal-cta"
              :disabled="!canCheckout || checkoutLoading || trialSignupLoading || altPaymentSubmitting"
              @click="startCheckout"
            >
              <span v-if="checkoutLoading || altPaymentSubmitting">
                {{ altPaymentSubmitting && paymentType === 'invoice' ? 'Setting up invoice…' : (altPaymentSubmitting && paymentType === 'ach' ? 'Submitting ACH details…' : 'Processing…') }}
              </span>
              <span v-else-if="tab === 'company' && paymentType === 'invoice'">
                Complete &amp; receive invoice — ${{ formatPrice(displayedTotal) }} →
              </span>
              <span v-else-if="tab === 'company' && paymentType === 'ach'">
                Complete with ACH — ${{ formatPrice(displayedTotal) }} →
              </span>
              <span v-else>Complete Purchase — ${{ formatPrice(displayedTotal) }} →</span>
            </button>
            <div v-if="paymentError" class="ph-demo-error" role="alert" aria-live="assertive">{{ paymentError }}</div>
            <div v-else-if="checkoutError" class="ph-demo-error" role="alert" aria-live="assertive">{{ checkoutError }}</div>
            <div v-else-if="altPaymentError" class="ph-demo-error" role="alert" aria-live="assertive">{{ altPaymentError }}</div>
          </template>

          <!-- Company-only: cardless 7-day free trial. Sign up now, pay later.
               Hidden when the buyer's already chosen invoice or ACH — those
               are also "no card required" paths so showing both options
               muddies the UX. License-content mode hides the trial too;
               license deals are bespoke. -->
          <template v-if="tab === 'company' && !trialSignupResult && paymentType === 'card' && licenseMode === 'chooseLMS'">
            <div class="ph-modal-or">or</div>
            <button
              type="button"
              class="ph-modal-trial-cta"
              :disabled="!canStartTrial || checkoutLoading || trialSignupLoading"
              @click="startFreeTrial"
            >
              <span v-if="trialSignupLoading">Setting up your trial…</span>
              <span v-else>Start 7-day free trial <small>— no card required</small></span>
            </button>
            <div v-if="trialSignupError" class="ph-demo-error" role="alert" aria-live="assertive">{{ trialSignupError }}</div>
          </template>

          <!-- Trial success panel — replaces the trial CTA once provisioned -->
          <div v-if="trialSignupResult" class="ph-modal-trial-result" role="status" aria-live="assertive">
            <div class="ph-modal-trial-result-icon" aria-hidden="true">
              <span v-if="trialSignupResult.token">✓</span>
              <span v-else>✉</span>
            </div>
            <template v-if="trialSignupResult.token">
              <strong>You're in. Trial started.</strong>
              <p class="ph-modal-trial-result-body">
                7 days free — no card needed. We'll let you know before it ends so you can add a card and keep going.
              </p>
              <p class="ph-modal-trial-redirect">Taking you to your dashboard…</p>
            </template>
            <template v-else>
              <strong>Check your inbox.</strong>
              <p class="ph-modal-trial-result-body">
                That email already has an account, so we sent a setup link to
                <strong>{{ trialSignupResult.email }}</strong>. Open it to pick up where you left off.
              </p>
              <div class="ph-modal-trial-result-actions">
                <button
                  type="button"
                  class="ph-modal-trial-retry"
                  @click="resetTrialForRetry"
                >Use a different email</button>
              </div>
            </template>
          </div>

          <div class="ph-modal-trust">
            <span>🔒 Secure · Stripe</span>
            <span>·</span>
            <span>7-day trial</span>
            <span>·</span>
            <span>No setup fees</span>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═════════ "WHAT'S INCLUDED" PLAN MODAL ═════════ -->
    <transition name="ph-modal-fade">
      <div
        v-if="includesModal.open"
        class="ph-modal-backdrop"
        @click.self="closeIncludesModal"
        @keydown.esc.stop="closeIncludesModal"
      >
        <div
          class="ph-modal ph-modal--includes"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ph-includes-title"
          tabindex="-1"
          ref="includesModal"
          @keydown.tab="trapFocus($event, 'includesModal')"
        >
          <button type="button" class="ph-modal-close" @click="closeIncludesModal" aria-label="Close">×</button>
          <header class="ph-modal-header">
            <span class="ph-modal-eyebrow">What's included</span>
            <h2 id="ph-includes-title">{{ includesModalPlan ? includesModalPlan.name : '' }} plan</h2>
            <p>{{ includedCoursesFor(includesModal.planKey).length }} course{{ includedCoursesFor(includesModal.planKey).length === 1 ? '' : 's' }} — sorted by compliance priority.</p>
          </header>
          <div class="ph-includes-body">
            <ul class="ph-includes-list">
              <li v-for="c in includedCoursesFor(includesModal.planKey)" :key="'im-' + c.id" class="ph-includes-item">
                <span class="ph-check">✓</span>
                <span class="ph-includes-name">{{ c.name }}</span>
                <span v-if="c.category" class="ph-includes-cat">{{ c.category }}</span>
              </li>
            </ul>
          </div>
          <button
            type="button"
            class="ph-btn ph-btn--checkout ph-modal-cta"
            @click="selectPlan(includesModal.planKey); closeIncludesModal()"
          >
            Select {{ includesModalPlan ? includesModalPlan.name : '' }} →
          </button>
        </div>
      </div>
    </transition>

    <!-- CompanySignupWizard (Plan D — reached behind ?wizard=1 in Phase A/B,
         becomes the default company-buyer flow in Phase C). -->
    <CompanySignupWizard
      :visible.sync="companyWizardOpen"
      :cart="cart"
      :session-uuid="sessionUuid"
      :catalog="catalog"
      :plan-included-ids="planIncludedIds"
      :company-plans="companyPlans"
      :alt-options="altOptions"
      :api-base="apiBase"
      :stripe-public-key="stripePublicKey"
      :initial-step="companyWizardInitialStep"
      :initial-plan="companyWizardInitialPlan"
      @stepChanged="onWizardStepChanged"
      @paid="onWizardPaid"
      @trialed="onWizardTrialed"
    />

    <!-- Per-course user-count prompt — opened from addCourse when the
         buyer clicks a discounted-flag course on the company tab. -->
    <SpecialCoursePromptModal
      :open="specialCourseModalOpen"
      :course="specialCoursePending"
      :default-users="companyConfig.total_users"
      @confirm="onSpecialCourseConfirm"
      @close="onSpecialCourseClose"
    />

    <!-- Enterprise / "talk to sales" — auto-opens when sliders cross
         the 2,500-employee or 500-location threshold so we never let an
         enterprise lead slip past with a quote that under-states our
         volume pricing. -->
    <EnterpriseRequestModal
      :open="enterpriseModalOpen"
      :prefill="sharedContactPrefill"
      @close="dismissEnterpriseModal"
    />

    <!-- Course-variant picker — opens from a grouped course card.
         Buyer picks one (or several) of the state/role variants of a
         multi-version course. -->
    <VariantPickerModal
      :open="variantPickerOpen"
      :group="variantPickerGroup"
      :cart-ids="(cart.items || []).map((i) => i.course_id)"
      :busy-id="busyCourseId"
      @pick="onVariantPicked"
      @close="closeVariantPicker"
    />

    <Footer />
  </div>
</template>

<script>
import axios from 'axios';
import { StripeElementCard } from '@vue-stripe/vue-stripe';
import Header from '../Common/Header.vue';
import Footer from '../Common/Footer.vue';
import CompanySignupWizard from '../Components/CompanySignupWizard.vue';

const API_BASE = (process.env.VUE_APP_API_URL || 'http://localhost:8000/').replace(/\/$/, '');

// localStorage key for UI state that's not on the server-side cart row —
// tab selection, company sliders, buyer contact fields. Cart line items,
// promo, and email already round-trip through the cart row keyed by
// hot-pricing-cart-uuid; this just covers the per-buyer UI bits the cart
// doesn't store. Bumped suffix forces stale shapes from older versions
// to be ignored on first read instead of crashing the restore.
const LS_UI_KEY = 'hot-pricing-ui-v1';
// Drop persisted UI state after 14 days — same window the cron uses for
// abandoned-cart drips; older sessions are stale anyway.
const LS_UI_TTL_MS = 14 * 24 * 60 * 60 * 1000;
const PAGE_SIZE_DESKTOP = 25;
const PAGE_SIZE_MOBILE = 12;

import AddOnsPicker from '@/components/Pricing/AddOnsPicker.vue';
import ScheduleDemoCard from '@/components/Pricing/ScheduleDemoCard.vue';
import EnterpriseRequestModal from '@/components/Pricing/EnterpriseRequestModal.vue';
import SpecialCoursePromptModal from '@/components/Pricing/SpecialCoursePromptModal.vue';
import InvoiceFieldsForm from '@/components/Pricing/InvoiceFieldsForm.vue';
import AchFieldsForm from '@/components/Pricing/AchFieldsForm.vue';
import LicenseModeChooser from '@/components/Pricing/LicenseModeChooser.vue';
import LicensePane from '@/components/Pricing/LicensePane.vue';
import VariantPickerModal from '@/components/Pricing/VariantPickerModal.vue';
import { groupCourses as groupCoursesUtil, nameStem as nameStemUtil, variantLabel as variantLabelUtil } from '@/utils/courseGrouping';

// Threshold (in employees OR locations) at which we surface the
// EnterpriseRequestModal — matches legacy /sign-up's PlanSelector.vue:221-228
// `checkEnterpriseLimit` floor of 2500. Auto-opens once per session, then
// stays dismissed so a buyer who's already declined doesn't keep getting
// nagged on every slider tick.
const ENTERPRISE_USERS_THRESHOLD = 2500;
const ENTERPRISE_LOCATIONS_THRESHOLD = 500;

// Multiplier applied to the company total when paymentType === 'invoice'.
// Matches legacy CompanyBillingInfo.vue:437-438 ("Payment using Invoice
// (Additional 10%)") so a buyer who's seen both flows gets the same number.
const INVOICE_SURCHARGE_RATE = 0.10;

// Per-location flat monthly fee. Module-level so computeds can reference
// it via PRICING_LOCATION_FEE without `this.` (ECC review 2026-05-03 —
// `this.LOCATION_FEE` was a latent NaN hazard if anyone refactored).
const PRICING_LOCATION_FEE = 19;

export default {
  name: 'PricingPage',
  components: {
    Header,
    Footer,
    StripeElementCard,
    CompanySignupWizard,
    AddOnsPicker,
    ScheduleDemoCard,
    EnterpriseRequestModal,
    SpecialCoursePromptModal,
    InvoiceFieldsForm,
    AchFieldsForm,
    LicenseModeChooser,
    LicensePane,
    VariantPickerModal,
  },

  metaInfo: {
    title: 'Pricing — Home of Training',
  },

  data() {
    return {
      tab: 'individual',
      tabs: [
        { key: 'individual', label: 'For Me',  sub: 'Single courses', icon: '👤' },
        { key: 'company',    label: 'My Team', sub: 'Plans + seats',  icon: '👥' },
      ],

      LOCATION_FEE: PRICING_LOCATION_FEE,
      // Owner toggle: hide HOT (food/alcohol-only) when the buyer doesn't serve food.
      showFoodPlan: true,
      // Three bundles. LMS-only + Build-your-own live in altOptions below,
      // rendered as secondary tiles so the grid doesn't look like 4/5 bundles.
      companyPlans: [
        {
          key: 'hot',
          name: 'HOT',
          tagline: 'Food + alcohol compliance',
          from_price: '2',
          per_user_rate: 2.00,
          popular: false,
          food_only: true,
          cta: 'Start with HOT',
        },
        {
          key: 'sizzling',
          name: 'SIZZLING',
          tagline: 'Safety + harassment',
          from_price: '3.50',
          per_user_rate: 3.50,
          popular: true,
          cta: 'Start with SIZZLING',
        },
        {
          key: 'scorching',
          name: 'SCORCHING',
          tagline: 'Everything',
          from_price: '5.50',
          per_user_rate: 5.50,
          popular: false,
          cta: 'Get Full Access',
        },
      ],
      // Secondary options — shown under the bundles, visually distinct.
      // LMS Only has a per_user_rate so it flows through the same
      // cart-summary math as the bundles (see selectedPlan below, which
      // also searches altOptions). Build Your Own is plan=null so the
      // buyer assembles à la carte from the extras panel.
      altOptions: [
        {
          key: 'build',
          name: 'Build your own',
          tagline: 'Hand-pick only the courses you need.',
          icon: '🧩',
        },
        {
          key: 'lms_only',
          name: 'LMS Only',
          tagline: 'Just the platform — bring your own training.',
          icon: '📦',
          per_user_rate: 0.99,
          from_price: '0.99',
          cta: 'Start with LMS Only',
        },
      ],

      catalog: [],
      plans: [],
      addons: [],
      stats: {},

      sessionUuid: null,
      cart: {
        items: [],
        item_count: 0,
        subtotal: 0,
        discount: 0,
        total: 0,
        promo_code: null,
        plan_rate: null,
        account_type: 'individual',
      },

      catalogQuery: '',
      catalogCategory: 'Best Selling',
      catalogPage: 1,
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1440,
      busyCourseId: null,

      companyConfig: {
        plan: 'sizzling',
        billing_cycle: 'monthly',
        total_users: 25,
        locations: 1,
      },
      companyDebounce: null,

      promoCodeInput: '',
      promoMessage: '',
      promoError: false,

      checkoutEmail: '',
      checkoutLoading: false,
      checkoutError: '',
      checkoutModalOpen: false,

      // Email-uniqueness pre-flight. Hits /user/valid-email on blur — the same
      // legacy endpoint /sign-up uses (CompanyBillingInfo.vue:685-716). Without
      // this, a duplicate-account buyer types their email, fills out the form,
      // submits payment, and only finds out their account exists after Stripe
      // pre-auths the card. emailTaken=true gates canCheckout / canStartTrial.
      // Network errors leave emailTaken=false so we don't punish flaky wifi.
      emailCheckLoading: false,
      emailTaken: false,
      emailError: '',

      // Surfaces API failures from cart/bootstrap to the user. Set via
      // setCartError() — cleared when the next successful request completes.
      cartError: '',
      cartInitialized: false,

      // Focus restoration target for modal close — the element that opened it.
      lastFocused: null,

      // Sign-up fields collected at checkout — routed through cart to the
      // fulfillment service which creates the real tbl_employee + tbl_company.
      //
      // password / password_confirm let the buyer log in immediately after
      // fulfillment (no forgot-password round-trip). Never sent to Stripe;
      // md5-hashed at the backend boundary (matching the rest of HOT's
      // auth) and scrubbed off tbl_cart after fulfillment copies it onto
      // tbl_employee.password.
      buyer: {
        first_name: '',
        last_name: '',
        company_name: '',
        phone: '',
        password: '',
        password_confirm: '',
      },

      // Stripe Elements embed. stripePublicKey is compile-time (VUE_APP_*),
      // matching every other Stripe-element page in the repo. payment_token
      // is the tok_... emitted by <stripe-element-card> on Pay click; we
      // forward it to /checkout/intent which creates + confirms a
      // PaymentIntent in one round-trip.
      stripePublicKey: process.env.VUE_APP_STRIPE_PUBLIC_KEY,
      // hidePostalCode=true because our Stripe accounts already validate
      // billing ZIP via `billing_details` / radar rules — the embedded ZIP
      // input in the CardElement duplicates that and adds an extra field
      // buyers have to fill.
      stripeHidePostalCode: true,
      // Payment-specific error surface (card declines, 3DS failures). Kept
      // separate from checkoutError so a validation message doesn't wipe
      // the Stripe-side decline reason the buyer just saw.
      paymentError: '',

      // Cardless free-trial flow (company path). POSTs sign-up only, no payment.
      trialSignupLoading: false,
      trialSignupResult: null,
      trialSignupError: '',

      // Phone "touched" tracker so we don't shout at the buyer the moment
      // the field appears empty — only after they've blurred or left
      // partial digits. The masked input formats on every keystroke so the
      // error message stays accurate even while they type.
      phoneTouched: false,

      demoForm: { email: '', first_name: '', last_name: '', company_name: '' },
      demoLoading: false,
      demoResult: null,
      demoError: '',

      panesMinHeight: null,

      // Map of planKey -> included_course_ids (array of ints or 'all')
      planIncludedIds: {},
      extrasPage: 1,
      extrasPageSize: 6,

      includesModal: { open: false, planKey: null },

      // Cart revival pill: when a 30-day-old cart loads on /pricing mount,
      // show a dismissible banner with the age + a clear-cart link so the
      // buyer never sees ghost items they forgot about.
      revivalPillDismissed: false,

      // Landing-page prefill flow: ?course=, ?courses=, ?plan=, etc. resolve
      // to cart adds + tab/plan/billing config + optional autocheckout.
      // prefillRunning gates the loading overlay; prefillError surfaces any
      // unresolved slug so the buyer doesn't see a silent drop.
      prefillRunning: false,
      prefillError: '',

      // CompanySignupWizard scaffolding (Plan D Phase A). The wizard is
      // reachable behind ?wizard=1 only — the existing tabs are unchanged
      // until Phase C flips the UX. companyWizardInitialStep / Plan let
      // deep-link entries like ?wizard=1&plan=sizzling open Step 2 with
      // the plan preselected once Phase B wires the plan picker.
      companyWizardOpen: false,
      companyWizardInitialStep: 1,
      companyWizardInitialPlan: null,

      // ── Parity additions (sign-up retirement, 2026-05-01) ──
      // Selected add-on keys (subset of `addons[].key` from pricing-bootstrap).
      // Round-trips through cart/update-company so the backend can apply
      // entitlements at fulfillment time. Lifted from /sign-up's AddOns.vue.
      selectedAddons: [],

      // Per-course user counts captured by SpecialCoursePromptModal when a
      // company-tab buyer adds a course where `is_discounted_course === 0`.
      // Shape: [{ id, name, users }]. Mirrors legacy SelectCourses.vue's
      // `selectedSpecialCourses` so the backend's special-course pricing
      // path keys off the same field.
      specialCourseSelections: [],
      specialCourseModalOpen: false,
      specialCoursePending: null,

      // Payment method picker on the company tab. 'card' is the existing
      // Stripe Elements path (default). 'invoice' adds a 10% surcharge and
      // routes through legacy /company/register without charging at signup.
      // 'ach' goes through the same legacy register endpoint with bank
      // details. Individual tab is card-only.
      paymentType: 'card',
      invoiceFields: {
        invoice_name: '',
        invoice_emails: '',
        invoice_address: '',
        invoice_city: '',
        invoice_state: '',
      },
      achFields: {
        transit_number: '',
        account_number: '',
        financial_instituation: '',
        branch: '',
        address_1: '',
        city: '',
        state: '',
        zip: '',
      },
      // Loading/error surfaces for the non-Stripe payment paths so the
      // UI doesn't share state with the card flow's checkoutLoading /
      // paymentError (which Stripe owns).
      altPaymentSubmitting: false,
      altPaymentError: '',

      // Inside-My-Team-tab toggle: 'chooseLMS' = use HOT's LMS (default,
      // shows the existing plans grid). 'chooseOtherPlatform' = buyer
      // licenses our courses to embed in their own LMS — replaces the
      // plans grid with LicensePane.vue. Mirrors legacy ChoosePlatform.vue
      // values so backend handlers reading `choose_platform` stay valid.
      licenseMode: 'chooseLMS',
      licenseConfig: {
        users: 25,
        locations: 1,
        selectedCourseIds: [],
      },

      // Auto-opens the EnterpriseRequestModal once when sliders cross the
      // threshold — `enterpriseDismissed` keeps a buyer who declined from
      // getting re-nagged each time they tweak a value.
      enterpriseModalOpen: false,
      enterpriseDismissed: false,

      // Variant picker modal — opens when the buyer clicks "Choose
      // version" on a grouped course card (state-specific siblings
      // collapsed into one card). Picking a variant calls addCourse()
      // with that variant's id; everything downstream (cart, special-
      // courses prompt, plan-locked guard) keys off course-level data
      // unchanged.
      variantPickerOpen: false,
      variantPickerGroup: null,

      // Billing-cycle toggle smoothing (UAT 2026-05-01, fix/pricing-wizard-opt-out).
      // When the buyer flips monthly↔annual, the client-side
      // companyDisplayedTotal computed updates instantly while the
      // server cart re-price lands ~500ms later via queueCompanySync,
      // producing a visible "two changes in a row" flicker. We freeze
      // the displayed total for the duration of the in-flight sync,
      // then commit one update on response.
      cycleSyncing: false,
      frozenDisplayedTotal: null,
    };
  },

  computed: {
    apiBase() { return API_BASE; },
    pageSize() { return this.viewportWidth < 768 ? PAGE_SIZE_MOBILE : PAGE_SIZE_DESKTOP; },
    categories() {
      // Chip order:
      //   Best Selling (virtual, backed by c.is_best_seller — surfaces
      //     only when the catalog has any best-sellers tagged)
      //   Compliance
      //   ...remaining categories, alpha sorted
      //   All (last — "see everything" escape)
      const found = new Set();
      this.catalog.forEach((c) => { if (c && c.category) found.add(c.category); });
      const ordered = [];
      const hasBestSellers = (this.catalog || []).some((c) => c && c.is_best_seller);
      if (hasBestSellers) ordered.push('Best Selling');
      if (found.has('Compliance')) { ordered.push('Compliance'); found.delete('Compliance'); }
      Array.from(found).sort().forEach((c) => ordered.push(c));
      ordered.push('All');
      return ordered;
    },
    filteredCatalog() {
      let list = this.catalog;
      if (this.catalogCategory === 'Best Selling') {
        // Virtual chip — filters on the boolean from pricing-bootstrap.
        list = list.filter((c) => c && c.is_best_seller);
      } else if (this.catalogCategory && this.catalogCategory !== 'All') {
        const allowed = this.matchingCategoriesFor(this.catalogCategory);
        list = list.filter((c) => allowed.indexOf(c.category) !== -1);
      }
      const q = this.catalogQuery.trim().toLowerCase();
      if (q) {
        list = list.filter(c =>
          c.name.toLowerCase().includes(q) ||
          (c.description && c.description.toLowerCase().includes(q))
        );
      }
      return list;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.groupedCatalog.length / this.pageSize));
    },
    catalogStartIdx() { return (this.catalogPage - 1) * this.pageSize; },
    catalogEndIdx() { return Math.min(this.groupedCatalog.length, this.catalogStartIdx + this.pageSize); },
    pagedCatalog() {
      return this.groupedCatalog.slice(this.catalogStartIdx, this.catalogEndIdx);
    },

    /**
     * Catalog with same-stem courses collapsed into one entry. Stem is
     * everything before the first " - " in the name. So
     *   "Alcohol Safety Training - Florida/Employee"
     *   "Alcohol Safety Training - Nationwide"
     *   "Alcohol Safety Training - New York / Off-Premises"
     *   "Alcohol Safety Training - New York / On-Premises"
     * collapse into ONE card titled "Alcohol Safety Training" with a
     * "4 versions" badge. Click → VariantPickerModal opens with the
     * four variants, buyer picks one, we add THAT variant to cart.
     *
     * Single-stem courses pass through with isGroup=false so the
     * existing card markup works unchanged.
     *
     * Owner directive 2026-05-03 — replaces the multi-card duplication
     * caught on the Compliance chip.
     */
    groupedCatalog() { return this.groupCourses(this.filteredCatalog); },
    groupedExtraCourses() { return this.groupCourses(this.extraCourses); },
    pageNumbers() {
      const total = this.totalPages;
      const cur = this.catalogPage;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      const pages = [1];
      if (cur > 3) pages.push('...');
      const start = Math.max(2, cur - 1);
      const end = Math.min(total - 1, cur + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (cur < total - 2) pages.push('...');
      pages.push(total);
      return pages;
    },
    selectedPlan() {
      // LMS Only lives in altOptions but needs to flow through all the
      // same cart-summary math as the main bundles — so we search both
      // lists. Build Your Own (plan=null) intentionally returns null so
      // the config/cart panels know there's no base plan rate to apply.
      if (this.companyConfig.plan === null) return null;
      return this.companyPlans.find(p => p.key === this.companyConfig.plan)
        || this.altOptions.find(p => p.key === this.companyConfig.plan && p.per_user_rate)
        || null;
    },
    visibleCompanyPlans() {
      return this.companyPlans.filter(p => this.showFoodPlan || !p.food_only);
    },
    // Which alt option (if any) is currently active.
    // 'build' = no base plan (companyConfig.plan === null); 'lms_only' = lms plan selected.
    altActiveKey() {
      if (this.companyConfig.plan === null) return 'build';
      if (this.companyConfig.plan === 'lms_only') return 'lms_only';
      return null;
    },
    includesModalPlan() {
      if (!this.includesModal.planKey) return null;
      return this.companyPlans.find(p => p.key === this.includesModal.planKey) || null;
    },
    planLabel() {
      return this.selectedPlan ? this.selectedPlan.name : '—';
    },
    companySimpleMonthly() {
      if (!this.selectedPlan) return 0;
      const users = Math.max(0, Number(this.companyConfig.total_users) || 0);
      const locs = Math.max(0, Number(this.companyConfig.locations) || 0);
      return (users * this.selectedPlan.per_user_rate) + (locs * this.LOCATION_FEE);
    },
    companyDisplayedTotal() {
      const monthly = this.companySimpleMonthly;
      if (this.companyConfig.billing_cycle === 'annual') {
        return monthly * 12 * 0.9;
      }
      return monthly;
    },
    companyDiscount() {
      if (this.companyConfig.billing_cycle !== 'annual') return 0;
      // 10% annual savings across the full company-side subtotal.
      // cart.subtotal/cart.total already have × 0.9 baked in by the
      // backend recalculate, so back it out to find the pre-discount
      // yearly figure, then take 10% of that. Add-ons get the same 10%.
      const cartSub = Number(this.cart.subtotal) || 0;
      const cartPortion = cartSub > 0 ? (cartSub / 0.9) * 0.1 : 0;
      const addonsPortion = (Number(this.addonsMonthly) || 0) * 12 * 0.1;
      return cartPortion + addonsPortion;
    },
    cartHasContents() {
      // Individual + Build-Your-Own company (à-la-carte, no plan): cart items
      //   alone satisfy "has contents". Without this, a company buyer on
      //   Build-Your-Own with courses in the cart but no plan selected gets
      //   a disabled Pay button (companyDisplayedTotal is 0 for BYO because
      //   companySimpleMonthly short-circuits when selectedPlan is null).
      // Plan-based company (HOT/SIZZLING/SCORCHING/LMS Only): accept either
      //   the plan-derived total OR à-la-carte items alongside the plan.
      // License-content mode: accept whenever the buyer has selected at
      //   least one course; pricing is the licenseMonthlyEstimate floor.
      const hasItems = !!(this.cart.items && this.cart.items.length > 0);
      if (this.tab === 'company') {
        if (this.licenseMode === 'chooseOtherPlatform') {
          const ids = (this.licenseConfig && this.licenseConfig.selectedCourseIds) || [];
          return ids.length > 0;
        }
        return this.companyDisplayedTotal > 0 || hasItems || this.addonsCycleTotal > 0;
      }
      return hasItems;
    },
    displayedTotal() {
      // While a billing-cycle toggle is in-flight, hold the price still.
      // Once the server sync resolves, frozenDisplayedTotal is cleared
      // and the live computed takes over with the final number — single
      // visible change instead of optimistic-then-server flicker.
      if (this.cycleSyncing && this.frozenDisplayedTotal !== null) {
        return this.frozenDisplayedTotal;
      }
      if (this.tab === 'company') {
        if (this.licenseMode === 'chooseOtherPlatform') {
          // License mode uses its own monthly estimate for the cart total
          // line. Annual savings + invoice surcharge layer on top.
          const monthly = this.licenseMonthlyEstimate;
          const cycleTotal = this.companyConfig.billing_cycle === 'annual'
            ? monthly * 12 * 0.9
            : monthly;
          return cycleTotal + this.invoiceSurcharge;
        }
        // cart.total already includes basePlanCost + extras + specials
        // (annualized when cycle=annual) on the company-with-plan and BYO
        // branches of PriceCalculateController. Adding companyDisplayedTotal
        // here double-counted the plan portion — buyer was quoted plan twice.
        return (Number(this.cart.total) || 0)
          + this.addonsCycleTotal
          + this.invoiceSurcharge;
      }
      return Number(this.cart.total) || 0;
    },
    displayedSubtotal() {
      if (this.tab === 'company') {
        if (this.licenseMode === 'chooseOtherPlatform') {
          const monthly = this.licenseMonthlyEstimate;
          return this.companyConfig.billing_cycle === 'annual' ? monthly * 12 : monthly;
        }
        // Pre-discount subtotal so the displayed savings line actually
        // reduces subtotal → total. Backend bakes the × 0.9 annual
        // discount into cart.subtotal, so back it out here when annual.
        const cartSub = Number(this.cart.subtotal) || 0;
        if (this.companyConfig.billing_cycle === 'annual') {
          const cartPre = cartSub > 0 ? cartSub / 0.9 : 0;
          const addonsPre = (Number(this.addonsMonthly) || 0) * 12;
          return cartPre + addonsPre;
        }
        return cartSub + (Number(this.addonsMonthly) || 0);
      }
      return Number(this.cart.subtotal) || 0;
    },
    displayedDiscount() {
      if (this.tab === 'company') return this.companyDiscount + (Number(this.cart.discount) || 0);
      return Number(this.cart.discount) || 0;
    },

    /**
     * Itemized discount breakdown — what the buyer sees as separate
     * "Annual savings (10%)" + "Promo (CODE)" lines in both the cart
     * sidebar and the checkout modal. Asad caught the dialog summarizing
     * to one "discount" while the cart side panel showed promo-and-annual
     * effects separately, which read as conflicting numbers. Driving
     * both surfaces from one computed makes them stay in sync.
     */
    discountBreakdown() {
      const items = [];
      // Annual cycle savings only apply on the company tab — the
      // displayedTotal already collapses monthly→annual at 10% off, so
      // this is the savings-vs-monthly-12x amount.
      if (this.tab === 'company'
          && this.companyConfig.billing_cycle === 'annual'
          && this.companyDiscount > 0) {
        items.push({
          key: 'annual',
          label: 'Annual savings (10%)',
          amount: this.companyDiscount,
        });
      }
      // Promo discount — server-side applied, dollar amount comes back on
      // cart.discount. We show the code so a buyer knows which promo is
      // active when stacked.
      const cartDiscount = Number(this.cart.discount) || 0;
      if (cartDiscount > 0) {
        items.push({
          key: 'promo',
          label: this.cart.promo_code ? `Promo (${this.cart.promo_code})` : 'Promo discount',
          amount: cartDiscount,
        });
      }
      return items;
    },

    /**
     * Selected add-on objects with their per-unit subtotal applied. The
     * unit math mirrors AddOnsPicker.monthlyForAddon so the cart-side
     * preview matches what the buyer saw on the picker. Backend recomputes
     * the canonical dollars at cart/update-company; this drives display
     * only. Returns an empty array on the individual tab — add-ons are
     * company-only.
     */
    addonLineItems() {
      if (this.tab !== 'company') return [];
      if (!Array.isArray(this.selectedAddons) || !this.selectedAddons.length) return [];
      if (!Array.isArray(this.addons)) return [];
      const users = Math.max(0, Number(this.companyConfig.total_users) || 0);
      const locations = Math.max(0, Number(this.companyConfig.locations) || 0);
      const out = [];
      this.selectedAddons.forEach((key) => {
        const a = this.addons.find((x) => x && x.key === key);
        if (!a) return;
        const price = Number(a.price) || 0;
        const unit = String(a.unit || '').toLowerCase();
        let monthly = price;
        if (unit.includes('per 100')) monthly = price * Math.max(1, Math.ceil(users / 100));
        else if (unit.includes('per 50')) monthly = price * Math.max(1, Math.ceil(users / 50));
        else if (unit.includes('per employee') || unit.includes('per user')) monthly = price * users;
        else if (unit.includes('per location')) monthly = price * locations;
        out.push({ key, label: a.name, monthly });
      });
      return out;
    },

    addonsMonthly() {
      return this.addonLineItems.reduce((sum, x) => sum + (Number(x.monthly) || 0), 0);
    },

    /**
     * Total contribution from add-ons after the active billing cycle is
     * applied — annual gets the same 10% discount the plan does so a
     * buyer toggling annual sees a consistent savings story across plan
     * + add-ons. Used by displayedTotal extension.
     */
    addonsCycleTotal() {
      const monthly = this.addonsMonthly;
      if (this.companyConfig.billing_cycle === 'annual') return monthly * 12 * 0.9;
      return monthly;
    },

    /**
     * 10% surcharge on top of the company subtotal when paymentType is
     * 'invoice'. Mirrors legacy CompanyBillingInfo.vue's "Payment using
     * Invoice (Additional 10%)" label so a buyer who's seen both flows
     * gets the same number. Card + ACH have no surcharge.
     */
    invoiceSurcharge() {
      if (this.tab !== 'company' || this.paymentType !== 'invoice') return 0;
      // cart.total already includes basePlanCost on the company-with-plan
      // branch, so adding companyDisplayedTotal here would surcharge the
      // plan twice. Use cart.total + addonsCycleTotal as the surcharge base.
      const base = (Number(this.cart.total) || 0)
        + (Number(this.addonsCycleTotal) || 0);
      return base * INVOICE_SURCHARGE_RATE;
    },

    /**
     * Surcharge breakdown — kept separate from discountBreakdown because
     * surcharges add to the total instead of subtracting. Modal + cart
     * both render this list under the existing discount lines.
     */
    surchargeBreakdown() {
      const items = [];
      if (this.invoiceSurcharge > 0) {
        items.push({
          key: 'invoice',
          label: 'Invoice processing (+10%)',
          amount: this.invoiceSurcharge,
        });
      }
      return items;
    },

    /**
     * Frontend estimate for license-content mode when active. Owned by
     * LicensePane internally for its own preview; here we surface the
     * monthly equivalent so the cart sidebar can show a unified "estimated
     * monthly" line that matches the rest of the page.
     */
    licenseMonthlyEstimate() {
      if (this.tab !== 'company' || this.licenseMode !== 'chooseOtherPlatform') return 0;
      const ids = (this.licenseConfig && this.licenseConfig.selectedCourseIds) || [];
      const users = Math.max(1, Number(this.licenseConfig.users) || 1);
      const locations = Math.max(1, Number(this.licenseConfig.locations) || 1);
      const courseSubtotal = (this.catalog || [])
        .filter((c) => ids.indexOf(c.id) !== -1)
        .reduce((sum, c) => sum + (Number(c.cost) || 0), 0) * users;
      return courseSubtotal + (locations * PRICING_LOCATION_FEE);
    },

    /**
     * Are buyer's sliders past the enterprise threshold? Drives the
     * EnterpriseRequestModal auto-open. Only triggers on the company
     * tab + LMS mode (license mode has its own quote flow).
     */
    enterpriseShouldOpen() {
      if (this.tab !== 'company') return false;
      if (this.licenseMode === 'chooseOtherPlatform') return false;
      const users = Number(this.companyConfig.total_users) || 0;
      const locs = Number(this.companyConfig.locations) || 0;
      return users >= ENTERPRISE_USERS_THRESHOLD || locs >= ENTERPRISE_LOCATIONS_THRESHOLD;
    },

    /**
     * Prefill blob handed to ScheduleDemoCard / EnterpriseRequestModal so
     * a buyer who's already typed their name/email/phone into the
     * checkout modal doesn't have to retype it on the demo or enterprise
     * forms. Read-only — child components copy on open.
     */
    sharedContactPrefill() {
      return {
        first_name: this.buyer.first_name || '',
        last_name: this.buyer.last_name || '',
        email: (this.checkoutEmail || '').trim(),
        phone: this.buyer.phone || '',
        company: this.buyer.company_name || '',
        users: Number(this.companyConfig.total_users) || 0,
        locations: Number(this.companyConfig.locations) || 0,
      };
    },

    // Ad-hoc extras for company tab.
    //   Plan selected (hot/sizzling/scorching/lms_only) → show catalog
    //     MINUS courses already in that plan, so buyers only bolt on what's
    //     missing.
    //   Build Your Own (plan=null) → show the FULL catalog so buyers can
    //     assemble à la carte (same UX as the Individual tab).
    //   Further filtered by `catalogQuery` so buyers can search within the
    //     extras panel too.
    extraCourses() {
      const q = (this.catalogQuery || '').trim().toLowerCase();
      let pool = this.catalog || [];
      if (this.selectedPlan) {
        const ids = this.planIncludedIds[this.selectedPlan.key];
        if (ids === 'all') return [];      // Scorching includes everything
        if (Array.isArray(ids)) {
          const set = new Set(ids);
          pool = pool.filter(c => !set.has(c.id));
        }
      }
      // Honor the same category chip the buyer picked on the individual
      // tab — sharing one selector keeps the page coherent + matches the
      // legacy expectation Josh flagged 2026-05-01 ("category selector
      // in the company pick your courses just like individual").
      // Best Selling is a virtual chip backed by c.is_best_seller.
      if (this.catalogCategory === 'Best Selling') {
        pool = pool.filter((c) => c && c.is_best_seller);
      } else if (this.catalogCategory && this.catalogCategory !== 'All') {
        const allowed = this.matchingCategoriesFor(this.catalogCategory);
        pool = pool.filter((c) => allowed.indexOf(c.category) !== -1);
      }
      if (q) {
        pool = pool.filter(c =>
          (c.name || '').toLowerCase().includes(q) ||
          (c.category || '').toLowerCase().includes(q)
        );
      }
      return pool;
    },
    extraCoursesPaged() {
      return this.groupedExtraCourses.slice(0, this.extrasPage * this.extrasPageSize);
    },
    // Show the extras/BYO panel whenever we're on the company tab. Even if
    // the filtered list is empty, we still render the panel so the search
    // bar stays visible — the empty state tells the buyer no courses match.
    showExtrasPanel() {
      if (this.tab !== 'company') return false;
      if (this.isBuildYourOwn) return true;
      // Stay visible while the buyer is actively filtering — a search or
      // category filter that returns 0 should still show the panel + chips
      // so they can recover, not vanish silently.
      const filtering = (this.catalogQuery || '').length > 0
        || (this.catalogCategory && this.catalogCategory !== 'All');
      return this.extraCourses.length > 0 || filtering;
    },
    isBuildYourOwn() {
      return this.companyConfig.plan === null;
    },
    cartTitle() {
      if (this.tab === 'company') return 'Plan Summary';
      return 'Your Cart';
    },

    // Cart age in days. Returns null when created_at is missing or unreadable;
    // the revival pill stays hidden in that case so we never show a misleading
    // "1 day ago" pill on a brand-new cart.
    cartAgeDays() {
      if (!this.cart || !this.cart.created_at) return null;
      const created = Date.parse(this.cart.created_at);
      if (Number.isNaN(created)) return null;
      const ms = Date.now() - created;
      if (ms <= 0) return 0;
      return Math.floor(ms / 86400000);
    },
    revivalAgeDays() {
      // The pill shows "<N> days ago" — we always lift to at least 1
      // when the threshold check has passed so the copy reads sensibly.
      return Math.max(1, this.cartAgeDays || 0);
    },
    showRevivalPill() {
      if (this.revivalPillDismissed) return false;
      if (this.tab !== 'individual') return false;
      if (!this.cart || !this.cart.items || this.cart.items.length === 0) return false;
      const ageHours = this.cart.created_at
        ? (Date.now() - Date.parse(this.cart.created_at)) / 3600000
        : 0;
      return ageHours >= 24;
    },

    // Slider sync: clamp the value to the slider's own max, compute the
    // fill percentage so we can draw an orange track up to the thumb.
    sliderSeatsValue() {
      return Math.min(Math.max(1, Number(this.companyConfig.total_users) || 1), 500);
    },
    sliderSeatsPct() {
      return ((this.sliderSeatsValue - 1) / (500 - 1)) * 100;
    },
    sliderLocationsValue() {
      return Math.min(Math.max(1, Number(this.companyConfig.locations) || 1), 20);
    },
    sliderLocationsPct() {
      return ((this.sliderLocationsValue - 1) / (20 - 1)) * 100;
    },
    canCheckout() {
      if (!this.cartHasContents) return false;
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(this.checkoutEmail.trim())) return false;
      // Email-uniqueness pre-flight: backend has explicitly told us this email
      // is taken. Soft-fail (emailVerified=false) on a network error doesn't
      // gate — only an explicit reject from /user/valid-email does.
      if (this.emailTaken) return false;
      const first = (this.buyer.first_name || '').trim();
      const last = (this.buyer.last_name || '').trim();
      if (!first || !last) return false;
      // Phone required for both individual + company — receipts, SMS, and
      // sales follow-up all depend on it. Legacy /sign-up always captured it
      // on the individual flow too, so this restores parity.
      // Accept 10-15 digits after stripping formatting — covers US (10),
      // US-with-country-code (11), and international (up to E.164 max 15).
      const phoneDigits = (this.buyer.phone || '').replace(/\D/g, '');
      if (phoneDigits.length < 10 || phoneDigits.length > 15) return false;
      if (this.tab === 'company') {
        if (!(this.buyer.company_name || '').trim()) return false;
      }
      // Password gate: 8+ chars, matches confirm field. Mirrors the backend
      // `password: required|string|min:8` rule on /checkout/intent — same
      // check on both sides so the buyer sees the error before we hit the
      // wire and so the server never accepts a weaker secret.
      const pw = this.buyer.password || '';
      const pwConfirm = this.buyer.password_confirm || '';
      if (pw.length < 8 || pw !== pwConfirm) return false;

      // Payment-method specific validation. Card uses Stripe Elements which
      // validates inside the iframe — we just need a payment_token at submit
      // time. Invoice/ACH need their own field sets filled before we can
      // POST to /company/register. Both tabs run the same gate so an
      // individual buyer picking invoice/ACH gets validated too.
      if (this.paymentType === 'invoice') {
        const inv = this.invoiceFields || {};
        if (!(inv.invoice_name || '').trim()) return false;
        // Format-check the FIRST recipient email (they're comma-separated)
        // — code-review caught buyers typing "Accounts Payable" passing
        // the empty-check before this gate (ECC review 2026-05-03).
        const firstInvEmail = String(inv.invoice_emails || '').split(',')[0].trim();
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(firstInvEmail)) return false;
        if (!(inv.invoice_address || '').trim()) return false;
        if (!(inv.invoice_city || '').trim()) return false;
        if (!(inv.invoice_state || '').trim()) return false;
        if (!(inv.invoice_zip || '').trim()) return false;
      }
      if (this.paymentType === 'ach') {
        const a = this.achFields || {};
        if (!(a.transit_number || '').match(/^\d{9}$/)) return false;
        if (!(a.account_number || '').trim()) return false;
        if (!(a.financial_instituation || '').trim()) return false;
        if (!(a.branch || '').trim()) return false;
        if (!(a.address_1 || '').trim()) return false;
        if (!(a.city || '').trim()) return false;
        if (!(a.state || '').trim()) return false;
        if (!(a.zip || '').trim()) return false;
      }
      return true;
    },
    // Inline phone validation. Empty stays empty (don't pre-shout) — once
    // the buyer types or blurs we report exactly how many digits are
    // missing or whether it's overflowed. `phoneError` is the message;
    // canStartTrial / canCheckout enforce the same digit-count rule.
    phoneError() {
      const digits = (this.buyer.phone || '').replace(/\D/g, '');
      if (digits.length === 0) return '';
      if (digits.length < 10) {
        const need = 10 - digits.length;
        return `Add ${need} more digit${need === 1 ? '' : 's'} (got ${digits.length}/10).`;
      }
      if (digits.length > 15) return 'Phone number is too long.';
      return '';
    },
    canStartTrial() {
      const first = (this.buyer.first_name || '').trim();
      const last = (this.buyer.last_name || '').trim();
      const phone = (this.buyer.phone || '').replace(/\D/g, '');
      // Password gate matches paid checkout. Without it the backend
      // fulfillment has no password to hash, falls back to a random md5
      // the user can't reproduce, and they're locked out after logout.
      const pw = this.buyer.password || '';
      const pwConfirm = this.buyer.password_confirm || '';
      return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((this.checkoutEmail || '').trim())
        && !this.emailTaken
        && !!first && !!last
        && !!(this.buyer.company_name || '').trim()
        && phone.length >= 10
        && pw.length >= 8 && pw === pwConfirm;
    },
    panesStyle() {
      // Equalizing the two tab panes' heights worked when both panes had
      // similar content. After the parity additions made the company tab
      // (license toggle + plans + add-ons + extras) much taller than
      // individual, the equalizer was stretching the For Me pane with
      // dead vertical space below the catalog. Each pane self-sizes now —
      // a short tab swap will visibly change page height, which is the
      // honest behavior anyway.
      return {};
    },
  },

  watch: {
    totalPages(newVal) {
      if (this.catalogPage > newVal) this.catalogPage = Math.max(1, newVal);
    },
    // Force native range inputs to re-render thumb position when the underlying
    // value is changed via the number-input or programmatically. Vue 2's :value
    // binding on <input type="range"> occasionally desyncs the thumb visually.
    'companyConfig.total_users'() {
      this.$nextTick(() => {
        if (this.$refs.seatSlider) {
          this.$refs.seatSlider.value = String(this.sliderSeatsValue);
        }
      });
      this.queueCompanySync();
    },
    'companyConfig.locations'() {
      this.$nextTick(() => {
        if (this.$refs.locSlider) {
          this.$refs.locSlider.value = String(this.sliderLocationsValue);
        }
      });
      this.queueCompanySync();
    },
    'companyConfig.plan'() { this.queueCompanySync(); },
    'companyConfig.billing_cycle'() {
      // Freeze the displayed total at the new client-computed value so
      // the buyer sees ONE change (old → new) instead of the
      // optimistic-then-server double update. The freeze is released
      // when the server sync resolves below in queueCompanySync().
      // Use $nextTick so companyDisplayedTotal has already recomputed
      // against the new billing_cycle when we capture it.
      this.$nextTick(() => {
        // cart.total already includes the plan portion when a plan is
        // selected — don't add companyDisplayedTotal on top here either.
        this.frozenDisplayedTotal = Number(this.cart.total) || 0;
        this.cycleSyncing = true;
      });
      this.queueCompanySync();
    },
    // Lead capture — debounced so typing an email doesn't fire per keystroke.
    // Also clears the emailTaken gate the moment the buyer edits the field —
    // a stale "taken" verdict against a different email shouldn't block
    // canCheckout while the buyer corrects their typo. Real verification
    // re-fires on the next blur.
    checkoutEmail() {
      this.captureLeadDebounced('email');
      this.persistUiState();
      if (this.emailTaken || this.emailError) {
        this.emailTaken = false;
        this.emailError = '';
      }
    },
    'demoForm.email'() { this.captureLeadDebounced('email'); },
    'buyer.first_name'() { this.captureLeadDebounced('buyer'); this.persistUiState(); },
    'buyer.last_name'() { this.captureLeadDebounced('buyer'); this.persistUiState(); },
    'buyer.company_name'() { this.captureLeadDebounced('buyer'); this.persistUiState(); },
    'buyer.phone'() { this.captureLeadDebounced('buyer'); this.persistUiState(); },
    // Flip cart.account_type as soon as the user switches tabs so checkout
    // and fulfillment route through the right path.
    tab(newTab) {
      if (newTab === 'company' || newTab === 'individual') {
        // Swallow any rejection from the fire-and-forget sync — the fire()
        // helper already try/catches internally, but defence in depth.
        Promise.resolve(this.queueCompanySync({ immediate: true })).catch(() => {});
      }
      this.persistUiState();
    },
    // Persist company sliders + plan + billing cycle alongside the cart
    // UUID. Without this a refresh on the company tab loses plan / users /
    // locations / annual cycle and the buyer comes back to a fresh
    // 25-user / sizzling / monthly default — exactly the "company info
    // lost on refresh" symptom Asad caught.
    companyConfig: { handler() { this.persistUiState(); }, deep: true },

    // Add-on selection — persist locally + sync to server cart so the
    // backend's CartService.updateCompanyConfig writes them to tbl_cart
    // and any future fulfillment branch can read them back.
    selectedAddons() {
      this.persistUiState();
      this.queueCompanySync({ immediate: true });
    },

    // Special-courses (per-user counts captured by the prompt modal).
    // Backend already accepts these via cart/update-company.
    specialCourseSelections: {
      handler() {
        this.persistUiState();
        this.queueCompanySync();
      },
      deep: true,
    },

    paymentType() { this.persistUiState(); },
    invoiceFields: { handler() { this.persistUiState(); }, deep: true },
    achFields: { handler() { this.persistUiState(); }, deep: true },

    // License-mode toggle — persist + sync. When the buyer flips to
    // license mode, we DON'T clobber companyConfig.plan; just shift what
    // the server cart is recording so the eventual fulfillment branch
    // knows which path to take.
    licenseMode() {
      this.persistUiState();
      this.queueCompanySync({ immediate: true });
    },
    licenseConfig: {
      handler() {
        this.persistUiState();
        this.queueCompanySync();
      },
      deep: true,
    },

    // Enterprise auto-open — once the threshold trips, open the modal
    // unless the buyer's already dismissed it this session. enterpriseDismissed
    // is intentionally NOT persisted across reloads; a fresh visit gets the
    // re-engagement prompt.
    enterpriseShouldOpen(val) {
      if (val && !this.enterpriseDismissed && !this.enterpriseModalOpen) {
        this.enterpriseModalOpen = true;
      }
    },
  },

  async mounted() {
    this.injectMetaTags();
    this.onResize = () => {
      this.viewportWidth = window.innerWidth;
      this.measureTallestPane();
    };
    window.addEventListener('resize', this.onResize);
    await this.bootstrap();
    await this.ensureCart();
    // Re-apply per-buyer UI state (tab, companyConfig, buyer contact
    // fields) BEFORE handleDeepLink — that way URL query params still win
    // on first load (e.g. ?plan=sizzling), but a plain page refresh
    // doesn't bounce the buyer back to the Individual tab with empty
    // fields. Tracks Asad's "company info lost on refresh" / "trial
    // option no more available after refresh" findings.
    this.restoreUiState();
    // Re-stamp the backend cart with the rendered tab/plan/users/locations.
    // The cart UUID survives across visits, so a buyer who left the page
    // in company-tab + HOT can come back, land on individual tab (default
    // or restored), and have the next addCourse trigger a recalculate
    // against the stale company-mode cart row → wrong total. The tab
    // watcher only fires on changes, so equal-on-restore states slip
    // through. Pushing once after restore makes the backend match what
    // the buyer actually sees before any cart write.
    await Promise.resolve(this.queueCompanySync({ immediate: true })).catch(() => {});
    // Await so the prefill loading overlay has a clean lifecycle and any
    // queued company-sync inside handleDeepLink finishes before reveal.
    await this.handleDeepLink();
    this.$nextTick(() => {
      this.initRevealObserver();
      this.measureTallestPane();
    });
  },

  beforeDestroy() {
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.revealObserver) this.revealObserver.disconnect();
    // Cancel any pending debounced company-cart sync. Without this, a pending
    // fire() closure can resolve after the component unmounts and call
    // applyCart() on a detached instance (Vue warning + stale reactive writes).
    if (this.companyDebounce) {
      clearTimeout(this.companyDebounce);
      this.companyDebounce = null;
    }
  },

  methods: {
    formatPrice(n) {
      // Always render with 2 decimals — matches how Stripe displays and what
      // buyers expect on an invoice. $5 becoming "$5" reads wrong next to
      // "$9.95" on the same screen.
      const num = Number(n);
      if (!Number.isFinite(num)) return '0.00';
      return num.toFixed(2);
    },

    // Phone input mask. Strips non-digits, caps at 15 (E.164 max), then
    // re-renders as (NXX) NXX-XXXX for 10-digit US numbers and
    // "+CC (NXX) NXX-XXXX" for 11-15 digit international entries. The
    // rule lives here instead of a directive so the trial + paid checkout
    // share one formatter — Asad caught the unmasked input accepting any
    // length, which made the disabled-button validation a silent dead-end.
    onPhoneInput(e) {
      const raw = (e && e.target && typeof e.target.value === 'string') ? e.target.value : '';
      const digits = raw.replace(/\D/g, '').slice(0, 15);
      this.buyer.phone = this.formatPhoneDigits(digits);
    },
    formatPhoneDigits(value) {
      const d = String(value || '').replace(/\D/g, '');
      if (!d) return '';
      if (d.length <= 3) return `(${d}`;
      if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
      // 7-10 digits — keep building the US shape "(NXX) NXX-XXXX"
      // through the partial entry. Originally jumped straight from 6 to
      // the 10-digit branch and let lengths 7/8/9 fall to the
      // international else, which formatted "5551234" as "+5551 (555)
      // 123-4". Caught in self-QA on 2026-04-30.
      if (d.length <= 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
      if (d.length === 11) return `+${d.slice(0, 1)} (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
      // 12-15 digits: country code + 10-digit subscriber number.
      const cc = d.slice(0, d.length - 10);
      const rest = d.slice(-10);
      return `+${cc} (${rest.slice(0, 3)}) ${rest.slice(3, 6)}-${rest.slice(6)}`;
    },

    // localStorage helpers for UI state that the server-side cart doesn't
    // own (tab, companyConfig, buyer contact fields). Cart line items +
    // promo + email round-trip through the cart row keyed by
    // hot-pricing-cart-uuid; everything below is per-buyer-only and would
    // otherwise reset on every refresh. Watchers above call persistUiState
    // on each relevant change; restoreUiState runs once after ensureCart
    // and before handleDeepLink so URL params still win on first load.
    persistUiState() {
      // Suspend writes while we're restoring — otherwise the assignments
      // we make in restoreUiState immediately fire watchers and we end up
      // re-saving exactly what we just read.
      if (this._uiPersistSuspended) return;
      try {
        const blob = {
          tab: this.tab,
          companyConfig: { ...this.companyConfig },
          buyer: {
            first_name: this.buyer.first_name || '',
            last_name: this.buyer.last_name || '',
            company_name: this.buyer.company_name || '',
            phone: this.buyer.phone || '',
            // Never persist password or password_confirm. Plaintext
            // credentials in localStorage outlive the buyer's session.
          },
          checkoutEmail: this.checkoutEmail || '',
          phoneTouched: this.phoneTouched,
          // Parity additions — every per-buyer add-on / payment-method /
          // license-mode selection round-trips through the same blob so a
          // refresh doesn't bounce the buyer back to defaults. Never
          // persist ACH account / routing numbers — banking secrets in
          // localStorage outlive the buyer's session, same rule as
          // passwords above. Invoice fields are non-sensitive contact
          // metadata so they're safe to round-trip.
          selectedAddons: Array.isArray(this.selectedAddons) ? this.selectedAddons.slice() : [],
          specialCourseSelections: Array.isArray(this.specialCourseSelections)
            ? this.specialCourseSelections.map((s) => ({ id: s.id, name: s.name, users: s.users }))
            : [],
          // paymentType intentionally NOT persisted (Josh 2026-05-01) —
          // every fresh page load should land on the card path so the
          // default checkout is the lowest-friction one. A buyer who
          // wants invoice/ACH picks it again per session.
          invoiceFields: { ...this.invoiceFields },
          licenseMode: this.licenseMode || 'chooseLMS',
          licenseConfig: this.licenseConfig
            ? {
                users: this.licenseConfig.users || 0,
                locations: this.licenseConfig.locations || 0,
                selectedCourseIds: Array.isArray(this.licenseConfig.selectedCourseIds)
                  ? this.licenseConfig.selectedCourseIds.slice()
                  : [],
              }
            : null,
          savedAt: Date.now(),
        };
        localStorage.setItem(LS_UI_KEY, JSON.stringify(blob));
      } catch (_) { /* private mode / quota — silent */ }
    },

    restoreUiState() {
      let blob;
      try {
        const raw = localStorage.getItem(LS_UI_KEY);
        if (!raw) return;
        blob = JSON.parse(raw);
      } catch (_) {
        // Bad JSON — clear so we don't keep failing.
        try { localStorage.removeItem(LS_UI_KEY); } catch { /* ignore */ }
        return;
      }
      if (!blob || typeof blob !== 'object') return;
      // Drop anything older than the TTL so stale tabs/plans don't haunt
      // a buyer who comes back weeks later.
      if (typeof blob.savedAt !== 'number' || (Date.now() - blob.savedAt) > LS_UI_TTL_MS) {
        try { localStorage.removeItem(LS_UI_KEY); } catch { /* ignore */ }
        return;
      }

      this._uiPersistSuspended = true;
      try {
        if (blob.tab === 'company' || blob.tab === 'individual') {
          this.tab = blob.tab;
        }
        if (blob.companyConfig && typeof blob.companyConfig === 'object') {
          const cc = blob.companyConfig;
          if (['hot', 'sizzling', 'scorching', 'lms_only'].includes(cc.plan) || cc.plan === null) {
            this.companyConfig.plan = cc.plan;
          }
          if (cc.billing_cycle === 'annual' || cc.billing_cycle === 'monthly') {
            this.companyConfig.billing_cycle = cc.billing_cycle;
          }
          const users = parseInt(cc.total_users, 10);
          if (Number.isFinite(users) && users > 0) {
            this.companyConfig.total_users = Math.min(100000, users);
          }
          const locs = parseInt(cc.locations, 10);
          if (Number.isFinite(locs) && locs > 0) {
            this.companyConfig.locations = Math.min(10000, locs);
          }
        }
        if (blob.buyer && typeof blob.buyer === 'object') {
          if (typeof blob.buyer.first_name === 'string') this.buyer.first_name = blob.buyer.first_name;
          if (typeof blob.buyer.last_name === 'string') this.buyer.last_name = blob.buyer.last_name;
          if (typeof blob.buyer.company_name === 'string') this.buyer.company_name = blob.buyer.company_name;
          if (typeof blob.buyer.phone === 'string') {
            // Re-format on restore so older saves that captured raw digits
            // pre-mask (or pre-2026-04-30 unmasked entries) come back
            // formatted. formatPhoneDigits is idempotent.
            this.buyer.phone = this.formatPhoneDigits(blob.buyer.phone);
          }
        }
        // Only restore checkoutEmail when the cart didn't already populate
        // it. Cart-side email (server) is canonical when present so a
        // returning buyer who finished /checkout/intent and is just
        // refreshing /pricing sees their up-to-date email, not a stale
        // typo from a prior visit. Also gate on basic email format —
        // older blobs occasionally captured a partial typo (". Alos",
        // "j", etc.) that would otherwise refill the field with junk on
        // next visit. Anything that doesn't match the email regex is
        // dropped silently.
        if (typeof blob.checkoutEmail === 'string'
            && !this.checkoutEmail
            && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(blob.checkoutEmail.trim())) {
          this.checkoutEmail = blob.checkoutEmail;
        }
        if (typeof blob.phoneTouched === 'boolean') {
          this.phoneTouched = blob.phoneTouched;
        }

        // Parity restore — same shape we wrote in persistUiState. Defensive
        // type checks so a hand-edited / older blob can't crash the page.
        if (Array.isArray(blob.selectedAddons)) {
          this.selectedAddons = blob.selectedAddons.filter((k) => typeof k === 'string');
        }
        if (Array.isArray(blob.specialCourseSelections)) {
          this.specialCourseSelections = blob.specialCourseSelections
            .filter((s) => s && Number.isFinite(Number(s.id)))
            .map((s) => ({
              id: Number(s.id),
              name: String(s.name || ''),
              users: Math.max(1, Number(s.users) || 1),
            }));
        }
        // paymentType intentionally not restored — see persistUiState.
        // Existing localStorage rows from before this change may still
        // hold a stored paymentType; we ignore it so buyers default
        // back to 'card' every load.
        if (blob.invoiceFields && typeof blob.invoiceFields === 'object') {
          this.invoiceFields = {
            invoice_name: String(blob.invoiceFields.invoice_name || ''),
            invoice_emails: String(blob.invoiceFields.invoice_emails || ''),
            invoice_address: String(blob.invoiceFields.invoice_address || ''),
            invoice_city: String(blob.invoiceFields.invoice_city || ''),
            invoice_state: String(blob.invoiceFields.invoice_state || ''),
          };
        }
        if (blob.licenseMode === 'chooseLMS' || blob.licenseMode === 'chooseOtherPlatform') {
          this.licenseMode = blob.licenseMode;
        }
        if (blob.licenseConfig && typeof blob.licenseConfig === 'object') {
          this.licenseConfig = {
            users: Math.max(1, Number(blob.licenseConfig.users) || 1),
            locations: Math.max(1, Number(blob.licenseConfig.locations) || 1),
            selectedCourseIds: Array.isArray(blob.licenseConfig.selectedCourseIds)
              ? blob.licenseConfig.selectedCourseIds.filter((id) => Number.isFinite(Number(id))).map(Number)
              : [],
          };
        }
      } finally {
        // Allow watchers to write again. We re-fire persistUiState() once
        // so any normalization (e.g. phone re-formatting) gets re-saved.
        this.$nextTick(() => {
          this._uiPersistSuspended = false;
          this.persistUiState();
        });
      }
    },

    clearUiState() {
      try { localStorage.removeItem(LS_UI_KEY); } catch { /* ignore */ }
      try { localStorage.removeItem('hot-pricing-cart-uuid'); } catch { /* ignore */ }
    },

    // Open-redirect guard: accept only relative routes (leading "/" but not "//"
    // which is protocol-relative) or absolute URLs on our own origin. Rejects
    // javascript:, data:, and cross-origin hrefs. Returns "" when the input is
    // unsafe so the caller can render it without executing anything.
    safeRedirect(url) {
      if (typeof url !== 'string') return '';
      const trimmed = url.trim();
      if (trimmed === '' || trimmed.startsWith('//')) return '';
      if (trimmed.startsWith('/')) return trimmed;
      try {
        const parsed = new URL(trimmed, window.location.origin);
        if (parsed.origin !== window.location.origin) return '';
        return parsed.pathname + parsed.search + parsed.hash;
      } catch {
        return '';
      }
    },

    // Email-uniqueness pre-flight. Hits the same /user/valid-email endpoint
    // legacy /sign-up uses on blur (CompanyBillingInfo.vue:685-716). Sets
    // emailTaken=true on an explicit reject so canCheckout / canStartTrial
    // gate before the buyer wastes time filling the rest of the form. Network
    // / 5xx leaves emailTaken=false so flaky wifi doesn't lock anyone out —
    // the checkout endpoint will surface the real error if a dupe slips
    // through. Idempotent so repeated blurs collapse cheaply.
    async checkEmail() {
      const email = (this.checkoutEmail || '').trim();
      if (!email) {
        this.emailError = '';
        this.emailTaken = false;
        return;
      }
      // Match canCheckout's format gate so we don't fire a network call on
      // every keystroke that's still mid-typing.
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        this.emailError = 'Please enter a valid email address.';
        this.emailTaken = false;
        return;
      }
      this.emailCheckLoading = true;
      try {
        const { data } = await axios.post(`${API_BASE}/user/valid-email`, { email });
        // Backend convention: { status: true } when the email is FREE to use,
        // { status: false, message: "..." } when it's taken. Mirrors what
        // CompanyBillingInfo.vue reads from the same endpoint.
        if (data && data.status) {
          this.emailError = '';
          this.emailTaken = false;
        } else {
          this.emailError = (data && data.message) || 'That email is already in use.';
          this.emailTaken = true;
        }
      } catch (e) {
        // Network / 5xx — soft fail. Don't gate the buyer; checkout-side
        // validation will catch a real dupe. Wipe any stale taken-state.
        this.emailError = '';
        this.emailTaken = false;
      } finally {
        this.emailCheckLoading = false;
      }
    },

    // Lead capture — mirror the legacy /user/lead upsert the old pricing pages
    // use (NewSignupPage, UserRegister, Register, etc). Writes to tbl_leads so
    // SendDebouncedLeadEmails picks them up for the sales@homeoftraining.com
    // drip and LeadController?filter=abandoned sees them. Fire-and-forget —
    // never blocks the UI.
    captureLead(stage /* 'email' | 'plan' | 'promo' | 'checkout_open' | 'trial' | 'demo' */) {
      try {
        // Need at least an email to be a meaningful lead row. Skip before.
        const email = (this.checkoutEmail || this.demoForm.email || this.cart.email || '').trim();
        if (!email) return;

        const user_type = this.tab === 'individual' ? 'individual' : 'corporate';
        const inLicense = this.tab === 'company' && this.licenseMode === 'chooseOtherPlatform';
        const courseIds = inLicense
          ? ((this.licenseConfig.selectedCourseIds || []).slice())
          : (this.cart.items || []).map(i => i.course_id).filter(Boolean);
        const usersForLead = inLicense
          ? Math.max(1, Number(this.licenseConfig.users) || 1)
          : (user_type === 'corporate' ? Number(this.companyConfig.total_users) || 1 : 1);
        const locationsForLead = inLicense
          ? Math.max(1, Number(this.licenseConfig.locations) || 1)
          : (user_type === 'corporate' ? Number(this.companyConfig.locations) || 1 : 1);
        const specialCoursesForLead = inLicense
          ? ((this.licenseConfig.selectedCourseIds || []).map((id) => ({
              id,
              users: usersForLead,
            })))
          : (Array.isArray(this.specialCourseSelections) ? this.specialCourseSelections : []);
        const payload = {
          session_id: this.sessionUuid || this.cart.session_uuid || null,
          source: 'pricing_v3',
          email,
          first_name: this.buyer.first_name || this.demoForm.first_name || '',
          last_name: this.buyer.last_name || this.demoForm.last_name || '',
          company_name: this.buyer.company_name || this.demoForm.company_name || '',
          phone_num: this.buyer.phone || '',
          number_of_employees: usersForLead,
          // Mirror legacy NewSignupPage contract — individuals send 1, not 0.
          // Keeps per_location / per_employee analytics comparable across flows
          // and avoids legacy-endpoint assumptions about non-zero denominators.
          number_of_locations: locationsForLead,
          user_type,
          plan: user_type === 'corporate' ? (this.companyConfig.plan || null) : null,
          course_ids: courseIds,
          // Selected add-on keys round-trip into the lead row so
          // sales+SendDebouncedLeadEmails can see what the buyer was
          // considering even if they bounced before checkout. Backend
          // already json_encodes addons_ids on tbl_leads (UserController:2012).
          addons_ids: Array.isArray(this.selectedAddons) ? this.selectedAddons : [],
          special_courses: specialCoursesForLead,
          // Payment method the buyer selected at the time of capture (card /
          // invoice / ach). Surfaces in the lead row + lead email so AR /
          // sales know whether the lead is going to need an invoice path.
          // Picker is now visible on both tabs so use this.paymentType
          // directly — was previously gated on company tab only (Josh
          // 2026-05-01).
          payment_method: this.paymentType || 'card',
          // For invoice leads, surface the agreed total INCLUDING the 10%
          // surcharge so AR sees the right number on the lead email
          // (code-review fix 2026-05-01). Card + ACH leads send the same
          // displayedTotal which already excludes surcharge.
          agreed_total: Number(this.displayedTotal) || 0,
          // Mode flag for license-our-content buyers so sales can route
          // those leads to the licensing team instead of standard onboarding.
          choose_platform: inLicense ? 'chooseOtherPlatform' : 'chooseLMS',
          promo_code: this.cart.promo_code || this.promoCodeInput || '',
          course_cost: Number(this.cart.subtotal) || 0,
          sub_total: Number(this.cart.subtotal) || 0,
          discount_value: Number(this.cart.discount) || 0,
          discounted_cost: Number(this.cart.total) || 0,
          cost_per_month: Number(this.cart.total) || 0,
          total_cost_per_year: user_type === 'corporate' && this.companyConfig.billing_cycle === 'annual'
            ? Number(this.cart.total) || 0
            : (Number(this.cart.total) || 0) * 12,
        };

        axios.post(`${API_BASE}/user/lead`, payload).catch(() => {
          /* swallow — lead capture must never break the checkout flow */
        });
      } catch (_) { /* same — never throw from capture */ }
    },

    // Debounced wrapper so rapid field changes (email typing, slider drag)
    // coalesce into one POST ~1s after the user stops fiddling.
    captureLeadDebounced(stage) {
      if (this._leadCaptureTimer) clearTimeout(this._leadCaptureTimer);
      this._leadCaptureTimer = setTimeout(() => this.captureLead(stage), 1200);
    },

    isInCart(courseId) {
      return !!(this.cart.items && this.cart.items.find(i => i.course_id === courseId));
    },

    /**
     * Returns the count of variants in a group that are already in cart.
     * Used by grouped course cards to show "2 / 4 selected" feedback.
     */
    variantsInCart(group) {
      if (!group || !group.isGroup || !Array.isArray(group.variants)) return 0;
      const ids = new Set((this.cart.items || []).map((i) => i.course_id));
      return group.variants.reduce((n, v) => n + (ids.has(v.id) ? 1 : 0), 0);
    },

    variantLabel(course) { return variantLabelUtil(course); },

    /**
     * Click handler for grouped course cards. Opens VariantPickerModal
     * with the group's variants. Solo courses route through addCourse
     * directly (no picker needed).
     */
    onCourseCtaClick(entry) {
      if (entry && entry.isGroup) {
        this.openVariantPicker(entry);
      } else if (entry) {
        // Solo entry — call addCourse with the entry's id (it's a passed-
        // through course, so id is the real course id).
        if (this.isInCart(entry.id)) this.removeCourse(entry.id);
        else this.addCourse(entry.id);
      }
    },

    openVariantPicker(group) {
      this.variantPickerGroup = group;
      this.variantPickerOpen = true;
    },

    closeVariantPicker() {
      this.variantPickerOpen = false;
      this.variantPickerGroup = null;
    },

    /**
     * Buyer picked a variant in the picker modal. Routes to addCourse /
     * removeCourse exactly like a normal course click would. Modal stays
     * open so the buyer can pick multiple variants in one session
     * (common: a multi-state employer adding both Florida + Nationwide).
     */
    onVariantPicked(variantId) {
      if (this.isInCart(variantId)) {
        this.removeCourse(variantId);
      } else {
        this.addCourse(variantId);
      }
    },

    /**
     * Resolve a chip name to the set of catalog categories it filters in.
     * Currently 1:1 — backend categoryForCourse() owns the Compliance
     * taxonomy directly (food handler / food manager / alcohol safety /
     * seller server / harassment / human trafficking are all Compliance).
     * Kept as a method so future overlapping-chip logic can layer in
     * without touching every call site.
     */
    // Course-name stem + variant grouping live in src/utils/courseGrouping.js
    // so LicensePane.vue uses the exact same logic.
    nameStem(name) { return nameStemUtil(name); },
    groupCourses(list) { return groupCoursesUtil(list); },

    matchingCategoriesFor(chipKey) {
      return [chipKey];
    },

    /**
     * True when a cart item is one of the plan's bundled courses on the
     * company tab. Removing it from the cart wouldn't actually save the
     * buyer money — the plan still bills them for it — so we disable the
     * remove button. Mirrors legacy /sign-up's PaymentsDetails.vue:43-86
     * disabled-prop pattern. Returns false on the individual tab and on
     * Build Your Own (no plan, nothing locked).
     */
    isPlanLocked(courseId) {
      if (this.tab !== 'company') return false;
      if (!this.selectedPlan) return false;
      const ids = this.planIncludedIds[this.selectedPlan.key];
      if (ids === 'all') return true;            // Scorching includes everything
      if (!Array.isArray(ids)) return false;
      return ids.indexOf(courseId) !== -1;
    },

    // Plan-card helpers — look up what's included in each plan so buyers see
    // real course names, not marketing fluff.
    priorityRank(category) {
      // Compliance first (Eric): food/alcohol → harassment/human trafficking → safety → mgmt → rest
      const order = { 'Food & Alcohol': 1, 'Compliance': 2, 'Safety': 3, 'Management': 4, 'General': 5 };
      return order[category] ?? 99;
    },
    sortByPriority(list) {
      return [...list].sort((a, b) => {
        const r = this.priorityRank(a.category) - this.priorityRank(b.category);
        return r !== 0 ? r : String(a.name || '').localeCompare(String(b.name || ''));
      });
    },
    includedCoursesFor(planKey) {
      const ids = this.planIncludedIds[planKey];
      let list;
      if (ids === 'all') list = this.catalog || [];
      else if (!Array.isArray(ids)) return [];
      else {
        const set = new Set(ids);
        list = (this.catalog || []).filter(c => set.has(c.id));
      }
      return this.sortByPriority(list);
    },
    includedCountLabel(planKey) {
      const ids = this.planIncludedIds[planKey];
      if (ids === 'all') return `${(this.catalog || []).length || '250+'} courses`;
      if (!Array.isArray(ids)) return '';
      return `${ids.length} course${ids.length === 1 ? '' : 's'}`;
    },
    previewIncluded(planKey) {
      return this.includedCoursesFor(planKey).slice(0, 3);
    },
    remainingIncluded(planKey) {
      return Math.max(0, this.includedCoursesFor(planKey).length - 3);
    },

    onUsersInput(e) {
      const v = parseInt(e.target.value, 10);
      this.companyConfig.total_users = Number.isFinite(v) && v > 0 ? v : 1;
    },
    onLocationsInput(e) {
      const v = parseInt(e.target.value, 10);
      this.companyConfig.locations = Number.isFinite(v) && v > 0 ? v : 1;
    },
    onSeatSliderInput(e) {
      const v = parseInt(e.target.value, 10);
      this.companyConfig.total_users = Number.isFinite(v) && v > 0 ? v : 1;
    },
    onLocSliderInput(e) {
      const v = parseInt(e.target.value, 10);
      this.companyConfig.locations = Number.isFinite(v) && v > 0 ? v : 1;
    },

    async bootstrap() {
      try {
        const { data } = await axios.get(`${API_BASE}/new-checkout/pricing-bootstrap`);
        this.catalog = data.catalog || [];
        this.plans = data.plans || [];
        // Default-chip fallback: demote to 'All' if the catalog
        // doesn't have what the chip filters on, so the buyer
        // doesn't land on an empty grid.
        if (this.catalogCategory === 'Best Selling'
            && !(this.catalog || []).some((c) => c && c.is_best_seller)) {
          this.catalogCategory = 'All';
        }
        if (this.catalogCategory === 'Compliance'
            && !(this.catalog || []).some((c) => c && c.category === 'Compliance')) {
          this.catalogCategory = 'All';
        }
        this.addons = data.addons || [];
        this.stats = data.stats || {};
        // Build planKey -> included course IDs map
        const map = {};
        (data.plans || []).forEach(p => {
          if (p && p.key) map[p.key] = p.included_course_ids;
        });
        this.planIncludedIds = map;
      } catch (e) {
        console.error('Pricing bootstrap failed', e);
        this.cartError = 'Could not load courses — please refresh the page.';
      }
    },

    async ensureCart() {
      const stored = localStorage.getItem('hot-pricing-cart-uuid');
      if (stored) {
        try {
          const { data } = await axios.get(`${API_BASE}/new-checkout/cart`, {
            params: { session_uuid: stored },
          });
          this.applyCart(data.cart);
          this.cartInitialized = true;
          return;
        } catch (e) { /* fall through to create a new cart */ }
      }
      try {
        const { data } = await axios.post(`${API_BASE}/new-checkout/cart`, {
          utm_source: this.$route.query.utm_source,
          utm_medium: this.$route.query.utm_medium,
          utm_campaign: this.$route.query.utm_campaign,
        });
        this.applyCart(data.cart);
        if (this.sessionUuid) localStorage.setItem('hot-pricing-cart-uuid', this.sessionUuid);
        this.cartInitialized = true;
      } catch (e) {
        console.error('Cart init failed', e);
        // Surface a user-visible banner instead of a silently broken page.
        this.cartError = 'Connection issue — could not start your cart. Please refresh to retry.';
      }
    },

    applyCart(c) {
      this.cart = c;
      this.sessionUuid = c.session_uuid;
      if (c.email && !this.checkoutEmail) this.checkoutEmail = c.email;
    },

    async addCourse(courseId) {
      if (this.busyCourseId) return;
      // Special-course pricing — when a company-tab buyer adds a course
      // flagged is_discounted_course===0, legacy /sign-up prompts for the
      // per-course user count (different from the team plan size). Without
      // it, the buyer's quoted total drifts from what legacy would have
      // priced. Catalog source-of-truth lives in `this.catalog` from
      // pricing-bootstrap. Individual tab + license mode skip the prompt
      // (license mode uses its own per-user count via licenseConfig).
      if (this.tab === 'company' && this.licenseMode === 'chooseLMS') {
        const course = (this.catalog || []).find((c) => c && c.id === courseId);
        if (course && course.is_discounted_course === 0) {
          // Already prompted for this course — don't re-open the modal,
          // just add it through to the cart with the existing user count.
          const already = (this.specialCourseSelections || []).find((s) => s.id === courseId);
          if (!already) {
            this.specialCoursePending = course;
            this.specialCourseModalOpen = true;
            return;
          }
        }
      }
      if (!this.sessionUuid) {
        // Cart never finished initializing — retry before abandoning the click.
        await this.ensureCart();
        if (!this.sessionUuid) {
          this.cartError = 'Cart not ready. Please refresh to try again.';
          return;
        }
      }
      this.busyCourseId = courseId;
      try {
        const { data } = await axios.post(`${API_BASE}/new-checkout/cart/add-course`, {
          session_uuid: this.sessionUuid,
          course_id: courseId,
        });
        this.applyCart(data.cart);
        this.cartError = '';
      } catch (e) {
        console.error('addCourse failed', e);
        this.cartError = e.response?.data?.message || 'Could not add that course. Try again?';
      } finally {
        this.busyCourseId = null;
      }
    },

    /**
     * Buyer confirmed the per-user count for a special course. Append to
     * specialCourseSelections (the deep watcher fires queueCompanySync so
     * the backend gets the updated array), then proceed with the standard
     * cart add. Keeping the cart row + selections array in sync means the
     * cart-side display still shows the course as a normal line item, but
     * the eventual checkout payload also carries the per-course user count.
     */
    async onSpecialCourseConfirm({ course, users }) {
      if (!course) return;
      this.specialCourseModalOpen = false;
      const idx = (this.specialCourseSelections || []).findIndex((s) => s.id === course.id);
      const entry = { id: course.id, name: course.name, users };
      if (idx === -1) this.specialCourseSelections.push(entry);
      else this.specialCourseSelections.splice(idx, 1, entry);
      // Continue with the normal add — addCourse re-checks and bypasses
      // the prompt because we just added the course to specialCourseSelections.
      this.specialCoursePending = null;
      await this.addCourse(course.id);
    },

    onSpecialCourseClose() {
      this.specialCourseModalOpen = false;
      this.specialCoursePending = null;
    },

    async removeCourse(courseId) {
      if (!this.sessionUuid) return;
      // Plan-locked guard — the cart-remove button is also disabled in
      // template, but defence-in-depth catches programmatic removes that
      // skip the UI (e.g. from clearRevivedCart on a course that's now
      // bundled into the buyer's plan).
      if (this.isPlanLocked && this.isPlanLocked(courseId)) return;
      this.busyCourseId = courseId;
      try {
        const { data } = await axios.post(`${API_BASE}/new-checkout/cart/remove-course`, {
          session_uuid: this.sessionUuid,
          course_id: courseId,
        });
        this.applyCart(data.cart);
        this.cartError = '';
        // Strip any per-user count we'd captured for this course so the
        // next checkout payload doesn't carry stale special-course state.
        if (Array.isArray(this.specialCourseSelections)) {
          const idx = this.specialCourseSelections.findIndex((s) => s.id === courseId);
          if (idx !== -1) this.specialCourseSelections.splice(idx, 1);
        }
      } catch (e) {
        console.error('removeCourse failed', e);
        this.cartError = e.response?.data?.message || 'Could not remove that course.';
      } finally {
        this.busyCourseId = null;
      }
    },

    /**
     * Clear every course from a revived cart in one click. Iterates the
     * current items and fires removeCourse sequentially so the cartError
     * messaging on individual failures stays honest. Hides the pill at the
     * end via the (now empty) `cart.items` length check inside showRevivalPill.
     */
    async clearRevivedCart() {
      if (!this.cart || !this.cart.items || this.cart.items.length === 0) return;
      const ids = this.cart.items.map((i) => i.course_id);
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        await this.removeCourse(id);
      }
      this.revivalPillDismissed = true;
    },

    dismissRevivalPill() {
      this.revivalPillDismissed = true;
    },

    /**
     * Primary "Get started" CTA. Scrolls to the catalog if the cart is
     * empty so buyers see the courses; if the cart already has contents,
     * jumps straight to the simple individual checkout modal.
     */
    startIndividualSignup() {
      this.tab = 'individual';
      if (this.cartHasContents) {
        this.openCheckoutModal();
        return;
      }
      this.$nextTick(() => {
        const grid = document.querySelector('.ph-courses-grid')
          || document.querySelector('.ph-catalog-controls');
        if (grid && typeof grid.scrollIntoView === 'function') {
          grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },

    /**
     * Open the company-signup wizard. Optional opts.step / opts.plan let
     * deep links pre-position the wizard on Step 2 with a plan selected.
     */
    openCompanyWizard(opts = {}) {
      this.companyWizardInitialStep = opts.step || 1;
      this.companyWizardInitialPlan = opts.plan || null;
      this.companyWizardOpen = true;
      this.captureLead('wizard_open');
    },

    /**
     * Wizard step-change breadcrumb. captureLead so sales can see how far
     * a wizard buyer got even if they abandoned mid-flow.
     */
    onWizardStepChanged(step) {
      this.captureLead(`wizard_step_${step}`);
    },

    /**
     * Wizard emitted `paid` after /checkout/intent succeeded (or 3DS
     * resolved). Mirror PricingPage's onStripeToken success branch:
     * redirect into CheckoutSuccess which fires /checkout/complete.
     */
    onWizardPaid({ intent_id }) {
      if (!intent_id) return;
      this.companyWizardOpen = false;
      // Mirror onStripeToken — drop the anonymous cart UUID + UI blob so
      // a post-purchase visit to /pricing starts fresh instead of
      // resurrecting the cart we just charged for.
      this.clearUiState();
      window.location.assign(
        '/?_=' + Date.now() + '#/checkout/success?payment_intent=' + encodeURIComponent(intent_id)
      );
    },

    /**
     * Wizard emitted `trialed` from the free-trial alt CTA. Mirror the
     * pricing-modal path: only redirect when the server gave us a token
     * (new-email branch). For an existing-email response there's no
     * session, so a /dashboard redirect would just bounce to /login —
     * surface a friendly "check your email" instead.
     */
    onWizardTrialed(data) {
      if (data && data.token) {
        this.storeAuth(data);
        this.companyWizardOpen = false;
        // Account provisioned — clear anonymous cart + UI blob the same
        // way the modal trial branch does.
        this.clearUiState();
        setTimeout(() => {
          window.location.assign('/?_=' + Date.now() + '#/dashboard');
        }, 800);
        return;
      }
      // Existing-email branch — keep the wizard open and surface the result
      // in the same modal trial-success panel by writing through to the
      // parent's trialSignupResult. The wizard's step-3 inline message
      // also handles this, so closing the wizard is optional. Default
      // here is to close + show the page-level success state.
      // Server omits `email` for the no-token branch (no enumeration), so
      // we backfill from checkoutEmail / cart.email so the copy renders.
      const fallbackEmail = (this.checkoutEmail || '').trim() || (this.cart && this.cart.email) || '';
      this.trialSignupResult = { ...(data || {}), email: (data && data.email) || fallbackEmail };
      this.companyWizardOpen = false;
    },

    selectPlan(planKey) {
      this.companyConfig.plan = planKey;
      this.$nextTick(() => {
        const el = document.querySelector('.ph-config-strip');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },

    /**
     * Push the current companyConfig (plan/users/locations/billing) to the
     * server cart. Debounced so slider drags don't hammer the endpoint.
     *
     * Also flips cart.account_type based on the active tab so that checkout
     * and webhook fulfillment route through the right path.
     */
    queueCompanySync({ immediate = false } = {}) {
      // Defensive: watchers can fire during component creation before
      // ensureCart() has completed. Without cartInitialized, a `tab` or
      // watcher-triggered sync could POST with a null session_uuid.
      if (!this.sessionUuid || !this.cartInitialized) return;
      if (this.companyDebounce) clearTimeout(this.companyDebounce);
      const fire = async () => {
        try {
          // License-mode buyers don't pick a plan — they use the
          // licenseConfig sliders for users/locations and the catalog ids
          // for what they're embedding. Cart endpoint stays the same; we
          // just swap which numbers we send so the row reflects the
          // active mode.
          const inLicense = this.tab === 'company' && this.licenseMode === 'chooseOtherPlatform';
          const totalUsers = inLicense
            ? Math.max(1, Number(this.licenseConfig.users) || 1)
            : this.companyConfig.total_users;
          const locations = inLicense
            ? Math.max(1, Number(this.licenseConfig.locations) || 1)
            : this.companyConfig.locations;
          // For license mode we plumb the selected course ids through the
          // existing special_courses array (backend already accepts it),
          // tagging each with the same `users` count so the per-course
          // billing math matches what the LicensePane preview showed.
          const specialCourses = inLicense
            ? ((this.licenseConfig.selectedCourseIds || []).map((id) => ({
                id,
                users: totalUsers,
              })))
            : (this.specialCourseSelections || []).map((s) => ({
                id: s.id,
                name: s.name,
                users: s.users,
              }));
          const { data } = await axios.post(`${API_BASE}/new-checkout/cart/update-company`, {
            session_uuid: this.sessionUuid,
            account_type: this.tab === 'company' ? 'company' : 'individual',
            // License-mode rows park on lms_only as the "no real plan"
            // sentinel — the backend's validate rule only accepts the
            // four canonical plan keys, and inferring intent off addons
            // alone is fragile. Fulfillment branch keys off the cart's
            // special_courses + addons arrays instead.
            plan: inLicense ? 'lms_only' : this.companyConfig.plan,
            billing_cycle: this.companyConfig.billing_cycle,
            total_users: totalUsers,
            locations,
            addons: Array.isArray(this.selectedAddons) ? this.selectedAddons : [],
            special_courses: specialCourses,
          });
          if (data && data.cart) this.applyCart(data.cart);
          this.captureLead('plan');
        } catch (e) { /* non-fatal: client-side total still renders */ }
        finally {
          // Release the price freeze. With the cart now up-to-date from
          // the server, the live computed takes over and resolves to the
          // final value. If client and server agree (the common case),
          // the buyer sees zero net change at this point.
          this.cycleSyncing = false;
          this.frozenDisplayedTotal = null;
        }
      };
      if (immediate) return fire();
      this.companyDebounce = setTimeout(fire, 450);
    },

    setShowFoodPlan(yes) {
      this.showFoodPlan = !!yes;
      // If they hid food and HOT was selected, fall back to the popular tier
      if (!this.showFoodPlan && this.companyConfig.plan === 'hot') {
        this.companyConfig.plan = 'sizzling';
      }
    },

    selectBuildYourOwn() {
      // Custom build: no base plan — buyer assembles their own from extras.
      // Backend's PriceCalculateController already handles plan=null for company.
      this.companyConfig.plan = null;
      this.$nextTick(() => {
        const el = document.querySelector('.ph-extras-panel') || document.querySelector('.ph-config-strip');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },

    selectAltOption(key) {
      if (key === 'build') return this.selectBuildYourOwn();
      if (key === 'lms_only') {
        this.companyConfig.plan = 'lms_only';
        this.$nextTick(() => {
          const el = document.querySelector('.ph-config-strip');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    },

    setBillingCycle(cycle) {
      this.companyConfig.billing_cycle = cycle;
    },

    async applyPromo() {
      const code = (this.promoCodeInput || '').trim();
      if (!code) return;
      this.promoMessage = '';
      this.promoError = false;
      try {
        const { data } = await axios.post(`${API_BASE}/new-checkout/cart/apply-promo`, {
          session_uuid: this.sessionUuid,
          code,
        });
        this.applyCart(data.cart);
        if (data.applied) {
          this.promoMessage = `Code "${code}" applied.`;
          this.promoCodeInput = '';
          this.captureLead('promo');
        } else {
          this.promoMessage = 'That code is invalid or expired.';
          this.promoError = true;
        }
      } catch (e) {
        this.promoMessage = 'Could not apply code.';
        this.promoError = true;
      }
    },

    async clearPromo() {
      try {
        const { data } = await axios.post(`${API_BASE}/new-checkout/cart/apply-promo`, {
          session_uuid: this.sessionUuid,
          code: null,
        });
        this.applyCart(data.cart);
        this.promoMessage = '';
        this.promoError = false;
      } catch (e) {
        // Surface to the user instead of silently leaving the discount row rendered.
        this.promoMessage = e.response?.data?.message || 'Could not remove the promo code. Please try again.';
        this.promoError = true;
      }
    },

    openCheckoutModal() {
      // Remember what triggered the modal so we can restore focus on close —
      // keyboard users end up back where they came from instead of at body top.
      this.lastFocused = document.activeElement;
      this.checkoutModalOpen = true;
      // Capture as a lead the moment they open the modal — even if they have
      // an email but bail before clicking Pay.
      this.captureLead('checkout_open');
      this.$nextTick(() => {
        const modal = this.$refs.checkoutModal;
        if (!modal) return;
        const input = modal.querySelector('input[type="email"]');
        (input || modal).focus();
      });
    },

    closeCheckoutModal() {
      this.checkoutModalOpen = false;
      // Reset per-attempt state so reopening the modal doesn't show a stale
      // decline from a previous attempt. Keep the filled name/email/phone so
      // the buyer doesn't have to retype them if they closed to check
      // something — but scrub password fields. Holding plaintext password
      // strings in Vue reactive state longer than necessary is easy to get
      // right by default: clear them on every modal close.
      this.paymentError = '';
      this.checkoutError = '';
      this.checkoutLoading = false;
      this.buyer.password = '';
      this.buyer.password_confirm = '';
      this.restoreFocus();
    },

    openIncludesModal(planKey) {
      this.lastFocused = document.activeElement;
      this.includesModal = { open: true, planKey };
      this.$nextTick(() => {
        const modal = this.$refs.includesModal;
        if (modal) modal.focus();
      });
    },
    closeIncludesModal() {
      this.includesModal = { open: false, planKey: null };
      this.restoreFocus();
    },

    restoreFocus() {
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        try { this.lastFocused.focus(); } catch { /* detached node */ }
      }
      this.lastFocused = null;
    },

    // Keep Tab focus inside the open modal — prevents keyboard users from
    // landing on hidden page content behind the backdrop.
    trapFocus(event, refName) {
      const root = this.$refs[refName];
      if (!root) return;
      const focusables = Array.from(root.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },

    /**
     * "Use a different email" button on the no-token success panel.
     * Clears the success state so the form (still mounted underneath the
     * panel) becomes re-submittable and focuses the email field. Tracks
     * Asad's "I mistakenly added wrong email in the trial, no resend
     * option" finding — the buyer can now correct the typo without
     * digging through their email or refreshing the page.
     */
    resetTrialForRetry() {
      this.trialSignupResult = null;
      this.trialSignupError = '';
      this.trialSignupLoading = false;
      this.$nextTick(() => {
        const modal = this.$refs.checkoutModal;
        if (!modal) return;
        const input = modal.querySelector('input[type="email"]');
        if (input) {
          input.focus();
          // Highlight the existing email so a retype overwrites it in
          // one keystroke instead of forcing the buyer to clear by hand.
          if (typeof input.select === 'function') input.select();
        }
      });
    },

    async startFreeTrial() {
      if (!this.canStartTrial || this.trialSignupLoading) return;
      this.trialSignupLoading = true;
      this.trialSignupError = '';
      this.trialSignupResult = null;
      // Capture as a lead BEFORE the request — we have full info here, and
      // if the POST fails (timeout / 422) we still want sales to follow up.
      this.captureLead('trial');
      try {
        const { data } = await axios.post(`${API_BASE}/new-checkout/checkout/signup-trial`, {
          email: this.checkoutEmail.trim(),
          first_name: (this.buyer.first_name || '').trim(),
          last_name: (this.buyer.last_name || '').trim(),
          company_name: (this.buyer.company_name || '').trim(),
          phone: (this.buyer.phone || '').trim(),
          password: this.buyer.password || '',
          plan: this.companyConfig.plan,
          total_users: this.companyConfig.total_users,
          locations: this.companyConfig.locations,
        });
        if (data && data.status === 'ok') {
          // Server's existing-email branch omits `email` to avoid enumeration.
          // Merge the form's email back in so the no-token success copy
          // renders something concrete instead of "setup link to . Open it…"
          this.trialSignupResult = { ...data, email: data.email || this.checkoutEmail.trim() };
          // Scrub plaintext from the Vue instance the moment the POST
          // resolves. Matches the paid-checkout success-branch scrub.
          this.buyer.password = '';
          this.buyer.password_confirm = '';
          // New-email branch returns an auth token; existing-email branch does
          // not (the server keeps that response generic to avoid enumeration).
          // When we have a token, drop the buyer onto the dashboard — but
          // give them 2.5s on the success panel first so they can actually
          // read the "You're in. Check your email..." confirmation before
          // the dashboard replaces it. Jarring instant redirects are a
          // "did anything even happen?" footgun.
          if (data.token) {
            this.storeAuth(data);
            // Trial provisioned a real account — drop the anonymous cart
            // UUID and any restored buyer-form state so a future visit to
            // /pricing while logged in doesn't re-show stale checkout
            // fields tied to the old anonymous session.
            this.clearUiState();
            setTimeout(() => {
              window.location.assign('/?_=' + Date.now() + '#/dashboard');
            }, 2500);
          }
          // If no token (existing email), leave the cart + UI state alone —
          // the buyer might fix a typo and resubmit. The banner tells them
          // to check email; the form stays usable.
        } else {
          this.trialSignupError = (data && data.message) || 'Could not start your trial.';
        }
      } catch (e) {
        this.trialSignupError = e.response?.data?.message
          || e.response?.data?.errors?.email?.[0]
          || 'Could not start your trial.';
      } finally {
        this.trialSignupLoading = false;
      }
    },

    // Mirror Login.vue's localStorage writes so the rest of the SPA treats the
    // buyer as fully authenticated after signup/checkout. Keep in sync with
    // the login handler, CheckoutSuccess.vue, and DemoEnter.vue.
    //
    // IMPORTANT: hot-admin-id + hot-user-id MUST be set — DashboardLayout's
    // trackUserActivity() POSTs user/update_activity with one or the other
    // based on role, and a null value there returns 401 which triggers
    // handleUnauthorized() and wipes the session we just built.
    //
    // hot-sidebar + hot-user-name are also required — Dashboard.vue's
    // created() reads hot-sidebar to pick a sidebar variant and reads
    // hot-user-name for the employee-path greeting. Missing either of
    // those on auto-login = blank dashboard on first render.
    storeAuth(data) {
      try {
        localStorage.setItem('hot-token', data.token);
        localStorage.setItem('hot-user', data.role || '');
        localStorage.setItem('hot-sidebar', data.sidebar || '');
        localStorage.setItem('hot-email', data.email || '');
        localStorage.setItem('hot-logged-user', String(data.user_id || ''));
        localStorage.setItem('hot-user-full-name', data.full_name || '');
        localStorage.setItem('hot-user-name', data.user_name || data.full_name || '');
        localStorage.setItem('hot-user-id', String(data.user_id || ''));
        if (data.company_id) {
          localStorage.setItem('hot-admin-id', String(data.company_id));
          localStorage.setItem('hot-company-id', String(data.company_id));
        }
        if (axios?.defaults?.headers?.common) {
          axios.defaults.headers.common.Authorization = `Bearer ${data.token}`;
        }
      } catch (e) { /* private mode — dashboard will bounce to login */ }
    },

    /**
     * Pay button handler. Embedded Elements flow:
     *   1. Sync the cart so plan/seats/locations are current on the server.
     *   2. Capture as a lead — even if Stripe tokenization fails below we
     *      still want sales to follow up on a buyer who got to this point.
     *   3. Ask <stripe-element-card> to tokenize — triggers @token in
     *      onStripeToken() (or @error → onStripeError()). We DO NOT call
     *      /checkout/intent yet; token creation has to come first so we
     *      have a tok_... to send to the server.
     * The rest of the flow lives in onStripeToken → /checkout/intent.
     */
    async startCheckout() {
      if (!this.canCheckout || this.checkoutLoading || this.altPaymentSubmitting) return;
      // Invoice + ACH bypass Stripe Elements — they POST to legacy
      // /company/register which provisions the company in 'invoiced' /
      // 'ach pending' state. Dispatch here so the modal CTA stays a
      // single button across all three payment paths. Available on
      // both tabs; individual buyers using invoice/ACH register as a
      // one-person company keyed off their first+last name (see
      // submitAltCheckout fallback).
      if (this.paymentType === 'invoice' || this.paymentType === 'ach') {
        return this.submitAltCheckout(this.paymentType);
      }
      this.checkoutError = '';
      this.paymentError = '';
      this.checkoutLoading = true;
      this.captureLead('checkout_stripe');
      try {
        await this.queueCompanySync({ immediate: true });
        const card = this.$refs.stripeCard;
        if (!card || typeof card.submit !== 'function') {
          this.checkoutError = 'Card form is not ready — reload and try again.';
          this.checkoutLoading = false;
          return;
        }
        // Tokenize. @token fires onStripeToken which takes it from here.
        card.submit();
      } catch (e) {
        this.checkoutError = 'Checkout failed to start. Please try again.';
        this.checkoutLoading = false;
      }
    },

    /**
     * Invoice + ACH submission path. Both go through the legacy
     * /company/register endpoint which already handles `payment_type` of
     * 'invoice' or 'ach' (UserController::register, lines 2012-2070).
     * Mirrors NewSignUp1.vue:530-544 payload shape so the same backend
     * branch handles it. On success, store auth + redirect to dashboard
     * the same way the trial branch does — no card was charged for
     * invoice (we'll bill manually) and ACH settles in the background.
     */
    async submitAltCheckout(method) {
      if (this.altPaymentSubmitting) return;
      this.altPaymentSubmitting = true;
      this.altPaymentError = '';
      this.captureLead(method === 'invoice' ? 'checkout_invoice' : 'checkout_ach');
      try {
        // Push the latest cart state so the row reflects what the buyer
        // sees on the modal summary line (addons, plan, etc.) before we
        // hand off to /company/register.
        await this.queueCompanySync({ immediate: true });

        const inLicense = this.licenseMode === 'chooseOtherPlatform';
        const courseIds = inLicense
          ? ((this.licenseConfig.selectedCourseIds || []).slice())
          : (this.cart.items || []).map((i) => i.course_id).filter(Boolean);
        const totalUsers = inLicense
          ? Math.max(1, Number(this.licenseConfig.users) || 1)
          : Number(this.companyConfig.total_users) || 1;
        const locations = inLicense
          ? Math.max(1, Number(this.licenseConfig.locations) || 1)
          : Number(this.companyConfig.locations) || 1;

        // Common buyer payload — matches the field names CompanyController::register
        // already validates (company_name, first_name, last_name, company_email,
        // company_address_1, etc.) so we don't have to fork the controller for
        // /pricing. Mirrors NewSignUp1.vue's collectPayload + payment branches
        // (lines 530-544) where the legacy /sign-up shape was finalized. Address
        // fields default to invoice/ACH values; for either we always have them.
        const billingAddr = method === 'invoice'
          ? {
              address_1: this.invoiceFields.invoice_address || '',
              city: this.invoiceFields.invoice_city || '',
              state: this.invoiceFields.invoice_state || '',
              zip: this.invoiceFields.invoice_zip || '',
            }
          : {
              address_1: this.achFields.address_1 || '',
              city: this.achFields.city || '',
              state: this.achFields.state || '',
              zip: this.achFields.zip || '',
            };
        const firstInvoiceEmail = method === 'invoice'
          ? String(this.invoiceFields.invoice_emails || '').split(',')[0].trim()
          : '';
        // Individual buyers don't fill the company_name field — fall back
        // to "{first} {last}" so the legacy validator (company_name is
        // required on /company/register) still accepts the request and
        // the buyer ends up provisioned as a one-person company.
        const buyerFullName = ((this.buyer.first_name || '') + ' ' + (this.buyer.last_name || '')).trim();
        const companyNameForRegister = (this.buyer.company_name || '').trim() || buyerFullName;
        const payload = {
          // Identity (validator requires these on every register call)
          company_name: companyNameForRegister,
          first_name: (this.buyer.first_name || '').trim(),
          last_name: (this.buyer.last_name || '').trim(),
          company_email: method === 'invoice' && firstInvoiceEmail
            ? firstInvoiceEmail
            : this.checkoutEmail.trim(),
          email: this.checkoutEmail.trim(),
          phone: (this.buyer.phone || '').trim(),
          telephone_no: (this.buyer.phone || '').trim(),
          // Legacy validator requires company_address_1 — both ACH and
          // Invoice surfaces collect it, so we always have a value.
          company_address_1: billingAddr.address_1,
          company_city: billingAddr.city,
          company_state: billingAddr.state,
          company_zip: billingAddr.zip,
          // Auth — buyer's chosen password gets md5-hashed by legacy backend.
          company_password: this.buyer.password,
          username: this.checkoutEmail.trim(),

          // Plan + sizing — fed straight to the legacy backend so its
          // pricing math + entitlement service writes the right tbl_company.
          plan: inLicense ? 'lms_only' : (this.companyConfig.plan || null),
          billing_cycle: this.companyConfig.billing_cycle,
          paymentType: method === 'invoice' ? 'invoice' : 'ach',
          payment_type: method === 'invoice' ? 'invoice' : 'ach',
          billingCycle: this.companyConfig.billing_cycle,
          number_of_employees: totalUsers,
          number_of_locations: locations,
          // /pricing-vs-/sign-up parity tracking — backend doesn't yet
          // branch on this but capturing it on the row keeps lead+company
          // attribution honest.
          choose_platform: inLicense ? 'chooseOtherPlatform' : 'chooseLMS',

          // Cart contents — addons + courses both round-trip the same
          // shape UserController::register already reads (lines 2012-2070).
          addons_ids: Array.isArray(this.selectedAddons) ? this.selectedAddons : [],
          course_ids: courseIds,
          special_courses: inLicense
            ? ((this.licenseConfig.selectedCourseIds || []).map((id) => ({ id, users: totalUsers })))
            : (this.specialCourseSelections || []),

          // Cart row — lets the legacy fulfillment service mark paid + scrub
          // the same way the Stripe path does.
          session_uuid: this.sessionUuid || null,
        };

        if (method === 'invoice') {
          const inv = this.invoiceFields || {};
          // Legacy /sign-up sends `invoice: true` boolean (not an object) —
          // CompanyController::register reads $request->invoice == true to
          // branch into the invoice flow. The actual fields live as
          // top-level keys on the request.
          payload.invoice = true;
          payload.invoice_name = inv.invoice_name || '';
          payload.invoice_emails = inv.invoice_emails || '';
          payload.invoice_address = inv.invoice_address || '';
          payload.invoice_city = inv.invoice_city || '';
          payload.invoice_state = inv.invoice_state || '';
          payload.invoice_zip = inv.invoice_zip || '';
          payload.invoice_phone = (this.buyer.phone || '').trim();
          payload.invoice_surcharge_rate = INVOICE_SURCHARGE_RATE;
          // Surcharge math is currently display-only on the backend
          // (CompanyController::register doesn't read invoice_surcharge_rate),
          // so AR has no recorded number for what the buyer actually
          // agreed to. Pass the post-surcharge total + raw amounts so the
          // manual-billing team has the canonical number on the lead row.
          // Code-review fix 2026-05-01.
          payload.invoice_total = Number(this.displayedTotal) || 0;
          payload.invoice_subtotal = Number(this.displayedSubtotal) || 0;
          payload.invoice_surcharge_amount = Number(this.invoiceSurcharge) || 0;
        } else if (method === 'ach') {
          const a = this.achFields || {};
          // Same convention — boolean flag + capitalized ACH sub-object.
          payload.ach = true;
          payload.ACH = {
            account_holder_name: ((this.buyer.first_name || '') + ' ' + (this.buyer.last_name || '')).trim(),
            transit_number: a.transit_number || '',
            account_number: a.account_number || '',
            financial_instituation: a.financial_instituation || '',
            branch: a.branch || '',
            accountType: 'checking',
          };
        }

        const { data } = await axios.post(`${API_BASE}/company/register`, payload);

        // Backend returns { token, user } on success — same shape as
        // /trial/signup so we can reuse the auth + redirect helpers.
        if (data && data.token) {
          this.buyer.password = '';
          this.buyer.password_confirm = '';
          this.storeAuth(data);
          this.clearUiState();
          setTimeout(() => {
            window.location.assign('/?_=' + Date.now() + '#/dashboard');
          }, 600);
          return;
        }

        // No token — surface whatever message the server returned and
        // keep the buyer on the modal so they can fix + retry.
        this.altPaymentError = (data && data.message)
          || (method === 'invoice'
            ? 'We could not register your invoice account. Please double-check the recipient email + address.'
            : 'We could not register your ACH account. Please double-check your bank details.');
      } catch (e) {
        // 422 → field validation; surface the first message. Anything
        // else → generic copy + log.
        if (e?.response?.status === 422 && e.response.data) {
          const data = e.response.data;
          if (data.errors && typeof data.errors === 'object') {
            const firstKey = Object.keys(data.errors).find((k) => Array.isArray(data.errors[k]) && data.errors[k][0]);
            this.altPaymentError = firstKey ? data.errors[firstKey][0] : (data.message || 'Please double-check the form and try again.');
          } else {
            this.altPaymentError = data.message || 'Please double-check the form and try again.';
          }
        } else {
          this.altPaymentError = e?.response?.data?.message
            || 'Could not complete that right now. Please try again or email support@homeoftraining.com.';
        }
      } finally {
        this.altPaymentSubmitting = false;
      }
    },

    /**
     * Buyer dismissed the EnterpriseRequestModal. Mark dismissed so it
     * doesn't re-open on the next slider tweak this session. A fresh
     * page load reopens it once if they're still over the threshold —
     * we don't persist enterpriseDismissed.
     */
    dismissEnterpriseModal() {
      this.enterpriseModalOpen = false;
      this.enterpriseDismissed = true;
    },

    /**
     * @token from <stripe-element-card>. We have a tok_... — POST the intent
     * and let the backend confirm. Server returns:
     *   - status=succeeded → redirect to /#/checkout/success?payment_intent=pi_...
     *   - status=requires_action → run 3DS via stripe.handleNextAction then
     *     retry the redirect
     *   - status=error → surface the message (decline reason for card errors)
     */
    async onStripeToken(token) {
      this.paymentError = '';
      try {
        const tokenId = token && token.id;
        if (!tokenId) {
          this.paymentError = 'Could not read card token — please re-enter your card.';
          this.checkoutLoading = false;
          return;
        }
        const { data } = await axios.post(`${API_BASE}/new-checkout/checkout/intent`, {
          session_uuid: this.sessionUuid,
          email: this.checkoutEmail.trim(),
          first_name: (this.buyer.first_name || '').trim(),
          last_name: (this.buyer.last_name || '').trim(),
          company_name: (this.buyer.company_name || '').trim() || null,
          phone: (this.buyer.phone || '').trim() || null,
          password: this.buyer.password,
          payment_token: tokenId,
        });
        const intent = (data && data.intent) || {};
        // Clear plaintext password from Vue state on terminal success paths
        // only — NOT on decline/error, because canCheckout gates the Pay
        // button on password length + match, and clearing eagerly would
        // leave the button disabled on retry. The server has the md5 hash
        // at this point anyway. Declines don't need a scrub since the state
        // reverts to "modal open, buyer still filling form" and
        // closeCheckoutModal clears on dismiss.
        const clearPassword = () => {
          this.buyer.password = '';
          this.buyer.password_confirm = '';
        };
        if (intent.status === 'succeeded' && intent.intent_id) {
          clearPassword();
          // Stripe confirmed — release every piece of anonymous-session
          // state on the way to /checkout/success. The cart is already
          // marked paid server-side; clearing the UUID + UI blob means a
          // post-purchase visit to /pricing starts fresh instead of
          // resurrecting the cart we just charged for.
          this.clearUiState();
          window.location.assign('/?_=' + Date.now() + '#/checkout/success?payment_intent=' + encodeURIComponent(intent.intent_id));
          return;
        }
        if (intent.status === 'requires_action' && intent.client_secret) {
          // 3DS / SCA challenge. Intent is already server-confirmed against
          // the tokenized card, so the client only needs to complete the
          // next_action. handleNextAction({clientSecret}) is Stripe's
          // current recommended API for split-flow auth (server confirms,
          // client authenticates). vue-stripe loaded Stripe.js globally
          // when the card element mounted, so window.Stripe is live.
          if (!window.Stripe) {
            this.paymentError = 'Card authentication was required but Stripe.js did not load.';
            this.checkoutLoading = false;
            return;
          }
          const stripe = window.Stripe(this.stripePublicKey);
          const result = await stripe.handleNextAction({ clientSecret: intent.client_secret });
          if (result.error) {
            this.paymentError = result.error.message || 'Card authentication failed.';
            this.checkoutLoading = false;
            return;
          }
          const pi = result.paymentIntent;
          if (pi && pi.status === 'succeeded') {
            clearPassword();
            window.location.assign('/?_=' + Date.now() + '#/checkout/success?payment_intent=' + encodeURIComponent(pi.id));
            return;
          }
          this.paymentError = 'Payment could not be completed. Please try a different card.';
          this.checkoutLoading = false;
          return;
        }
        if (intent.mock) {
          // Dev environment without STRIPE_SECRET_KEY — don't redirect to a
          // success page that will then 500 on /checkout/complete.
          this.checkoutError = intent.error || 'Stripe is not configured on this environment.';
          this.checkoutLoading = false;
          return;
        }
        if (intent.status === 'error' || intent.error) {
          this.paymentError = intent.error || 'Payment failed.';
          this.checkoutLoading = false;
          return;
        }
        this.paymentError = 'Unexpected response from payment server.';
        this.checkoutLoading = false;
      } catch (e) {
        this.paymentError = e.response?.data?.error
          || e.response?.data?.message
          || 'Payment request failed.';
        this.checkoutLoading = false;
      }
    },

    /**
     * @error from <stripe-element-card>. Tokenization failed (invalid card,
     * incomplete form, Stripe network error). Surface the message and unlock
     * the button so the buyer can fix and resubmit.
     */
    onStripeError(err) {
      this.paymentError = (err && err.message) || 'Please check your card details and try again.';
      this.checkoutLoading = false;
    },

    async provisionDemo() {
      if (!this.demoForm.email || this.demoLoading) return;
      this.demoLoading = true;
      this.demoError = '';
      // Capture before POST — even if Stripe/demo spinup fails, we have them.
      this.captureLead('demo');
      try {
        const { data } = await axios.post(`${API_BASE}/new-checkout/checkout/demo`, this.demoForm);
        this.demoResult = data;
        // Auto-redirect into the demo redeemer page after the success beat.
        // The magic_link is same-origin (`/#/demo/enter?token=…`), so
        // safeRedirect accepts it.
        setTimeout(() => {
          const target = this.safeRedirect(data.magic_link) || '';
          if (target) {
            window.location.assign('/?_=' + Date.now() + target);
          }
        }, 1500);
      } catch (e) {
        this.demoError = e.response?.data?.error || 'Could not provision demo.';
      } finally {
        this.demoLoading = false;
      }
    },

    switchTab(key, event) {
      // Pure local-state tab switch. We deliberately do NOT write to the URL
      // here — any history API call that changes the hash triggers Vue
      // Router's scrollBehavior which yanks the user back to the top.
      // Deep links like ?tab=company still work on first load via handleDeepLink().
      if (this.tab === key) return;
      if (event && event.currentTarget && event.currentTarget.blur) {
        event.currentTarget.blur();
      }
      this.tab = key;
    },

    // WAI-ARIA roving-tabindex keyboard nav for the tab row.
    moveTabFocus(delta) {
      const idx = this.tabs.findIndex(t => t.key === this.tab);
      const next = (idx + delta + this.tabs.length) % this.tabs.length;
      this.focusTab(this.tabs[next].key);
    },
    focusTab(key) {
      this.tab = key;
      this.$nextTick(() => {
        const btn = document.getElementById(`ph-tab-${key}`);
        if (btn) btn.focus();
      });
    },

    /**
     * Read ?course=…&plan=…&users=…&autocheckout=1 etc. and prefill the
     * page state. Designed for the 36 hot-wordpress landing pages that
     * deep-link buyers straight to /pricing with intent baked into the URL.
     *
     * Contract:
     *   ?course=<id|slug>         → individual tab + add that course
     *   ?courses=<csv of ids|slugs> → individual tab + add each
     *   ?course_slug= / ?course_slugs= → aliases for the slug variants
     *   ?add=<slug>               → legacy single-add (kept working)
     *   ?account_type=company     → opens company tab (also accepts ?tab=company)
     *   ?plan=<hot|sizzling|scorching|lms_only> → company tab + plan preselected
     *   ?type=annual              → annual billing
     *   ?users=<int>              → company total_users (clamped 1..100000)
     *   ?locations=<int>          → company locations (clamped 1..10000)
     *   ?autocheckout=1           → after settle, scroll to + open checkout modal
     */
    async handleDeepLink() {
      const q = this.$route.query;

      // CompanySignupWizard auto-opens when the URL signals company intent.
      // ?account_type=company opens at Step 1; ?plan=<key> opens at Step 2
      // with the plan preselected. Legacy ?tab=company is honored. The
      // earlier ?wizard=1 flag still works as a manual override.
      //
      // Owner override (2026-05-01): ?wizard=0 explicitly suppresses the
      // wizard so a homepage CTA can land buyers on the company tab
      // directly without the 3-step modal. Tab + plan + sliders + cart
      // are all visible on the page itself; the wizard was always a
      // wrapper around that surface, not an additional step.
      const wizardSuppressed = q.wizard === '0' || q.wizard === 0 || q.wizard === false;
      const wantsCompanyFlow = !wizardSuppressed && (
           q.wizard === '1' || q.wizard === 1 || q.wizard === true
        || q.account_type === 'company'
        || q.tab === 'company'
        || (q.plan && ['hot', 'sizzling', 'scorching', 'lms_only'].includes(q.plan))
      );
      if (wantsCompanyFlow) {
        const planParam = q.plan && ['hot', 'sizzling', 'scorching', 'lms_only'].includes(q.plan)
          ? q.plan
          : null;
        const stepParam = q.step
          ? Math.min(3, Math.max(1, parseInt(q.step, 10) || 1))
          : (planParam ? 2 : 1);
        this.companyWizardInitialStep = stepParam;
        this.companyWizardInitialPlan = planParam;
        this.companyWizardOpen = true;
      }

      // Tab selection — explicit ?account_type wins, then ?tab.
      if (q.account_type === 'company' || q.tab === 'company') {
        this.tab = 'company';
      } else if (q.account_type === 'individual' || q.tab === 'individual') {
        this.tab = 'individual';
      }

      // Plan preselect — implies company tab.
      if (q.plan && ['hot', 'sizzling', 'scorching', 'lms_only'].includes(q.plan)) {
        this.tab = 'company';
        this.companyConfig.plan = q.plan;
      }
      if (q.type === 'annual') this.companyConfig.billing_cycle = 'annual';

      // Company sliders. Bound by the existing CartController validation
      // (max 100000 users, max 10000 locations) so a malformed query string
      // can't crash the server-side update.
      if (q.users) {
        const n = parseInt(q.users, 10);
        if (Number.isFinite(n) && n > 0) {
          this.companyConfig.total_users = Math.min(100000, n);
          this.tab = 'company';
        }
      }
      if (q.locations) {
        const n = parseInt(q.locations, 10);
        if (Number.isFinite(n) && n > 0) {
          this.companyConfig.locations = Math.min(10000, n);
          this.tab = 'company';
        }
      }

      // Course list. Accept id-or-slug in any of: course / courses /
      // course_slug / course_slugs / add (legacy single).
      const tokens = []
        .concat(this.parseQueryList(q.course))
        .concat(this.parseQueryList(q.courses))
        .concat(this.parseQueryList(q.course_slug))
        .concat(this.parseQueryList(q.course_slugs))
        .concat(this.parseQueryList(q.add));

      if (tokens.length && this.catalog.length) {
        // If any course was requested, the buyer is on the individual path
        // unless they explicitly asked for the company tab.
        if (this.tab !== 'company') this.tab = 'individual';

        this.prefillRunning = true;
        const missing = [];
        try {
          for (const tok of tokens) {
            // eslint-disable-next-line no-await-in-loop
            const matched = await this.addCourseByToken(tok);
            if (!matched) missing.push(tok);
          }
        } finally {
          this.prefillRunning = false;
        }
        if (missing.length) {
          this.prefillError = `Some courses from this link couldn't be added (${missing.join(', ')}).`;
        }
      }

      // Push company config to the server now that all flags settled, so
      // the cart total reflects the prefill before the buyer sees the page.
      if (this.tab === 'company') {
        this.queueCompanySync({ immediate: true });
      }

      // Autocheckout — fire after the catalog is mounted so the modal can
      // pull a fresh cart total. Only honored when the cart actually has
      // contents; we never spring a payment form on someone for an empty cart.
      if (q.autocheckout === '1' || q.autocheckout === 1 || q.autocheckout === true) {
        this.$nextTick(() => {
          if (!this.cartHasContents) return;
          const cart = document.querySelector('.ph-cart');
          if (cart && typeof cart.scrollIntoView === 'function') {
            cart.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          this.openCheckoutModal();
        });
      }

      // Direct-to-trial entry. ?action=trial flips to company tab + opens
      // the checkout modal where the "Start 7-day free trial — no card
      // required" CTA lives. One URL → trial form. Implies ?wizard=0
      // because the wizard is a separate guided flow.
      if (q.action === 'trial') {
        this.tab = 'company';
        if (!this.companyConfig.plan && !q.plan) {
          this.companyConfig.plan = 'hot';
        }
        this.$nextTick(() => {
          this.openCheckoutModal();
          // Scroll the trial CTA into view inside the modal so it lands
          // above the fold even if the buyer form is long.
          setTimeout(() => {
            const trialBtn = document.querySelector('.ph-modal-trial-cta');
            if (trialBtn && typeof trialBtn.scrollIntoView === 'function') {
              trialBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 350);
        });
      }
    },

    /**
     * Normalize any of the supported query forms (`a,b,c`, `["a","b"]`,
     * single string) into a clean list of trimmed lowercase tokens.
     */
    parseQueryList(value) {
      if (!value) return [];
      const arr = Array.isArray(value) ? value : String(value).split(',');
      return arr
        .map((s) => String(s).trim().toLowerCase())
        .filter(Boolean);
    },

    /**
     * Add a course by either numeric id or slug. Returns true if the
     * course resolved + the add-course call did not throw, false if it
     * couldn't be resolved at all (so the caller can surface a banner).
     */
    async addCourseByToken(token) {
      if (!token) return false;
      let course = null;
      if (/^\d+$/.test(token)) {
        const id = parseInt(token, 10);
        course = this.catalog.find((c) => c.id === id) || null;
      }
      if (!course) {
        const slug = token.replace(/_/g, '-');
        course = this.catalog.find((c) => (c.slug || '').toLowerCase() === slug)
          || this.catalog.find((c) => (c.name || '').toLowerCase().includes(slug.replace(/-/g, ' ')))
          || null;
      }
      if (!course) return false;
      if (!this.isInCart(course.id)) {
        try {
          await this.addCourse(course.id);
        } catch (e) {
          // addCourse swallows errors into cartError already; surface this
          // case as a soft miss instead of a crash.
          return false;
        }
      }
      return true;
    },

    /**
     * Measure all three tab panes and pin the wrapper to the tallest height.
     * Called at mount, after catalog load, and on resize. This makes tab
     * switching visually stable — the document height never shrinks when the
     * active pane changes, so the browser never clamps scrollY.
     */
    measureTallestPane() {
      const wrap = this.$refs.panes;
      if (!wrap) return;
      // Temporarily clear min-height so we can measure intrinsic sizes.
      this.panesMinHeight = null;
      this.$nextTick(() => {
        const panes = wrap.querySelectorAll('.ph-tab-pane');
        let max = 0;
        panes.forEach((p) => {
          // Temporarily reveal so offsetHeight reflects the pane's natural size.
          const prevVis = p.style.visibility;
          const prevPos = p.style.position;
          p.style.visibility = 'hidden';
          p.style.position = 'relative';
          const h = p.scrollHeight;
          if (h > max) max = h;
          p.style.visibility = prevVis;
          p.style.position = prevPos;
        });
        if (max > 0) this.panesMinHeight = max;
      });
    },

    initRevealObserver() {
      if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
      const nodes = this.$el.querySelectorAll('[data-reveal]');
      if (!nodes.length) return;
      // Mark everything starting-state
      nodes.forEach(n => n.classList.add('ph-reveal-pre'));
      this.revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('ph-reveal-pre');
            entry.target.classList.add('ph-reveal-in');
            this.revealObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
      nodes.forEach(n => this.revealObserver.observe(n));
    },

    injectMetaTags() {
      document.title = 'Pricing — Home of Training';
      const set = (name, content, attr = 'name') => {
        let el = document.querySelector(`meta[${attr}="${name}"]`);
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute(attr, name);
          document.head.appendChild(el);
        }
        el.setAttribute('content', content);
      };
      set('description', 'Home of Training pricing — compliance and certification courses for individuals and teams. Honest prices, instant certificates, SOC 2 and HIPAA compliant.');
      set('og:title', 'Pricing — Home of Training', 'property');
      set('og:description', 'Honest pricing for individuals and teams. Free 7-day trial on every Company plan.', 'property');
      set('og:type', 'website', 'property');
    },
  },
};
</script>

<style lang="scss" scoped>
$brand: #FE672C;
$brand-2: #FF8A4C;
$brand-dark: #D94B14;
$ink: #1a1a1a;
$ink-2: #4a4a4a;
$muted: #707070;
$line: #e9e9ef;
$bg: #fafafa;
$radius: 10px;
$shadow: 0 4px 14px 1px rgba(141, 141, 141, 0.18);
$shadow-lg: 0 12px 32px 0 rgba(0, 0, 0, 0.12);

.pricing-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: $ink;
  background: $bg;

  .container { max-width: 1280px; padding-left: 28px; padding-right: 28px; margin: 0 auto; }
  h1, h2, h3, h4 { color: $ink; }
  * { box-sizing: border-box; }
}

/* ═══ PROMO BANNER ═══ */
.ph-promo-banner {
  background: linear-gradient(90deg, #ecfdf5 0%, #d1fae5 100%);
  border-bottom: 1px solid #a7f3d0;
  padding: 10px 0;
  font-size: 13px;
  color: #065f46;
  font-weight: 500;
}
.ph-promo-banner-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}
.ph-promo-icon { font-size: 16px; }

/* ═══ HERO (pricing lives here now) ═══ */
.ph-hero {
  position: relative;
  padding: 48px 0 64px;
  background: linear-gradient(180deg, #fff 0%, $bg 100%);
  overflow: hidden;
}
.ph-hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 28px;
  align-items: flex-start;
}
.ph-hero-main { min-width: 0; }

.ph-eyebrow {
  display: inline-block;
  background: rgba($brand, 0.1);
  color: $brand-dark;
  font-weight: 600;
  font-size: 12px;
  padding: 5px 12px;
  border-radius: 100px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 14px;
}
.ph-h1 {
  font-size: 46px;
  line-height: 1.05;
  letter-spacing: -1.2px;
  font-weight: 800;
  margin: 0 0 14px;
}
.ph-h1-accent {
  background: linear-gradient(120deg, $brand 0%, $brand-2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.ph-sub {
  font-size: 16px;
  color: $ink-2;
  line-height: 1.55;
  max-width: 580px;
  margin: 0 0 24px;
}

/* ═══ TABS (sit inside hero now) ═══ */
.ph-tabs {
  display: inline-flex;
  background: #fff;
  border: 1px solid $line;
  border-radius: 12px;
  padding: 5px;
  box-shadow: $shadow;
  margin-bottom: 28px;
}

// Plan D Phase C — two-CTA hero stack replaces the three-tab switcher.
// Owner directive: simpler entry, fewer decisions before Pay.
.ph-cta-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  margin: 4px 0 28px;
}
.ph-cta-primary {
  font-size: 16px;
  padding: 14px 24px;
  border-radius: 10px;
  min-height: 48px;
  font-weight: 800;
  letter-spacing: 0.2px;
}
.ph-cta-secondary {
  font-size: 14px;
  padding: 10px 18px;
  border-radius: 100px;
  min-height: 42px;
  font-weight: 700;
  background: transparent;
  color: $brand-dark;
  border: 1px solid rgba($brand, 0.5);
  cursor: pointer;
  transition: background 0.16s, color 0.16s;
  &:hover { background: rgba($brand, 0.08); }
  &:focus-visible { outline: 2px solid $brand; outline-offset: 2px; }
}
.ph-cta-tertiary {
  appearance: none;
  background: transparent;
  border: 0;
  color: $muted;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 0;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 4px;
  &:hover { color: $brand-dark; }
  &:focus-visible { outline: 2px solid $brand; outline-offset: 2px; }
}
.ph-tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 10px 22px;
  border: none;
  background: transparent;
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.25s ease;
  color: $muted;
  &:hover { color: $ink; }
  &--active {
    background: linear-gradient(135deg, $brand 0%, $brand-2 100%);
    color: #fff !important;
    box-shadow: 0 4px 12px rgba($brand, 0.35);
    .ph-tab-sub { color: rgba(#fff, 0.85); }
  }
}
.ph-tab-icon { font-size: 18px; line-height: 1; }
.ph-tab-label { font-weight: 700; font-size: 14px; }
.ph-tab-sub { font-size: 10px; color: $muted; text-transform: uppercase; letter-spacing: 0.4px; }

/* ═══ TAB CONTENT PANE ═══ */
/* CSS grid overlap keeps all three panes in the same cell so the grid cell's
   height = max(pane heights). Inactive panes are invisible but still layout. */
.ph-panes {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto;
  position: relative;
}
.ph-tab-pane {
  grid-column: 1 / -1;
  grid-row: 1 / -1;
  min-width: 0;
  visibility: hidden;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}
.ph-tab-pane--active {
  visibility: visible;
  pointer-events: auto;
  opacity: 1;
  z-index: 2;
}
.ph-catalog-head {
  margin-bottom: 22px;
  h2 { font-size: 24px; font-weight: 800; letter-spacing: -0.4px; margin: 0 0 6px; }
  p { font-size: 14px; color: $ink-2; margin: 0; }
}

/* ═══ CATALOG CONTROLS ═══ */
.ph-catalog-controls {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 22px;
}
.ph-search-wrap { position: relative; max-width: 440px; }
.ph-search-icon {
  position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
  width: 17px; height: 17px; color: $muted; pointer-events: none;
}
.ph-search-input {
  width: 100%;
  height: 42px;
  padding: 0 14px 0 40px;
  border: 1px solid $line;
  border-radius: $radius;
  background: #fff;
  font-size: 14px;
  color: $ink;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: $brand; box-shadow: 0 0 0 4px rgba($brand, 0.12); }
}
.ph-categories { display: flex; gap: 7px; flex-wrap: wrap; }
.ph-cat-chip {
  padding: 6px 14px;
  border: 1px solid $line;
  background: #fff;
  border-radius: 100px;
  font-size: 12px;
  color: $ink-2;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: $brand; color: $brand; }
  &--active { background: $ink; color: #fff; border-color: $ink; }
}

/* ═══ COURSES GRID ═══ */
.ph-courses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}
.ph-course-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid $line;
  border-radius: $radius;
  padding: 16px 18px 14px;
  box-shadow: $shadow;
  transition: all 0.25s ease;
  min-height: 195px;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.09);
    border-color: rgba($brand, 0.4);
  }
  &--in-cart {
    border-color: $brand;
    background: #fff;
  }
}
.ph-course-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
}
.ph-course-badge {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: $brand-dark;
  background: rgba($brand, 0.1);
  padding: 4px 9px;
  border-radius: 100px;
}
.ph-course-price {
  font-size: 20px;
  font-weight: 800;
  color: $ink;
  line-height: 1;
  white-space: nowrap;
}
.ph-currency { font-size: 13px; color: $muted; vertical-align: top; margin-right: 1px; }
.ph-course-name {
  font-size: 15px;
  font-weight: 700;
  margin: 0 0 6px;
  line-height: 1.3;
}
/* Multi-variant pill — appears next to a grouped course's title to
   signal "this card collapses N similar courses; click to choose." */
.ph-variant-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 2px 8px;
  font-size: 10px;
  line-height: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #d34c14;
  background: rgba(254, 103, 44, 0.12);
  border-radius: 999px;
  vertical-align: middle;
}
.ph-course-card--group { border-color: rgba(254, 103, 44, 0.35); }
.ph-course-card--group-active {
  border-color: #fe672c;
  background: linear-gradient(135deg, #fff8f4 0%, #fff 100%);
}
.ph-extra-card--group { border-color: rgba(254, 103, 44, 0.35); }
.ph-course-desc {
  font-size: 12px;
  color: $ink-2;
  line-height: 1.5;
  margin: 0 0 12px;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.ph-course-meta {
  display: flex;
  gap: 10px;
  font-size: 11px;
  color: $muted;
  margin-bottom: 10px;
}
.ph-course-btn { width: 100%; padding: 8px 14px; font-size: 13px; }

/* ═══ PAGINATION ═══ */
.ph-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid $line;
}
.ph-page-btn {
  min-width: 36px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid $line;
  background: #fff;
  color: $ink-2;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  &:hover:not(:disabled):not(.ph-page-btn--dots) {
    border-color: $brand;
    color: $brand;
  }
  &:disabled:not(.ph-page-btn--active) { opacity: 0.4; cursor: not-allowed; }
  &--active {
    background: $ink;
    color: #fff;
    border-color: $ink;
    cursor: default;
  }
  &--num { padding: 0; }
  &--dots {
    border: none;
    cursor: default;
    background: transparent;
  }
}
.ph-page-info {
  margin-left: 10px;
  font-size: 12px;
  color: $muted;
  font-weight: 500;
}

/* ═══ BUTTONS ═══ */
.ph-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &--add {
    background: linear-gradient(135deg, $brand 0%, $brand-2 100%);
    color: #fff;
    box-shadow: 0 4px 10px rgba($brand, 0.3);
    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba($brand, 0.4);
    }
  }
  &--in-cart { background: $ink; color: #fff; }
  &--lg { padding: 14px 26px; font-size: 15px; border-radius: 10px; }
  &--checkout {
    width: 100%;
    background: linear-gradient(135deg, $brand 0%, $brand-2 100%);
    color: #fff;
    padding: 14px;
    font-size: 15px;
    font-weight: 700;
    border-radius: 11px;
    box-shadow: 0 6px 18px rgba($brand, 0.35);
    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 10px 24px rgba($brand, 0.45);
    }
  }
}

/* ═══ COMPANY PLANS ═══ */
.ph-plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 18px;
}
.ph-plan-card {
  position: relative;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 2px solid $line;
  border-radius: 14px;
  padding: 26px 20px 20px;
  cursor: pointer;
  transition: all 0.25s ease;
  &:hover {
    transform: translateY(-3px);
    box-shadow: $shadow-lg;
    border-color: rgba($brand, 0.4);
  }
  &--popular {
    border-color: $brand;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
  }
  &--selected {
    border-color: $brand;
    background: #fff;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
  }
}
.ph-plan-pop {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, $brand 0%, $brand-2 100%);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 5px 14px;
  border-radius: 100px;
  box-shadow: 0 4px 12px rgba($brand, 0.4);
  white-space: nowrap;
}
.ph-plan-name { font-size: 20px; font-weight: 800; margin: 0 0 2px; }
.ph-plan-tag {
  font-size: 12px;
  color: $muted;
  margin: 0 0 14px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-weight: 600;
}
.ph-plan-price {
  display: flex;
  align-items: baseline;
  gap: 3px;
  margin-bottom: 2px;
}
.ph-plan-from { font-size: 11px; color: $muted; margin-right: 4px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; }
.ph-plan-currency { font-size: 15px; color: $muted; font-weight: 600; }
.ph-plan-amount { font-size: 36px; font-weight: 800; line-height: 1; }
.ph-plan-per { font-size: 12px; color: $muted; }
.ph-plan-range { font-size: 11px; color: $muted; margin-bottom: 14px; }
.ph-plan-cta {
  width: 100%;
  padding: 10px 14px;
  font-size: 13px;
  margin-top: auto; /* pin CTA to bottom for equal-height cards */
}

/* ═══ PLAN INCLUDES ═══ */
.ph-plan-includes {
  border-top: 1px dashed $line;
  padding-top: 12px;
  margin: 14px 0 16px;
  min-height: 110px; /* consistent height across all 3 cards */
}
.ph-plan-includes-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
  span {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: $muted;
    font-weight: 700;
  }
  strong {
    font-size: 11px;
    color: $brand-dark;
    font-weight: 700;
  }
}
.ph-plan-includes-list {
  list-style: none;
  padding: 0;
  margin: 0;
  li {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: $ink-2;
    line-height: 1.45;
    margin-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ph-check {
    color: #10b981;
    font-weight: 800;
    font-size: 11px;
    flex-shrink: 0;
  }
  .ph-plan-includes-more {
    color: $brand-dark;
    font-weight: 700;
    font-size: 10px;
    margin-top: 4px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    &::before { content: ''; }
  }
}

/* ═══ EXTRAS PANEL (bolt-on courses on company plan) ═══ */
.ph-extras-panel {
  margin-top: 22px;
  background: #fff;
  border: 1px solid $line;
  border-radius: 14px;
  padding: 22px 24px;
}
.ph-extras-head {
  // Same flex+gap rhythm .ph-catalog-controls uses on the individual
  // tab so the title block, chip row, and search row each get
  // predictable 14px breathing room. Title + description live inside
  // .ph-extras-title which keeps them visually paired tight.
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 18px;
}
.ph-extras-title {
  h3 {
    font-size: 16px;
    font-weight: 800;
    margin: 0 0 4px;
    color: $ink;
  }
  p {
    font-size: 12px;
    color: $muted;
    margin: 0;
  }
}
.ph-categories--extras {
  // Tighten chip wrap a hair so the row reads as one band, not two
  // when categories happen to wrap.
  row-gap: 6px;
}
.ph-extras-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: $bg;
  border: 1px solid $line;
  border-radius: 10px;
  font-size: 13px;
  &:focus-within { border-color: $brand; }
}
.ph-extras-search-input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  outline: none;
  font-size: 13px;
  color: $ink;
  &::placeholder { color: $muted; }
}
.ph-extras-empty {
  padding: 22px 14px;
  font-size: 13px;
  color: $muted;
  text-align: center;
  background: $bg;
  border-radius: 10px;
}
.ph-extras-clear {
  margin-left: 8px;
  background: transparent;
  border: 0;
  color: $brand;
  font-weight: 700;
  cursor: pointer;
  font-size: 13px;
}
.ph-extras-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
.ph-extra-card {
  background: $bg;
  border: 1px solid $line;
  border-radius: 10px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: border-color 0.2s, transform 0.2s;
  &:hover { border-color: rgba($brand, 0.4); transform: translateY(-1px); }
}
.ph-extra-name {
  font-size: 12px;
  font-weight: 700;
  color: $ink;
  line-height: 1.35;
  min-height: 32px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.ph-extra-meta {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 10px;
}
.ph-extra-cat {
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: $muted;
  font-weight: 600;
}
.ph-extra-price {
  font-weight: 800;
  font-size: 13px;
  color: $ink;
}
.ph-extra-btn {
  padding: 7px 10px;
  font-size: 11px;
}
.ph-extras-pagination {
  margin-top: 14px;
  text-align: center;
}
.ph-extras-more {
  background: none;
  border: 1px solid $line;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 600;
  color: $brand-dark;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: rgba($brand, 0.06); border-color: rgba($brand, 0.4); }
}

/* ═══ COMPANY CART EXTRAS SECTION ═══ */
.ph-summary-extras {
  border-top: 1px dashed $line;
  margin-top: 14px;
  padding-top: 14px;
}
.ph-summary-extras-head {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: $muted;
  font-weight: 700;
  margin-bottom: 8px;
}
.ph-cart-list--compact {
  .ph-cart-item-name { font-size: 12px; }
  .ph-cart-item-price { font-size: 12px; }
}

/* ═══ CONFIG PANEL (sliders + toggle) ═══ */
.ph-config-panel {
  background: #fff;
  border: 1px solid $line;
  border-radius: 14px;
  padding: 26px 28px;
  box-shadow: $shadow;
  h3 { font-size: 17px; font-weight: 700; margin: 0 0 22px; }
}

.ph-slider-row {
  margin-bottom: 22px;
}
.ph-slider-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  span { font-size: 13px; font-weight: 600; color: $ink-2; }
}
.ph-slider-input {
  width: 90px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid $line;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  color: $brand-dark;
  text-align: right;
  background: #fff;
  transition: border-color 0.15s;
  &:focus { outline: none; border-color: $brand; box-shadow: 0 0 0 3px rgba($brand, 0.12); }
  /* remove spinner arrows */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  -moz-appearance: textfield;
}
.ph-slider {
  --pct: 0%;
  width: 100%;
  appearance: none;
  height: 6px;
  background: linear-gradient(to right,
    #{$brand} 0%,
    #{$brand-2} var(--pct),
    #{$line} var(--pct),
    #{$line} 100%);
  border-radius: 100px;
  outline: none;
  margin: 6px 0;
  transition: background 0.1s linear;
  &::-webkit-slider-runnable-track {
    height: 6px;
    background: transparent;
    border-radius: 100px;
  }
  &::-moz-range-track {
    height: 6px;
    background: linear-gradient(to right,
      #{$brand} 0%,
      #{$brand-2} var(--pct),
      #{$line} var(--pct),
      #{$line} 100%);
    border-radius: 100px;
  }
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    margin-top: -8px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #fff;
    border: 3px solid $brand;
    box-shadow: 0 4px 12px rgba($brand, 0.45), 0 0 0 0 rgba($brand, 0.35);
    cursor: pointer;
    transition: transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1),
                box-shadow 0.2s ease;
    &:hover {
      transform: scale(1.15);
      box-shadow: 0 6px 16px rgba($brand, 0.5), 0 0 0 6px rgba($brand, 0.12);
    }
    &:active { transform: scale(1.1); }
  }
  &::-moz-range-thumb {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #fff;
    border: 3px solid $brand;
    box-shadow: 0 4px 12px rgba($brand, 0.45);
    cursor: pointer;
    transition: transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1);
    &:hover { transform: scale(1.15); }
  }
}
/* Tick labels are absolute-positioned at their real % on the track,
   so a thumb at value=275 sits directly above the "250" label.
   padding 0 11px compensates for native range thumb-radius inset
   (thumb center only travels between [thumb-radius, track − thumb-radius]). */
.ph-slider-scale {
  position: relative;
  height: 16px;
  margin-top: 8px;
  padding: 0 11px;
  font-size: 10px;
  color: $muted;
  font-weight: 500;
  span {
    position: absolute;
    top: 0;
    transform: translateX(-50%);
    white-space: nowrap;
    &:first-child { transform: none; left: 0 !important; }
    &:last-child { transform: none; left: auto !important; right: 0; }
  }
}

.ph-cycle-toggle {
  display: inline-flex;
  background: $bg;
  border: 1px solid $line;
  border-radius: 10px;
  padding: 4px;
  margin-top: 8px;
}
.ph-cycle-opt {
  padding: 8px 18px;
  border: none;
  background: transparent;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  color: $muted;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  &:hover { color: $ink; }
  &--active {
    background: $ink;
    color: #fff !important;
    * { color: inherit !important; }
  }
}
.ph-save-pill {
  background: rgba(#10b981, 0.2);
  color: #10b981;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 100px;
}
.ph-cycle-opt--active .ph-save-pill { background: #10b981; color: #fff; }

/* ═══ Compact config strip — replaces ph-config-panel + sliders ═══ */
.ph-config-strip {
  display: flex;
  align-items: flex-end;
  gap: 22px;
  flex-wrap: wrap;
  background: #fff;
  border: 1px solid $line;
  border-radius: 12px;
  padding: 16px 22px;
  box-shadow: $shadow;
  margin: 22px 0;
}
.ph-config-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  &--billing { margin-left: auto; }
}
.ph-config-label {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: $muted;
  font-weight: 700;
}
.ph-config-input {
  width: 110px;
  height: 38px;
  padding: 0 12px;
  border: 1px solid $line;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 700;
  color: $brand-dark;
  background: #fff;
  transition: border-color 0.15s;
  &:focus { outline: none; border-color: $brand; box-shadow: 0 0 0 3px rgba($brand, 0.12); }
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  -moz-appearance: textfield;
}
.ph-config-item--billing .ph-cycle-toggle { margin-top: 0; }
@media (max-width: 640px) {
  .ph-config-strip { gap: 14px; padding: 14px 16px; }
  .ph-config-item--billing { margin-left: 0; width: 100%; }
}

/* Inline hint inside the HOT plan card pointing to build-your-own. */
.ph-plan-hint {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed $line;
  font-size: 11px;
  color: $muted;
  text-align: center;
  line-height: 1.5;
}
.ph-plan-hint-link {
  background: transparent;
  border: 0;
  font: inherit;
  font-size: 11px;
  font-weight: 700;
  color: $brand-dark;
  text-decoration: underline;
  cursor: pointer;
  padding: 0 0 0 4px;
  &:hover { color: $brand; }
}

/* "More ways to start" disclosure (Build-your-own + LMS Only). */
.ph-alt-options--collapsed {
  background: transparent;
  border: 0;
  padding: 0;
  margin: 4px 0 18px;
}
.ph-alt-options-summary {
  cursor: pointer;
  list-style: none;
  font-size: 13px;
  font-weight: 700;
  color: $ink-2;
  padding: 12px 18px;
  background: #fff;
  border: 1px solid $line;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: border-color 0.15s;
  &::-webkit-details-marker { display: none; }
  &::after {
    content: "▾";
    color: $muted;
    transition: transform 0.2s;
  }
  &:hover { border-color: $brand; }
}
.ph-alt-options--collapsed[open] .ph-alt-options-summary::after {
  transform: rotate(180deg);
}
.ph-alt-options--collapsed .ph-alt-options-grid {
  margin-top: 10px;
}

/* ═══ DEMO TAB (legacy styles — markup removed but classes still
       referenced by paymentError/checkoutError; safe to leave) ═══ */
.ph-demo-form {
  background: #fff;
  border: 1px solid $line;
  border-radius: 14px;
  padding: 28px;
  box-shadow: $shadow;
}
.ph-demo-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 22px;
}
.ph-field { display: flex; flex-direction: column; gap: 6px; &--full { grid-column: 1 / -1; } }
.ph-field-label { font-size: 12px; font-weight: 600; color: $ink-2; }
.ph-field-input {
  height: 42px;
  padding: 0 14px;
  border: 1px solid $line;
  border-radius: $radius;
  font-size: 14px;
  color: $ink;
  background: #fff;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:focus { outline: none; border-color: $brand; box-shadow: 0 0 0 3px rgba($brand, 0.12); }
  &--invalid {
    border-color: #dc2626;
    box-shadow: 0 0 0 3px rgba(#dc2626, 0.10);
    &:focus { border-color: #dc2626; box-shadow: 0 0 0 3px rgba(#dc2626, 0.18); }
  }
}
.ph-demo-cta { width: 100%; margin-bottom: 16px; }
.ph-demo-list {
  background: $bg;
  border-radius: $radius;
  padding: 18px 20px;
  margin-top: 16px;
  h4 {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: $muted;
    margin: 0 0 10px;
  }
  ul { list-style: none; padding: 0; margin: 0; }
  li {
    font-size: 13px;
    color: $ink-2;
    padding: 4px 0 4px 18px;
    position: relative;
    &::before {
      content: '✓';
      position: absolute;
      left: 0;
      color: $brand;
      font-weight: 700;
    }
  }
}
.ph-demo-success {
  background: linear-gradient(180deg, #fff 0%, #f0fdf4 100%);
  border: 1px solid #a7f3d0;
  border-radius: 14px;
  padding: 36px 30px;
  text-align: center;
  box-shadow: $shadow-lg;
  h3 { font-size: 22px; margin: 14px 0 8px; }
  p { color: $ink-2; margin: 0 0 20px; font-size: 14px; }
}
.ph-demo-success-icon {
  width: 62px;
  height: 62px;
  border-radius: 50%;
  background: #10b981;
  color: #fff;
  font-size: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 20px rgba(#10b981, 0.4);
}
.ph-demo-meta { font-size: 12px; color: $muted; margin-top: 14px; }
.ph-demo-error {
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
  border-radius: $radius;
  padding: 10px 14px;
  font-size: 13px;
  margin-top: 10px;
}

/* ═══ CART SIDEBAR ═══ */
.ph-cart {
  position: sticky;
  top: 24px;
  width: 340px;
  min-width: 0;
}
.ph-cart-inner {
  background: #fff;
  border: 1px solid $line;
  border-radius: 14px;
  padding: 22px 22px 20px;
  box-shadow: $shadow-lg;
}
.ph-cart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid $line;
  h3 { font-size: 15px; font-weight: 700; margin: 0; }
}
.ph-cart-count {
  background: rgba($brand, 0.12);
  color: $brand-dark;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 100px;
  &--plan {
    background: linear-gradient(135deg, $brand 0%, $brand-2 100%);
    color: #fff;
    letter-spacing: 0.4px;
  }
}
.ph-cart-empty {
  text-align: center;
  padding: 20px 8px 12px;
  .ph-cart-empty-icon { font-size: 32px; opacity: 0.55; margin-bottom: 8px; }
  p { color: $muted; font-size: 13px; margin: 0; line-height: 1.5; }
}
.ph-cart-revival {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba($brand, 0.08);
  border: 1px solid rgba($brand, 0.25);
  color: $brand-dark;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 10px;
  border-radius: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
  .ph-cart-revival-text { flex: 1 1 auto; line-height: 1.35; }
  .ph-cart-revival-clear {
    appearance: none;
    background: $brand;
    color: #fff;
    border: 0;
    padding: 4px 10px;
    border-radius: 100px;
    font-weight: 700;
    cursor: pointer;
    font-size: 11px;
    letter-spacing: 0.2px;
    &:hover { background: $brand-dark; }
  }
  .ph-cart-revival-dismiss {
    appearance: none;
    background: transparent;
    border: 0;
    color: $brand-dark;
    font-size: 18px;
    line-height: 1;
    padding: 0 4px;
    cursor: pointer;
    opacity: 0.7;
    &:hover { opacity: 1; }
  }
}
.ph-cart-list {
  list-style: none;
  padding: 0;
  margin: 0 0 12px;
  max-height: 260px;
  overflow-y: auto;
}
.ph-cart-item {
  padding: 10px 0;
  border-bottom: 1px solid $line;
  &:last-child { border-bottom: 0; }
}
.ph-cart-item-name { font-size: 12px; font-weight: 600; color: $ink; line-height: 1.3; margin-bottom: 5px; }
.ph-cart-item-row { display: flex; align-items: center; justify-content: space-between; }
.ph-cart-item-price { font-size: 13px; font-weight: 700; color: $brand-dark; }
.ph-cart-remove {
  width: 22px; height: 22px;
  border-radius: 50%;
  border: none;
  background: $bg;
  color: $muted;
  font-size: 17px;
  line-height: 1;
  cursor: pointer;
  &:hover { background: #fef2f2; color: #dc2626; }
}
.ph-summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 7px 0;
  font-size: 13px;
  color: $muted;
  strong { color: $ink; font-weight: 600; }
  &--calc { font-size: 12px; }
  &--rate {
    border-top: 1px dashed $line;
    border-bottom: 1px dashed $line;
    margin: 6px 0;
    padding: 10px 0;
    color: $brand-dark;
    strong { color: $brand-dark; font-weight: 800; font-size: 15px; }
  }
}
.ph-summary-plan { color: $brand-dark !important; font-weight: 700 !important; }
.ph-summary-row--cycle {
  align-items: center;
}
.ph-cart-cycle {
  display: inline-flex;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 999px;
  padding: 3px;
  gap: 0;
}
.ph-cart-cycle-opt {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.2px;
  color: $muted;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s, color 0.12s;
}
.ph-cart-cycle-opt--active {
  background: #1a1a1a;
  color: #fff;
}
.ph-cart-cycle-save {
  font-size: 10px;
  font-weight: 700;
  margin-left: 4px;
  color: $brand;
}
.ph-cart-cycle-opt--active .ph-cart-cycle-save {
  color: #ffb38a;
}
.ph-summary-volume {
  font-size: 11px;
  color: #047857;
  font-weight: 600;
  padding: 8px 0 0;
  text-align: center;
}
.ph-promo {
  margin: 14px 0 12px;
  padding: 14px 0 0;
  border-top: 1px solid $line;
}
.ph-promo-input-row { display: flex; gap: 7px; }
.ph-promo-input {
  flex: 1;
  min-width: 0;
  height: 34px;
  padding: 0 11px;
  border: 1px solid $line;
  border-radius: 7px;
  font-size: 12px;
  &:focus { outline: none; border-color: $brand; }
}
.ph-promo-apply {
  height: 34px;
  padding: 0 14px;
  border: none;
  background: $ink;
  color: #fff;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: $brand; }
}
.ph-promo-applied {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 9px 12px;
  background: rgba(#10b981, 0.1);
  color: #047857;
  border-radius: 8px;
  font-size: 12px;
  button {
    background: none;
    border: none;
    color: #047857;
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;
  }
}
.ph-promo-message { font-size: 11px; margin-top: 6px; color: #047857; &--err { color: #dc2626; } }
.ph-cart-totals {
  margin-top: 6px;
  padding-top: 14px;
  border-top: 1px solid $line;
}
.ph-totals-row {
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
  font-size: 13px;
  color: $ink-2;
  &--discount { color: #047857; font-weight: 600; }
  &--total {
    font-size: 18px;
    font-weight: 800;
    color: $ink;
    border-top: 1px solid $line;
    padding-top: 12px;
    margin-top: 6px;
    small { font-size: 12px; color: $muted; font-weight: 500; margin-left: 2px; }
  }
}
.ph-checkout-trust {
  text-align: center;
  font-size: 11px;
  color: $muted;
  margin-top: 12px;
}

/* ═══ TRUST BAND (stats + compliance) ═══ */
.ph-trust-band {
  background: #fff;
  border-top: 1px solid $line;
  border-bottom: 1px solid $line;
  padding: 56px 0 52px;
}
.ph-trust-container {
  max-width: min(1120px, 82%);
  margin: 0 auto;
}
.ph-trust-header {
  text-align: center;
  margin-bottom: 38px;
}
.ph-trust-label {
  display: inline-block;
  background: rgba($brand, 0.1);
  color: $brand-dark;
  font-weight: 700;
  font-size: 11px;
  padding: 5px 12px;
  border-radius: 100px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 12px;
}
.ph-trust-title {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.4px;
  margin: 0 auto;
  max-width: 680px;
  color: $ink;
}
.ph-trust-grid {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 44px;
}
@media (max-width: 768px) {
  .ph-trust-grid {
    gap: 28px;
    .ph-trust-badge-wrap { border-left: 0; }
  }
}
.ph-trust-stat {
  display: flex;
  flex-direction: column;
  strong {
    font-size: 28px;
    font-weight: 800;
    color: $ink;
    line-height: 1;
    letter-spacing: -0.5px;
  }
  span {
    font-size: 10px;
    color: $muted;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin-top: 6px;
    font-weight: 600;
  }
}
.ph-trust-badge-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px;
  border-left: 1px solid $line;
  transition: transform 0.25s ease;
  &:hover { transform: translateY(-2px); }
}
.ph-trust-badge {
  height: 60px;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  filter: grayscale(12%);
  opacity: 0.95;
  transition: all 0.25s ease;
  .ph-trust-badge-wrap:hover & { filter: none; opacity: 1; }
}
.ph-trust-badge-label {
  font-size: 9px;
  color: $muted;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-weight: 700;
  white-space: nowrap;
}

/* ═══ FAQ ═══ */
.ph-faq {
  background: $bg;
  padding: 56px 0 72px;
}
.ph-faq-title {
  font-size: 26px;
  font-weight: 800;
  text-align: center;
  margin: 0 0 36px;
}
.ph-faq-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  max-width: 960px;
  margin: 0 auto;
}
.ph-faq-item {
  background: #fff;
  border-radius: $radius;
  padding: 18px 22px;
  border: 1px solid $line;
  summary {
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    color: $ink;
    list-style: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    &::after {
      content: '+';
      font-size: 22px;
      font-weight: 400;
      color: $brand;
      transition: transform 0.2s;
    }
    &::-webkit-details-marker { display: none; }
  }
  &[open] summary::after { transform: rotate(45deg); }
  p {
    margin: 12px 0 0;
    font-size: 13px;
    color: $ink-2;
    line-height: 1.6;
  }
}

/* ═══ EMPTY CATALOG ═══ */
.ph-empty-catalog {
  background: #fff;
  border: 1px dashed $line;
  border-radius: $radius;
  padding: 36px;
  text-align: center;
  color: $muted;
}

/* ═══ CHECKOUT MODAL ═══ */
.ph-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(20, 20, 30, 0.65);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}
.ph-modal {
  position: relative;
  background: #fff;
  border-radius: 18px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 30px 34px 28px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4);
  animation: ph-modal-pop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes ph-modal-pop {
  from { opacity: 0; transform: translateY(20px) scale(0.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
.ph-modal-close {
  position: absolute;
  top: 14px; right: 14px;
  width: 34px; height: 34px;
  border: none;
  background: $bg;
  border-radius: 50%;
  font-size: 20px;
  line-height: 1;
  color: $muted;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: #fef2f2; color: #dc2626; }
}
.ph-modal-eyebrow {
  display: inline-block;
  background: rgba($brand, 0.1);
  color: $brand-dark;
  font-weight: 700;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 4px 11px;
  border-radius: 100px;
  margin-bottom: 12px;
}
.ph-modal-header {
  margin-bottom: 20px;
  h2 { font-size: 24px; font-weight: 800; margin: 0 0 5px; letter-spacing: -0.4px; }
  p { color: $ink-2; font-size: 13px; margin: 0; }
}
.ph-modal-summary {
  background: $bg;
  border-radius: 11px;
  padding: 16px 18px;
  margin-bottom: 20px;
}
.ph-modal-line {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 5px 0;
  font-size: 13px;
  color: $ink-2;
  span { flex: 1; padding-right: 10px; }
  strong { color: $ink; font-weight: 700; }
  &--discount { color: #047857; strong { color: #047857; } }
  &--total {
    font-size: 16px;
    color: $ink;
    font-weight: 700;
    border-top: 1px solid $line;
    padding-top: 10px;
    margin-top: 6px;
    strong { font-size: 20px; }
  }
}
.ph-modal-form { margin-bottom: 14px; }
.ph-modal-form .ph-field + .ph-field,
.ph-modal-form .ph-field-row,
.ph-modal-form .ph-field--full + .ph-field-row { margin-top: 10px; }
.ph-modal-form .ph-field-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;
  // align-items:start keeps both cells anchored at the top of the row
  // so the two inputs sit on the same baseline. Default `stretch` lets
  // minor height differences between labels/inputs leak into a visible
  // vertical offset between First name and Last name (same for
  // Password / Confirm password).
  align-items: start;
  // Each cell must accept min-content 0 so inner inputs with autofill
  // chrome padding or long label text can't blow the column out and
  // kick the second cell to the next row. Backstop in case a nested
  // .ph-field ever sets its own min-width.
  //
  // margin-top: 0 overrides the `.ph-field + .ph-field` sibling rule
  // above that adds 10px top margin for vertically-stacked fields.
  // Inside the grid that margin pushes the second field (Last name,
  // Confirm password) ~10px below the first — visible as a vertical
  // offset even with align-items:start.
  > .ph-field { min-width: 0; margin-top: 0; }
}
.ph-modal-form .ph-field { min-width: 0; }
.ph-modal-form .ph-field-input { min-width: 0; width: 100%; box-sizing: border-box; }
.ph-modal-form .ph-field-row + .ph-field { margin-top: 10px; }
// At narrow viewports (mobile modal), stack instead of trying to fit two
// columns — prevents the 'last name below first name but half-width' look.
@media (max-width: 460px) {
  .ph-modal-form .ph-field-row { grid-template-columns: 1fr; }
}
.ph-stripe-field {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  .ph-field-label { font-size: 12px; font-weight: 600; color: $ink-2; }
  // vue-stripe mounts an iframe inside a <div>; wrap it in a card shell
  // that matches our other ph-field-input borders.
  > div {
    border: 1px solid $line;
    border-radius: $radius;
    background: #fff;
    padding: 10px 12px;
  }
}
.ph-modal-promo {
  display: flex;
  gap: 7px;
  margin-top: 12px;
  input { flex: 1; min-width: 0; }
}
.ph-modal-or {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px 0 10px;
  color: $muted;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  &::before, &::after { content: ''; flex: 1; height: 1px; background: $line; }
}
.ph-modal-trial-cta {
  width: 100%;
  background: #fff;
  border: 1px solid $line;
  color: $ink;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  padding: 12px 16px;
  border-radius: $radius;
  cursor: pointer;
  transition: all 0.18s ease;
  &:hover:not(:disabled) {
    border-color: $brand;
    color: $brand-dark;
    background: #fff;
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  small { color: $muted; font-weight: 500; margin-left: 6px; }
}
.ph-modal-trial-result {
  margin-top: 18px;
  padding: 22px 22px 20px;
  background: #ecfdf5;
  border: 1px solid #6ee7b7;
  border-radius: 14px;
  color: #064e3b;
  text-align: center;
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.12);
  strong {
    display: block;
    font-size: 18px;
    font-weight: 800;
    color: #064e3b;
    margin-bottom: 6px;
    letter-spacing: -0.01em;
  }
  p { font-size: 14px; line-height: 1.55; color: #065f46; margin: 0; }
}
.ph-modal-trial-result-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin: 0 auto 12px;
  border-radius: 50%;
  background: #10b981;
  color: #fff;
  font-size: 30px;
  font-weight: 800;
  line-height: 1;
  box-shadow: 0 8px 18px rgba(16, 185, 129, 0.4);
}
.ph-modal-trial-result-body { margin-top: 6px; }
.ph-modal-trial-redirect {
  display: block;
  margin-top: 10px;
  font-size: 12px;
  font-weight: 700;
  color: #047857;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}
.ph-modal-trial-result-actions {
  display: flex;
  justify-content: center;
  margin-top: 14px;
}
.ph-modal-trial-retry {
  appearance: none;
  background: transparent;
  border: 1px solid #6ee7b7;
  color: #047857;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  padding: 9px 16px;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.18s, color 0.18s;
  &:hover { background: #047857; color: #fff; }
  &:focus-visible { outline: 2px solid #047857; outline-offset: 2px; }
}
.ph-modal-cta { width: 100%; }
.ph-modal-trust {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-top: 14px;
  font-size: 11px;
  color: $muted;
  font-weight: 500;
}
.ph-modal-fade-enter-active, .ph-modal-fade-leave-active { transition: opacity 0.25s ease; }
.ph-modal-fade-enter, .ph-modal-fade-leave-to { opacity: 0; }

/* ═══ MOTION / REVEAL ANIMATIONS ═══ */
@media (prefers-reduced-motion: no-preference) {
  [data-reveal].ph-reveal-pre {
    opacity: 0;
    transform: translateY(18px);
  }
  [data-reveal].ph-reveal-in {
    animation: ph-reveal 0.75s cubic-bezier(0.22, 1, 0.36, 1) forwards;
  }
  @keyframes ph-reveal {
    to { opacity: 1; transform: translateY(0); }
  }

  /* Staggered hero elements on first paint */
  .ph-hero .ph-eyebrow {
    animation: ph-pop 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: 0.05s;
  }
  .ph-hero .ph-h1 {
    animation: ph-slide-up 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: 0.1s;
  }
  .ph-hero .ph-sub {
    animation: ph-slide-up 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: 0.22s;
  }
  .ph-hero .ph-tabs {
    animation: ph-slide-up 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: 0.32s;
  }
  .ph-hero .ph-tab-pane {
    animation: ph-fade 0.7s ease both;
    animation-delay: 0.42s;
  }
  .ph-hero .ph-cart {
    animation: ph-slide-in-right 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: 0.28s;
  }
  @keyframes ph-pop {
    from { opacity: 0; transform: scale(0.9); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes ph-slide-up {
    from { opacity: 0; transform: translateY(24px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes ph-slide-in-right {
    from { opacity: 0; transform: translateX(32px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes ph-fade {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  /* Pay Now button: flat on-brand shadow, no pulse (toned down) */
  .ph-btn--checkout:not(:disabled) {
    box-shadow: 0 4px 12px rgba($brand, 0.25);
  }

  /* Plan card selection morph */
  .ph-plan-card--selected {
    animation: ph-select-pop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  @keyframes ph-select-pop {
    0%   { transform: translateY(-3px) scale(1); }
    50%  { transform: translateY(-6px) scale(1.03); }
    100% { transform: translateY(-3px) scale(1); }
  }

  /* Course card add-to-cart morph */
  .ph-course-card--in-cart {
    animation: ph-card-check 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  @keyframes ph-card-check {
    0%   { transform: translateY(0) scale(1); }
    50%  { transform: translateY(-4px) scale(1.02); }
    100% { transform: translateY(-2px) scale(1); }
  }

  /* Tab pill morph on hover */
  .ph-tab { position: relative; overflow: hidden; }
  .ph-tab::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.04);
    opacity: 0;
    transition: opacity 0.25s ease;
    border-radius: inherit;
    pointer-events: none;
  }
  .ph-tab:hover:not(.ph-tab--active)::before { opacity: 1; }
}

/* Respect reduced motion */
@media (prefers-reduced-motion: reduce) {
  * { animation: none !important; transition: none !important; }
}

/* ═══ RESPONSIVE ═══ */
@media (max-width: 1100px) {
  .ph-hero-grid { grid-template-columns: 1fr; }
  /* Cart drops below hero copy + course list on tablet/mobile so the first-fold
     content is the value proposition, not an empty cart. Natural DOM order is
     hero-main first, cart aside second — so no explicit `order` needed here. */
  .ph-cart { position: static; width: 100%; max-width: 520px; margin: 28px auto 0; }
  .ph-h1 { font-size: 40px; }
  .ph-trust-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .ph-trust-badge-wrap:nth-child(5) { border-left: 1px solid $line; }
  .ph-trust-badge-wrap:nth-child(6) { border-left: 1px solid $line; }
}

@media (max-width: 820px) {
  .ph-hero { padding: 32px 0 44px; }
  .ph-h1 { font-size: 34px; }
  .ph-sub { font-size: 15px; }
  .ph-plans-grid { grid-template-columns: 1fr; }
  .ph-extras-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .ph-courses-grid { grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); }
  .ph-faq-grid { grid-template-columns: 1fr; }
  .ph-trust-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
  }
  .ph-trust-badge-wrap { border-left: none !important; border-top: 1px solid $line; padding-top: 16px; }
  .ph-trust-title { font-size: 20px; }
}

@media (max-width: 560px) {
  .pricing-page .container { padding-left: 18px; padding-right: 18px; }
  .ph-h1 { font-size: 28px; letter-spacing: -0.7px; }
  .ph-sub { font-size: 14px; }
  .ph-tab { padding: 9px 14px; }
  .ph-tab-sub { display: none; }
  .ph-courses-grid { grid-template-columns: 1fr; }
  .ph-extras-grid { grid-template-columns: 1fr; }
  .ph-demo-form-grid { grid-template-columns: 1fr; }
  .ph-demo-form, .ph-config-panel { padding: 20px; }
  .ph-trust-badge { height: 50px; }
  .ph-trust-stat strong { font-size: 22px; }
  .ph-trust-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
  .ph-modal { padding: 24px 20px 20px; }
}

/* ═══ FOOD/ALCOHOL TOGGLE (above plan grid) ═══ */
.ph-food-toggle {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0 0 18px;
  padding: 12px 16px;
  background: #fff;
  border: 1px solid $line;
  border-radius: $radius;
  flex-wrap: wrap;
}
.ph-food-toggle-label {
  font-size: 13px;
  font-weight: 600;
  color: $ink-2;
}
.ph-food-toggle-pills {
  display: inline-flex;
  background: $bg;
  border-radius: 100px;
  padding: 3px;
  gap: 2px;
}
.ph-food-pill {
  background: transparent;
  border: 0;
  padding: 6px 18px;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  color: $muted;
  border-radius: 100px;
  cursor: pointer;
  transition: all 0.18s ease;
  &:hover { color: $ink; }
  &--on {
    background: $ink;
    color: #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
}

/* ═══ ALT OPTIONS (Build-your-own + LMS-only) ═══
   Sits under the 3 bundles. Intentionally styled differently from plan cards
   so buyers read these as "alternatives" not a 4th/5th bundle. */
.ph-alt-options {
  margin: 4px 0 26px;
}
.ph-alt-options-head {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: $muted;
  margin: 0 0 10px;
  padding-left: 2px;
}
.ph-alt-options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 12px;
}
.ph-alt-tile {
  background: #fff;
  border: 1px dashed $line;
  border-radius: $radius;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  font: inherit;
  text-align: left;
  transition: all 0.18s ease;
  &:hover {
    border-color: rgba($brand, 0.5);
    background: rgba($brand, 0.03);
    transform: translateY(-1px);
  }
  &--on {
    border-style: solid;
    border-color: $brand;
    background: #fff;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
  }
}
.ph-alt-icon {
  font-size: 22px;
  line-height: 1;
  flex-shrink: 0;
}
.ph-alt-body { flex: 1; min-width: 0; }
.ph-alt-name {
  font-size: 14px;
  font-weight: 700;
  color: $ink;
  letter-spacing: -0.1px;
}
.ph-alt-tag {
  font-size: 12px;
  color: $muted;
  margin-top: 2px;
}
.ph-alt-check {
  color: $brand;
  font-size: 18px;
  font-weight: 800;
  flex-shrink: 0;
}

/* ═══ "SEE ALL INCLUDED" BUTTON + MODAL ═══ */
.ph-plan-seeall {
  margin-top: 10px;
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  cursor: pointer;
  color: $brand-dark;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  text-align: left;
  text-decoration: underline;
  text-underline-offset: 3px;
  &:hover { color: $brand; }
}

.ph-modal--includes {
  max-width: 580px;
  /* Flex-column layout so header is fixed, list scrolls inside, CTA pinned. */
  display: flex;
  flex-direction: column;
  /* Override the base .ph-modal padding so each section controls its own. */
  padding: 0;
  overflow: hidden; /* outer scroll disabled — body has its own */
  max-height: min(90vh, 760px);

  .ph-modal-close { top: 16px; right: 16px; }

  .ph-modal-header {
    flex: 0 0 auto;
    padding: 26px 28px 18px;
    margin: 0;
    border-bottom: 1px solid $line;
  }
  .ph-includes-body {
    flex: 1 1 auto;
    min-height: 0;            /* required for flex-child scroll */
    overflow-y: auto;
    padding: 16px 28px;
    margin: 0;
    /* Subtle fade so list doesn't look clipped at top/bottom edges. */
    background:
      linear-gradient(#fff, transparent 18px) top / 100% 18px no-repeat,
      linear-gradient(transparent, #fff calc(100% - 18px)) bottom / 100% 18px no-repeat,
      #fff;
  }
  .ph-includes-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 8px;
  }
  .ph-includes-item {
    display: grid;
    grid-template-columns: 20px 1fr auto;
    gap: 10px;
    align-items: center;
    padding: 10px 12px;
    background: #fafafa;
    border: 1px solid $line;
    border-radius: 8px;
    font-size: 13px;
  }
  .ph-includes-name {
    color: $ink;
    font-weight: 600;
    line-height: 1.3;
  }
  .ph-includes-cat {
    color: $muted;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-weight: 700;
    white-space: nowrap;
  }
  .ph-modal-cta {
    flex: 0 0 auto;
    margin: 0;
    border-radius: 0 0 18px 18px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }
}
@media (max-width: 520px) {
  .ph-modal--includes {
    max-height: 92vh;
    .ph-modal-header { padding: 22px 22px 14px; }
    .ph-includes-body { padding: 14px 22px; }
  }
}

.ph-cart-error {
  background: #fff7e6;
  border-bottom: 1px solid #f5c265;
  color: #703808;
  font-weight: 500;
  position: sticky;
  top: 0;
  z-index: 10;
}
.ph-cart-error-inner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  font-size: 14px;
  line-height: 1.4;
}
.ph-cart-error-icon {
  font-size: 18px;
  flex-shrink: 0;
}
.ph-cart-error-dismiss {
  margin-left: auto;
  background: transparent;
  border: 0;
  font-size: 22px;
  line-height: 1;
  color: #703808;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  &:hover { background: rgba(112, 56, 8, 0.1); }
  &:focus-visible { outline: 2px solid #703808; outline-offset: 2px; }
}

// Softer informational variant of the cart error banner — used for prefill
// soft-misses (e.g. unknown course slug from a landing-page deep link).
.ph-cart-error--soft {
  background: #eef5ff;
  border-bottom-color: #b6cef0;
  color: #244673;
  .ph-cart-error-dismiss { color: #244673; &:hover { background: rgba(36, 70, 115, 0.08); } }
}

// Loading overlay shown while landing-page query params resolve to cart
// adds. Centered card with spinner — fixed-position so it survives any
// scrolling done by the autocheckout flow.
.ph-prefill-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.42);
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
}
.ph-prefill-overlay-inner {
  background: #fff;
  border-radius: 12px;
  padding: 18px 22px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  color: #1f2937;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18);
}
.ph-prefill-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(254, 103, 44, 0.25);
  border-top-color: $brand;
  border-radius: 50%;
  animation: ph-prefill-spin 0.7s linear infinite;
}
@keyframes ph-prefill-spin {
  to { transform: rotate(360deg); }
}

/* ═══════════════════════════════════════════════════════════════════
   Parity additions (sign-up retirement, 2026-05-01)
   ═══════════════════════════════════════════════════════════════════ */

/* Plan-locked cart remove button — distinct from the active × so the
   buyer immediately reads "this is bundled, you can't drop it." */
.ph-cart-remove--locked {
  cursor: not-allowed !important;
  opacity: 0.55;
  background: transparent !important;
  color: #888 !important;
  border-color: rgba(0, 0, 0, 0.08) !important;
}
.ph-cart-remove--locked:hover {
  opacity: 0.6;
  background: transparent !important;
}

/* Email "sign in instead" inline link — keeps tone helpful, not punishing. */
.ph-email-login-link {
  display: inline-block;
  margin-left: 8px;
  font-weight: 700;
  color: #fe672c;
  text-decoration: none;
}
.ph-email-login-link:hover { text-decoration: underline; }

/* Payment-method picker (modal). Compact segmented-control row — same
   visual rhythm as the billing-cycle toggle so the modal stays simple. */
.ph-paytype {
  border: 0;
  padding: 0;
  margin: 6px 0 14px;
}
.ph-paytype legend.ph-field-label {
  margin-bottom: 6px;
  display: block;
  padding: 0;
}
.ph-paytype-row {
  display: inline-flex;
  align-items: stretch;
  background: #f7f7f7;
  border-radius: 999px;
  padding: 4px;
  gap: 2px;
  border: 1px solid rgba(37, 37, 37, 0.06);
  width: 100%;
}
.ph-paytype-pill {
  flex: 1 1 0;
  border: 0;
  background: transparent;
  border-radius: 999px;
  padding: 8px 14px;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  color: #555;
  white-space: nowrap;
  transition: background-color 160ms ease, color 160ms ease;
}
.ph-paytype-pill small {
  font-size: 11px;
  font-weight: 700;
  color: #d34c14;
  margin-left: 2px;
}
/* Small ⓘ icon next to "Invoice" — hover/focus reveals a tooltip
   explaining the +10% bump without crowding the pill text itself. */
.ph-paytype-info {
  position: relative;
  display: inline-flex;
  width: 14px;
  height: 14px;
  margin-left: 4px;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: #777;
  cursor: help;
  border-radius: 50%;
  transition: color 160ms ease;
}
.ph-paytype-info:hover, .ph-paytype-info:focus-visible {
  color: #fe672c;
  outline: none;
}
.ph-paytype-info::after {
  content: attr(data-tip);
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  width: 220px;
  padding: 8px 10px;
  background: #1a1a1a;
  color: #fff;
  font-size: 11px;
  font-weight: 500;
  line-height: 15px;
  letter-spacing: 0;
  text-transform: none;
  border-radius: 6px;
  white-space: normal;
  text-align: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 160ms ease;
  z-index: 10001;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
}
.ph-paytype-info::before {
  content: '';
  position: absolute;
  bottom: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  border: 4px solid transparent;
  border-top-color: #1a1a1a;
  opacity: 0;
  pointer-events: none;
  transition: opacity 160ms ease;
  z-index: 10001;
}
.ph-paytype-info:hover::after,
.ph-paytype-info:focus-visible::after,
.ph-paytype-info:hover::before,
.ph-paytype-info:focus-visible::before {
  opacity: 1;
}
.ph-paytype-pill:hover {
  color: #1a1a1a;
}
.ph-paytype-pill:focus-visible {
  outline: 2px solid #fe672c;
  outline-offset: 2px;
}
.ph-paytype-pill--on {
  background: #fff;
  color: #1a1a1a;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

/* Cart-side add-on summary — inline section under the cart body, tight
   so it reads as part of the cart not a separate panel. */
.ph-cart-addons {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed rgba(254, 103, 44, 0.25);
}
.ph-cart-addons-head {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #d34c14;
  margin-bottom: 4px;
}

/* Modal summary — addon + surcharge line variants. */
.ph-modal-line--addon {
  color: #d34c14;
  font-size: 13px;
}
.ph-modal-line--addon strong { color: #1a1a1a; }
.ph-modal-line--surcharge {
  color: #d34c14;
  font-size: 13px;
}
.ph-modal-line--surcharge strong { color: #d34c14; }

/* Cart totals — surcharge variant (mirror discount but in orange). */
.ph-totals-row--surcharge {
  color: #d34c14;
  font-weight: 600;
  span { font-weight: inherit; }
}

/* Tiny mailto link under the cart — quiet, no card chrome, a
   companion to the schedule-demo trigger above it. */
.ph-cart-support-link {
  display: block;
  margin-top: 4px;
  padding: 4px 0;
  font-size: 12px;
  line-height: 16px;
  color: #777;
  text-decoration: none;
  transition: color 160ms ease;
}
.ph-cart-support-link strong {
  color: #1a1a1a;
  font-weight: 600;
}
.ph-cart-support-link:hover {
  color: #fe672c;
  strong { color: #fe672c; }
}
</style>
